/* global gtag:true, analytics:true */
/*eslint no-extend-native: ["error", { "exceptions": ["Array"] }]*/
import {
  DashboardSmmItem,
  SmmAccountsData,
  SocialMediaAccountsType,
  SocialMediaBusinessAccountsType,
} from '../types/socialMediaMonitoring';
import {LL_SITE_DIRECORY_PARTNER_ID, LL_SSDCAT} from '../constants/supportUrl';
import {getBrowserLanguage, getLangCode, interpolate} from '../stringBundle';

import AlertToggle from '../containers/alerts/AlertToggle';
import {withEstatePlanningEligibilityMock} from '../customHooks/estatePlanning/estatePlanningEligibilityMock';
import Image from '../components/common/VectorImage';
import {Institution} from '../reducers/transactionReducers';
import _debounce from 'lodash/debounce';
import _filter from 'lodash/filter';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import {bannerTextContainerClasses} from './componentStyles';
import {checkInactiveInstitutions} from './transactionHelper';
import cookie from 'react-cookies';
import DOMPurify from 'dompurify';
import {currencyCodes} from '../constants/currencyCodeMap';
import dateUtils from './dateUtils';
import errorCodeMapForAnalytics from '../constants/errorCodeMap';
import {getConnectedInstitutionsCountUnified} from './financeHelper';
import {getHeaderAccessToken} from './fetchRest';
import {getTrackingValues} from './tracking';
import log from './log';
import {buildAvastLsaConsentUrl} from './partnerUtils';
import {logCardShieldVisibilityFromMemberApi} from './cardShieldVisibilityDebugLog';
import pageNameMapForAnalytics from '../constants/pageNameMapForAnalytics';
import parse from 'html-react-parser';
import sessionstore from 'store/storages/sessionStorage';
import {setModalSizeSpec} from '../actions/modal';
import storageEngine from 'store/src/store-engine';
import store from 'store';
import {REIMAGINE_IMAGES} from '../constants/reimagineImages';

const sessionStorage = storageEngine.createStore([sessionstore], []);
const SHOWN = 'shown';
const PRIMARY_PHONE_MODAL_PREFIX = 'PrimaryPhoneModal-';
export const US_COUNTRY_CODE = 'US';

export const STANDARD_PLAN_CUTOFF_MONTHS_FOR_UPSELL_MSG = 3;

const EFX_BUREAU_CODE = 'EFX';
const TU_BUREAU_CODE = 'TU';
const EXP_BUREAU_CODE = 'EXP';

type Message = {
  type: string;
  payload: any;
};
type DynamicObject = {
  [key: string]: string; // Allows any string key with string or number values
};

type AdobeTrackEventData = {
  /** Event name/action identifier (required for s.tl call) */
  name?: string;
  /** Event category for grouping related events */
  category?: string;
  /** Descriptive label for the event */
  label?: string;
  /** Optional numeric value associated with the event */
  value?: string | number;
  /** Additional custom properties to include with the event */
  customProps?: Record<string, string | number | boolean>;
};

export default class utils {
  // Credit Rating Constants (enum-like object pattern)
  static CreditRating = {
    EXCELLENT: 'EXCELLENT',
    GOOD: 'GOOD',
    FAIR: 'FAIR',
    POOR: 'POOR',
    LIMITED_NO_HISTORY: 'LIMITED / NO HISTORY',
  } as const;

  /**
   * Converts a loosely-typed value to boolean using common conventions.
   *
   * Accepts:
   * - boolean: returns as-is
   * - string: 'true' | 'false' (case-insensitive), also '1' | '0'
   * - number: 1 | 0
   * Returns null when the value cannot be interpreted as boolean.
   *
   * @param value - The input value to convert
   * @returns boolean | null
   */
  static toBoolean(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'true' || v === '1') return true;
      if (v === 'false' || v === '0') return false;
      return null;
    }
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }
    return null;
  }

  /**
   * Converts a credit score number directly to a credit rating text
   * @param creditScore - The credit score number
   * @returns Credit rating text matching CreditRating constants
   */
  static getCreditRating(creditScore: number | string | null | undefined): string {
    if (!creditScore || creditScore === '' || isNaN(Number(creditScore))) {
      return utils.CreditRating.LIMITED_NO_HISTORY;
    }
    const score = Number(creditScore);
    if (score >= 720) return utils.CreditRating.EXCELLENT;
    if (score >= 660) return utils.CreditRating.GOOD;
    if (score >= 620) return utils.CreditRating.FAIR;
    if (score > 0) return utils.CreditRating.POOR;

    return utils.CreditRating.LIMITED_NO_HISTORY;
  }

  /**
   * Extracts the CTA URL from the hash portion of the current URL
   * Used to retrieve redirect URLs embedded in hash parameters
   * @returns {string} The extracted CTA URL or empty string if not found
   */
  static getCtaUrlFromHash(): string {
    if (typeof window === 'undefined' || !window.location || !window.location.hash) {
      return '';
    }

    const hash = window.location.hash; // Get everything after #

    // Extract query params from hash
    const queryStringStart = hash.indexOf('?');
    if (queryStringStart === -1) return '';

    const queryString = hash.substring(queryStringStart + 1);

    // More robust approach: consider the entire URL structure with nested query params
    // Find the starting position of ctaurl
    const ctaUrlPos = queryString.indexOf('ctaurl=');
    if (ctaUrlPos === -1) return '';

    // Get everything after "ctaurl="
    const ctaUrlValue = queryString.substring(ctaUrlPos + 7); // 7 is length of "ctaurl="

    // Find the next parameter after ctaurl (if any)
    // But be careful - only look for & that's not part of the ctaurl's own query string
    // We need to find the first & that is NOT preceded by a ? (which would make it part of the ctaurl's query)
    const segments = ctaUrlValue.split('&');

    // First segment is definitely part of the ctaurl
    let fullCtaUrl = segments[0];

    // For additional segments, we need to determine if they're part of ctaurl or the next param
    for (let i = 1; i < segments.length; i++) {
      // If previous part of URL contained a ?, this & is part of ctaurl's query string
      if (fullCtaUrl.includes('?')) {
        fullCtaUrl += '&' + segments[i];
      } else {
        // Otherwise we've reached the next hash parameter
        break;
      }
    }

    // Decode the URL to handle any encoded characters
    return decodeURIComponent(fullCtaUrl);
  }

  static clearSessionAndCookies() {
    store.clearAll();
    sessionStorage.clearAll();
    const cookies = document.cookie;
    for (let i = 0; i < cookies.split(';').length; ++i) {
      const myCookie = cookies[i];
      const pos = myCookie.indexOf('=');
      const name = pos > -1 ? myCookie.substr(0, pos) : myCookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'cookie' implicitly has an 'any' type.
  static getCookieValue(cookie, name) {
    const nameStr = name + '=';
    const decodedCookie = decodeURIComponent(cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(nameStr) === 0) {
        return c.substring(nameStr.length, c.length);
      }
    }
    return '';
  }

  /** Amplitude/Adobe gen_id from the `gd_id` cookie (`GEN-{cookieValue}`). */
  static getGenIdFromGdIdCookie(): string | null {
    const gdId = this.getCookieValue(document.cookie, 'gd_id');
    if (!gdId) {
      return null;
    }
    return `GEN-${gdId}`;
  }

  static parseHtml(text: string) {
    return parse(text ? text : '');
  }

  /**
   * Sanitizes HTML before `dangerouslySetInnerHTML` (e.g. any string from CMS or string bundles).
   * @param {string} html - string content to be added to dangerouslySetInnerHTML
   * @returns {string} - purified HTML string safe to be added to dangerouslySetInnerHTML
   */
  static sanitizeHtml(html: string): string {
    return DOMPurify.sanitize(html ?? '');
  }

  static removeHtmlTags(text: string) {
    return text ? text.replace(/\s*<[^>]*>.*?<\/[^>]*>\s*/g, ' ').trim() : '';
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'parameters' implicitly has an 'any' typ... Remove this comment to see the full error message
  static log(parameters) {
    const url = this.getLogPath();

    const selectedParameters = {
      source: _get(parameters, 'source', 'portal'),
      version: _get(parameters, 'version', ''),
      feature: _get(parameters, 'feature', ''),
      actionType: _get(parameters, 'actionType', ''),
      message: _get(parameters, 'message', ''),
      accountId: _get(parameters, 'accountId', ''),
      token: _get(parameters, 'token', ''),
      startTime: _get(parameters, 'startTime', ''),
      endTime: _get(parameters, 'endTime', ''),
      elapsedTime: _get(parameters, 'elapsedTime', ''),
      error: _get(parameters, 'error', ''),
      statusCode: _get(parameters, 'statusCode', ''),
    };

    const config: FetchConfig = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        client_id: _get(parameters, 'clientId', 'dsp'),
        'X-CSRF-Token': this.getCookieValue(document.cookie, 'XSRF-TOKEN'),
        'x-nlok-trace-id': getTrackingValues().NLokTraceId,
      },
      credentials: 'include',
      body: JSON.stringify(selectedParameters),
    };

    // The SPA must use a token in the header when it is loaded in a 3rd party domain which prevents session cookies from being sent to Member API
    if (getHeaderAccessToken()) {
      config.headers.access_token = getHeaderAccessToken();
    }

    return fetch(url, config);
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
  static mapComparedObjectKeys(a, b) {
    // @ts-expect-error TS(7034) FIXME: Variable 'objectKeyList' implicitly has type 'any[... Remove this comment to see the full error message
    const objectKeyList = [];
    const comparedObjectsList = this.compareAlertDetailObjects(a, b);

    comparedObjectsList.forEach((t) => {
      objectKeyList[t.key] = t;
    });

    // @ts-expect-error TS(7005) FIXME: Variable 'objectKeyList' implicitly has an 'any[]'... Remove this comment to see the full error message
    return objectKeyList;
  }

  /**
   * Determines the type of the given value as a string.
   *
   * @param {any} value - The value whose type is to be determined.
   * @return {string} - A string representing the type of the value. Possible values are 'string', 'number', 'boolean', 'undefined', 'object', 'array', 'function', or 'symbol'.
   */
  static getValueType(value: any): string {
    const valType = typeof value;
    return valType === 'object' ? (Array.isArray(value) ? 'array' : 'object') : valType;
  }

  /**
   * Compares two values and determines their difference status.
   * For primitive values, it returns a simple status string ('same', 'removed', 'changed').
   * For objects, it returns an object with keys representing nested properties and values indicating their status.
   *
   * @param {any} valA - The first value to compare (often the "original" or "previous" value)
   * @param {any} valB - The second value to compare (often the "new" or "current" value)
   * @returns {string | DynamicObject} A string status for primitive values ('same', 'removed', 'changed') or
   *                                   an object with keys representing properties and values indicating their status
   *                                   for complex objects. Possible statuses include:
   *                                   - 'same': Values are identical
   *                                   - 'removed': Value exists in A but not in B
   *                                   - 'added': Value exists in B but not in A
   *                                   - 'changed': Value exists in both but is different
   * @example
   * // Returns 'same'
   * getStatusForObjects('test', 'test');
   *
   * // Returns 'removed'
   * getStatusForObjects('test', null);
   *
   * // Returns 'changed'
   * getStatusForObjects('test1', 'test2');
   *
   * // Returns { name: 'same', age: 'changed' }
   * getStatusForObjects({name: 'John', age: 30}, {name: 'John', age: 31});
   */
  static getStatusForObjects(valA: any, valB: any): string | DynamicObject {
    let status: string | DynamicObject = 'same';
    const aEmpty = this.isEmpty(valA);
    const bEmpty = this.isEmpty(valB);
    const bothObjects =
      typeof valA === 'object' &&
      valA !== null &&
      typeof valB === 'object' &&
      valB !== null &&
      !Array.isArray(valA) &&
      !Array.isArray(valB);
    // if primitive values and both empty or a is empty, and b is not empty or both not empty and exactly the same
    if (!bothObjects && ((aEmpty && bEmpty) || (aEmpty && !bEmpty) || JSON.stringify(valA) === JSON.stringify(valB))) {
      status = 'same';
    } else if (!aEmpty && bEmpty) {
      status = 'removed';
    } else if (!aEmpty && !bEmpty && bothObjects && valA !== valB) {
      const keys = new Set([...Object.keys(valA || {}), ...Object.keys(valB || {})]);
      status = {};
      // compare each key/value pair in the combined objects set
      for (const key of keys) {
        const keyAVal = valA[key];
        const keyBVal = valB[key];
        const keyAValType = typeof keyAVal;
        const keyBValType = typeof keyBVal;
        const aEmpty = this.isEmpty(keyAVal);
        const bEmpty = this.isEmpty(keyBVal);
        if (!status[key]) {
          if (aEmpty && !bEmpty) {
            status[key] = 'added';
          } else if (!aEmpty && bEmpty) {
            status[key] = 'removed';
          } else if (keyAVal !== keyBVal) {
            if (keyAValType === 'object' && keyBValType === 'object') {
              Object.assign(status, this.getStatusForObjects(keyAVal, keyBVal));
            } else {
              status[key] = 'changed';
            }
          } else if (keyAVal === keyBVal) {
            status[key] = 'same';
          } else if (keyAVal && !keyBVal) {
            status[key] = 'added';
          }
        }
      }
    } else if (!bothObjects && !aEmpty && !bEmpty && valA === valB) {
      status = 'same';
    } else if (!bothObjects && !aEmpty && !bEmpty && valA !== valB) {
      status = 'changed';
    }

    return status;
  }

  /**
   * Evaluates whether the provided value is considered "empty".
   * A value is considered empty if it is null, undefined, an empty string,
   * an empty array, or an object with no own enumerable properties.
   *
   * @param {string | any[] | null | undefined} value - The value to check for emptiness.
   * @return {boolean} Returns `true` if the value is empty, otherwise `false`.
   */
  static isEmpty(value: string | any[] | null | undefined) {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0)
    );
  }

  /**
   * Compares two objects for differences and similarities, producing a detailed report
   * which includes the keys, types, statuses, and values of the compared properties.
   *
   * @param {any} a - The first object to be compared.
   * @param {any} b - The second object to be compared.
   * @param {string} [parentKey=''] - The parent key prefix used to track nested object keys.
   * @return {any[]} A list of result objects that describe the differences or similarities between the two input objects.
   */
  static compareAlertDetailObjects(a: any, b: any, parentKey = ''): any[] {
    const results: any[] = [];

    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const valA = a?.[key];
      const valB = b?.[key];

      const bothObjects = typeof valA === 'object' && valA !== null && typeof valB === 'object' && valB !== null;

      if (bothObjects && !Array.isArray(valA) && !Array.isArray(valB)) {
        if (valA !== valB) {
          results.push({
            key: fullKey,
            type: 'object',
            status: this.getStatusForObjects(valA, valB),
            value: valB,
            before: valA,
          });
        }
      } else {
        const aEmpty = this.isEmpty(valA);
        const bEmpty = this.isEmpty(valB);

        if (aEmpty && bEmpty) {
          results.push({
            key: fullKey,
            type: this.getValueType(valA),
            status: this.getStatusForObjects(valA, valB),
            value: null,
            before: null,
          });
        } else if (aEmpty && !bEmpty) {
          // Treat empty previous as the same if current is not empty
          results.push({
            key: fullKey,
            type: this.getValueType(valB),
            status: this.getStatusForObjects(valA, valB),
            value: valB,
            before: null,
          });
        } else if (!aEmpty && bEmpty) {
          // Previous had a value, now it's empty → treat as removed
          results.push({
            key: fullKey,
            type: this.getValueType(valA),
            status: this.getStatusForObjects(valA, valB),
            value: null,
            before: valA,
          });
        } else if (JSON.stringify(valA) === JSON.stringify(valB)) {
          results.push({
            key: fullKey,
            type: this.getValueType(valA),
            status: this.getStatusForObjects(valA, valB),
            value: valB,
          });
        } else {
          results.push({
            key: fullKey,
            type: this.getValueType(valA),
            status: this.getStatusForObjects(valA, valB),
            value: valB,
            before: valA,
          });
        }
      }
    }

    return results;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'obj' implicitly has an 'any' type.
  static someObjectParametersHaveValue(obj) {
    let notEmpty = false;

    if (obj) {
      const allKeys = Object.keys(obj);

      if (allKeys.length > 0) {
        allKeys.forEach((key) => {
          if (obj[key].value) {
            notEmpty = true;
          }
        });
      }
    }

    return notEmpty;
  }

  static historyToObject(
    dates: string[],
    scores: {
      meta: string;
      value: string | number;
    }[]
  ) {
    // Check length
    if (Array.isArray(dates) && Array.isArray(scores)) {
      const datesArr = dates;
      const scoresArr = scores;
      const historyObject = {
        dates: datesArr,
        scores: scoresArr,
      };
      return historyObject;
    }
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'str' implicitly has an 'any' type.
  static convertUpperCase(str) {
    let description = str.toLowerCase();
    description = description.split(' ');
    for (let i = 0; i < description.length; i++) {
      if (i === 0) {
        description[i] = description[i].charAt(0).toUpperCase() + description[i].slice(1);
      }
    }
    return description.join(' ');
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'type' implicitly has an 'any' type.
  static trackEventForMonitoredInfo(gaLabel) {
    const isMonitoredInfoPage = window.location?.hash.includes('/monitoring');
    const category = isMonitoredInfoPage ? 'Member info monitored' : 'DashboardMonitoring Member info';
    const option = {
      category,
      action: 'Click',
      label: gaLabel,
    };
    this.trackEvent(option);
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'option' implicitly has an 'any' type.
  static trackEvent(option) {
    if (typeof option.value === 'number') {
      option.value = option.value.toString();
    }

    const gaAction = option.action;
    if (window.REACT_APP_ENABLE_AMPLITUDE_PAGE_VIEWS !== false) {
      window.genDL = window.genDL || [];
      if (gaAction === 'click' || gaAction === 'Click') {
        // Pushes click to genDL for Amplitude tracking (parallel with GA gtag click events).
        window.genDL.push({
          event: 'click',
          click_text: option.label || '',
          click_name: option.category || '',
        });
      } else if (gaAction && String(gaAction).toLowerCase().includes('view')) {
        // Pushes view-style GA actions to genDL as element_impression (parallel with gtag).
        window.genDL.push({
          event: 'element_impression',
          element_detail: `${option.category || ''}-${option.label || ''}`,
        });
      }
    }

    if (window.gtag) {
      const {category, label, action, ...restOptions} = option;
      const eventData = {
        event_category: category || '',
        event_label: label || '',
        event_client_id: _get(window, 'clientId', 'dsp'),
        ...restOptions,
      };

      if (option.value) (eventData as $TSFixMe).value = option.value;

      if (window.analytics) {
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.sku) (eventData as $TSFixMe).SKU = analytics.sku;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.promoCode) eventData['Promo Code'] = analytics.promoCode;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.isPrimary) eventData['Is Primary Member'] = analytics.isPrimary.toString();
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.archivedAlertCount) eventData['Archived Alert Count'] = analytics.archivedAlertCount;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.disputedAlertCount) eventData['Disputed Alert Count'] = analytics.disputedAlertCount;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.inboxAlertCount) eventData['Inbox Alert Count'] = analytics.inboxAlertCount;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.lpmUsertype) eventData['LPM Usertype'] = analytics.lpmUsertype;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.hashedAccountId) eventData['Hashed Norton Guid'] = analytics.hashedNortonGuid;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.memberGuid) eventData['Hashed_Account_Id'] = analytics.hashedAccountId;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics'.
        if (analytics.partnerGuid) eventData['Norton_Guid'] = `guid_${analytics.partnerGuid}`;
        // @ts-expect-error TS(2304) FIXME: Cannot find name 'analytics
        const PartnerFamilyPlanId = utils.createPartnerFamilyPlanIdEventStr(analytics);
        if (PartnerFamilyPlanId) eventData['partner_family_plan_Id'] = PartnerFamilyPlanId;
      }
      // @ts-expect-error TS(2304) FIXME: Cannot find name 'gtag'.
      gtag('event', action || '', eventData);
    }
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'inputType' implicitly has an 'any' type... Remove this comment to see the full error message
  static trackEventForManageAccount(inputType, action, hasValue, productFeatures?: ProductFeatures | null) {
    const labelMap = {
      ADDRESS: 'Address',
      PHONE_NUMBER: 'Phone',
      PHONE: 'Phone',
      E_MAIL_ADDR: 'Email',
      EMAIL: 'Email',
      DRIVERS_LICENSE: 'Driver License',
      INSURANCE_CARD: 'Insurance',
      transaction: 'TXM_Purchase',
      transfer: 'TXM_Transfer',
      withdrawal: 'TXM_Cash',
      USERNAME: 'Username',
      PASSWORD: 'Password',
      VERBAL_PASSCODE: 'Verbal_Passcode',
      BANK_ACCOUNT: 'Internet_Monitor',
      CREDIT_DEBIT: 'Internet_Monitor',
      HOME_PROPERTY: 'Home_Property',
    };
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    let label: string = labelMap[inputType] ? labelMap[inputType] : inputType;
    if (productFeatures != null) {
      label = this.withFmEbdlCohortLabel(String(label), productFeatures);
    }
    const option = {
      category: 'MAccount',
      action,
      label,
      event: 'trackEvent',
    };
    if (hasValue) {
      (option as $TSFixMe).value = 1;
    }
    this.trackEvent(option);
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'data' implicitly has an 'any' type.
  static getMaxRecordCount(data, inputType, defaultVal) {
    let maxCount = defaultVal;
    if (data !== undefined && data.zone !== undefined) {
      const zones = data.zone;
      const recordtype = _filter(zones, {recordType: inputType});
      maxCount = _get(recordtype[0], 'maxRecordCount', defaultVal);
    }
    return Number(maxCount);
  }

  /**
   * Returns the amount in localized currency format with 2 decimal places and currency symbol.
   * Example: 12345.12 with 'en-US' langCode and 'USD' currency turns into $12,345.12
   *
   * @param {number} amount Number to be formatted
   * @param {string} langCode Language code for formatting amount
   * @param {string} currency Currency code for formatting amount
   * @return {string}
   */
  static convertAmountToString(amount: number, langCode: string, currency: string) {
    const amt = Number(amount).toFixed(2);
    // Validate currency code - default to 'USD' if empty, null, or undefined
    const validCurrency = currency && currency.trim() ? currency : 'USD';
    const currencyDisplay = this.getCurrencyDisplay(langCode, validCurrency);
    // @ts-expect-error TS(2769) FIXME: No overload matches this call.

    return new Intl.NumberFormat(langCode, {style: 'currency', currency: validCurrency, currencyDisplay}).format(amt);
  }

  /**
   * Returns currencyDisplay type as a style option for Intl.NumberFormat
   * For currency codes 'AUD', 'CAD', 'NZD' and 'USD', '$' symbol is returned if currency code is in line with langCode; otherwise currency code is used.
   * '¥' and '£' are returned in all locales for currency code 'JPY' and 'GBP' respectively.
   * Example: 12345.12 with 'en-US' langCode and 'USD' currency code returns $12,345.12
   *          12345.12 with 'en-CA' langCode and 'USD' currency code returns USD 12,345.12
   *          12345.12 with all locales and 'JPY' currency code returns ¥12,345.12
   *          12345.12 with all locales and 'GBP' currency code returns £12,345.12
   * @param {string} langCode Language code for formatting amount
   * @param {string} currency Currency code for formatting amount
   * @return {string}
   */
  static getCurrencyDisplay(langCode: string, currency: string) {
    const langCodeSplit = langCode.split('-');
    const currencyCodeKey = langCodeSplit.length === 2 ? langCodeSplit[1].toUpperCase() : langCode.toUpperCase();
    // Among the currencies using '$' as currency symbols, only currency code 'AUD', 'CAD', 'NZD', and 'USD' in the current Lifelock support list are handled here.
    return ['AUD', 'CAD', 'NZD', 'USD'].includes(currency) && currencyCodes[currencyCodeKey] !== currency
      ? 'code'
      : 'narrowSymbol';
  }

  /**
   * returns formatted currency value based on lang code, currency code and fraction digits
   */
  static formatCurrency(
    value: number,
    langCode: string,
    currency: string,
    minimumFractionDigits = 0,
    maximumFractionDigits = 0
  ) {
    const currencyFormatter = Intl.NumberFormat(langCode, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits,
      maximumFractionDigits,
    });

    return currencyFormatter.format(value);
  }

  /**
   * returns the localized amount with negative sign if the transactionType is DEBIT
   */
  static getConvertedTransactionAmount(amount: number, currencyCode: string, transactionType: string) {
    let convertedAmount = utils.convertAmountToString(amount, getLangCode(), currencyCode);
    if (transactionType === 'DEBIT') {
      convertedAmount = `-${convertedAmount}`;
    }
    return convertedAmount;
  }

  /**
   * Returns an object with count of transactions and total amount for given startDate and list of transactions.
   *
   * @param {string} startDate String representation of date in YYYY-MM-DD format
   * @param {object} data Array of transaction objects, or a single transaction object
   * @param {string} langCode Language code for formatting total
   * @param {string} membershipCountryCode Two-character country-code of the member's membership
   * @return {{count: number, total: (*|amt)}}
   */
  static numberOfTransactions(startDate: string, data: $TSFixMe, langCode = 'en-US', membershipCountryCode = 'US') {
    const transactions = data;
    let count = 0;
    let total = 0;

    let currency = currencyCodes[membershipCountryCode.toUpperCase()] || 'USD'; // Defaults to USD
    if (Array.isArray(transactions) && !_isEmpty(transactions)) {
      transactions.forEach((arrayItem) => {
        currency = arrayItem.currencyCode || 'USD';
        if (arrayItem.postedDate >= startDate) {
          count += 1;
          if (!_isEmpty(arrayItem.absoluteAmount)) {
            total += parseFloat(arrayItem.absoluteAmount);
          }
        }
      });
    } else if ((transactions as $TSFixMe)?.postedDate >= startDate) {
      count = 1;
      total = parseFloat((transactions as $TSFixMe).amount);
      currency = (transactions as $TSFixMe).currencyCode || 'USD';
    }
    return {count, total: utils.convertAmountToString(total, langCode, currency)};
  }

  /**
   * Returns a boolean after checking the partnerId from member partnerDetails
   *
   * @param {object} partnerDetails Object containing the partnerId and partnerUnitId
   * @return {boolean}
   */
  static isDirectToConsumer(partnerDetails: PartnerDetails | {}) {
    const dtcPartnerList = [1000];
    const partnerId = _get(partnerDetails, 'partnerId', 0);
    return dtcPartnerList.indexOf(partnerId) > -1;
  }

  /**
   * Checks if the user's feature set includes any Norton Small Business (NSB) feature codes.
   * @param {number} userFeatureSet - Feature code string.
   * @returns {boolean} True if any NSB feature code is present, otherwise false.
   */
  static hasNSBFeatureSet(userFeatureSetId: number): boolean {
    const nsbCodes = [4111, 3911, 5163];
    return nsbCodes.includes(userFeatureSetId);
  }

  /**
   * Constructs the appropriate URL for upgrade redirect, based on PSN and originId (app GUI origin)
   * @param {string} psn - Product Serial Number
   * @param {string} originId - The id of the origination for the inid query parameter in the URL, if any
   */
  static getUpgradeUrl(psn: string, originId = '') {
    let url = `${window.REACT_APP_UPGRADE_PLAN_URL}${psn}`;

    if (!_isEmpty(originId)) {
      const inidParam = '&inid=ss2up_mynorton-lifelock_store_upgrade';
      url = url.replace(inidParam, `${inidParam}_${originId}`);
    }

    return url;
  }

  /**
   * Returns an object with count of transactions and total amount for given startDate and list of transactions.
   *
   * @param {Object} auth Auth object containing all the member related data
   * @param {object} transactions Object of complete txm state
   * @param {string} modalType String val depicting the type of modal, for which content is needed
   * @param {StringMap} stringBundle Localized stringBundle
   * @param {boolean} isZeroStateForModal Boolean val indicating if there are records added for given modalType
   * @param {Object} colpData - subscription related data from colp api
   * @return {Object} aside content for the given modalType
   */
  static getAsideContentForDWMModals(
    auth: AuthState,
    modalType: string,
    stringBundle: StringMap,
    transactions: Object,
    isZeroStateForModal: boolean,
    colpData: Object
  ): object {
    const productFeatures = _get(auth, 'user.primaryMember.plan.productFeatures', {});
    const partnerDetails = _get(auth, 'user.primaryMember.plan.partnerDetails', {});
    const feautureSetId = this.safeGet(auth, 'user.primaryMember.plan.featureSetId', 0);
    const primarySubscriptionStart = _get(auth, 'user.primaryMember.primarySubscriptionStart', '');
    const whiteLabelClient = _get(auth, 'whiteLabelClient', '');
    const isDirectToConsumer = utils.isDirectToConsumer(partnerDetails);
    const isNSBUser = utils.hasNSBFeatureSet(feautureSetId);
    const isTxmUpgradeScenario = utils.isTxmUpgradeScenario(productFeatures, whiteLabelClient);
    const institutionsList = _get(transactions, 'institutionsList', {});
    const isAccountAddedForTxm = !institutionsList.noResults;
    const gaCategory = `DWM Modal - ${modalType}`;

    let title = '',
      description = '',
      dataTestId = '';

    switch (modalType) {
      case 'creditCard':
        {
          title = stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TITLE_CREDIT_CARD;
          description = stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TEXT1_CREDIT_CARD;
          dataTestId = 'test-credit-card-modal-aside-content';
        }
        break;
      case 'bankAccount': {
        title = stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TITLE;
        description = stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TEXT1;
        dataTestId = 'test-bank-modal-aside-content';
      }
    }
    const imageTag = (
      <img
        src={utils.getCdnImagesPath(
          '/dsp-northstar/monitored-info/monitored-info-credit-card@1x.png',
          whiteLabelClient
        )}
        alt="credit-card"
        className="m-auto flex h-32 w-56"
      />
    );

    const tagClassNames = `${bannerTextContainerClasses} w-fit font-bold text-sm mb-2 px-2`;
    const spotLightTag = (
      <div className={tagClassNames} data-testid="spotlight-tile-tag">
        {stringBundle.UNLOCK_PREMIUM_FEATURES}
      </div>
    );

    const defaultAsideContent = {
      title: title,
      description: [description],
      testIdentifier: dataTestId,
    };
    let asideContent = {},
      gaLabel = '';

    // if there are records for monitorInfo, if DTC partnerCode, if txm feature is available and no accounts added for txm
    if (
      !isZeroStateForModal &&
      isDirectToConsumer &&
      utils.isFeaturePresent(productFeatures, 'transactionMonitoring') &&
      !isAccountAddedForTxm
    ) {
      gaLabel = 'TXM Activation';
      asideContent = {
        title: stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TXM_TITLE,
        description: [stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TXM_MSG],
        testIdentifier: dataTestId,
        icon: imageTag,
        ctaText: stringBundle.GET_STARTED,
        ctaUrl: '/transactions',
        hasImageAndCta: true,
        showTooltip: true,
        tooltipTitle: title,
        tooltipParagraphArray: [description],
        isCtaUrlInApp: true,
        gaOptions: {gaCategory, gaLabel},
      };
    } else if (!isZeroStateForModal && isDirectToConsumer && isTxmUpgradeScenario && !isNSBUser) {
      // if standard plan, calculate the subscription age, if the subscriptionAge > 3 months, then only show upsell msg
      const standardPlanAge = dateUtils.getDifferenceInMonths(new Date(primarySubscriptionStart), new Date());
      if (standardPlanAge >= STANDARD_PLAN_CUTOFF_MONTHS_FOR_UPSELL_MSG) {
        const psn = _get(colpData, 'subscriptionPsn', '');

        const upsellUrl = utils.getUpgradeUrl(psn);
        gaLabel = 'Plan Upgrade';

        asideContent = {
          title: stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TXM_UPGRADE_TITLE,
          description: [stringBundle.MANAGE_MODAL_INTERNET_MONITORING_INFO_TXM_UPGRADE_MSG],
          testIdentifier: dataTestId,
          icon: spotLightTag,
          ctaText: stringBundle.VIEW_OFFER,
          ctaUrl: upsellUrl,
          hasImageAndCta: true,
          showTooltip: true,
          tooltipTitle: title,
          tooltipParagraphArray: [description],
          isCtaUrlInApp: false,
          gaOptions: {gaCategory, gaLabel},
        };
      } else {
        asideContent = defaultAsideContent;
      }
    } else {
      asideContent = defaultAsideContent;
    }
    return asideContent;
  }

  /**
   * Returns an object containing the aside content configuration for address-related modals.
   * This content includes titles, descriptions, icons, CTAs and tracking data.
   *
   * @param {AuthState} auth - Authentication state object containing user and plan information
   * @param {string} modalType - Type of modal to generate aside content for (e.g. 'creditCard', 'bankAccount')
   * @param {StringMap} stringBundle - Object containing localized strings for the UI
   * @param {Object} transactions - Object containing transaction-related state and data
   * @param {boolean} isZeroStateForModal - Flag indicating if there are any records for this modal type
   * @param {Object} colpData - Subscription related data from COLP API
   *
   * @returns {Object} Aside content configuration object containing:
   * - title: string - Title text to display
   * - description: string[] - Array of description text paragraphs
   * - testIdentifier: string - Test ID for QA automation
   * - icon?: ReactElement - Optional icon component to display
   * - ctaText?: string - Optional call-to-action button text
   * - ctaUrl?: string - Optional URL for CTA button
   * - hasImageAndCta?: boolean - Flag indicating if content has both image and CTA
   * - showTooltip?: boolean - Flag to show tooltip
   * - tooltipTitle?: string - Title text for tooltip
   * - tooltipParagraphArray?: string[] - Array of tooltip content paragraphs
   * - isCtaUrlInApp?: boolean - Flag indicating if CTA URL is internal to app
   * - gaOptions?: Object - Google Analytics tracking configuration
   */
  static getAsideContentForAddressModals(
    auth: AuthState,
    modalType: string,
    stringBundle: StringMap,
    colpData: Object,
    numberOfAddresses: number
  ): object {
    const productFeatures = _get(auth, 'user.primaryMember.plan.productFeatures', {});
    const partnerDetails = _get(auth, 'user.primaryMember.plan.partnerDetails', {});
    const feautureSetId = this.safeGet(auth, 'user.primaryMember.plan.featureSetId', 0);
    const primarySubscriptionStart = _get(auth, 'user.primaryMember.primarySubscriptionStart', '');
    const isDirectToConsumer = utils.isDirectToConsumer(partnerDetails);
    const psn = _get(colpData, 'subscriptionPsn', '');
    const userCountryCode = _get(auth, 'user.countryCode', 'US');
    const userBrowserLangCode = _get(auth, 'user.browserLangCode', 'en');
    const isNSBUser = utils.hasNSBFeatureSet(feautureSetId);
    let asideContent = {};

    const showAlternativeVersion = numberOfAddresses > 1;
    const hasNoHomeTitleFeature = !utils.isFeaturePresent(productFeatures, 'homeTitle');
    const userIsInUSAndBrowserEnglish =
      userCountryCode === 'US' && (userBrowserLangCode === 'en' || userBrowserLangCode.startsWith('en-'));
    const standardPlanAge = dateUtils.getDifferenceInMonths(new Date(primarySubscriptionStart), new Date());
    const ctaUrl = `${window.REACT_APP_VIEW_HOME_TITLE_OFFER_URL}${psn}`;

    if (modalType === 'address') {
      if (
        showAlternativeVersion &&
        hasNoHomeTitleFeature &&
        isDirectToConsumer &&
        !isNSBUser &&
        userIsInUSAndBrowserEnglish &&
        standardPlanAge >= STANDARD_PLAN_CUTOFF_MONTHS_FOR_UPSELL_MSG
      ) {
        asideContent = {
          title: stringBundle.MANAGE_MODAL_ADDRESS_INFO_TITLE_ADDRESS_B,
          description: [stringBundle.MANAGE_MODAL_ADDRESS_INFO_TEXT1_B, stringBundle.MANAGE_MODAL_ADDRESS_INFO_TEXT2_B],
          testIdentifier: 'test-address-modal-aside-content',
          headerLabel: stringBundle.UNLOCK_PREMIUM_FEATURES,
          headerLabelClass:
            'font-inter mb-2 inline-flex items-center rounded bg-accent p-2 py-1 text-sm font-bold text-onAccent-primary',
          ctaText: stringBundle.VIEW_OFFER,
          ctaUrl,
          hasImageAndCta: true,
          showTooltip: true,
          tooltipTitle: stringBundle.MANAGE_MODAL_ADDRESS_INFO_TITLE_ADDRESS,
          tooltipParagraphArray: [
            stringBundle.MANAGE_MODAL_ADDRESS_INFO_TEXT1,
            stringBundle.MANAGE_MODAL_ADDRESS_INFO_TEXT2,
          ],
          gaOptions: {gaCategory: 'Home Title Upgrade banner', gaLabel: 'home title views'},
        };
      } else {
        asideContent = {
          title: stringBundle.MANAGE_MODAL_ADDRESS_INFO_TITLE_ADDRESS,
          description: [stringBundle.MANAGE_MODAL_ADDRESS_INFO_TEXT1, stringBundle.MANAGE_MODAL_ADDRESS_INFO_TEXT2],
          testIdentifier: 'test-address-modal-aside-content',
          showTooltip: false,
          gaOptions: {gaCategory: `Address Modal - ${modalType}`, gaLabel: 'address modal views'},
        };
      }
    }

    return asideContent;
  }
  /**
   * Checks if the current user has Transactions available for him.
   *
   * @param currentUser
   * @returns {boolean}
   */
  // @ts-expect-error TS(7006) FIXME: Parameter 'currentUser' implicitly has an 'any' ty... Remove this comment to see the full error message
  static showTransactions(currentUser) {
    const productFeatures = _get(currentUser, 'plan.productFeatures', '');
    const featureStatus = _get(productFeatures, 'transactionMonitoring.featureStatus', '');
    const fulfillmentStatus = _get(productFeatures, 'transactionMonitoring.fulfillmentStatus', '');
    const featureStatusJp = _get(productFeatures, 'transactionMonitoringJp.featureStatus', '');
    const fulfillmentStatusJp = _get(productFeatures, 'transactionMonitoringJp.fulfillmentStatus', '');
    const featureStatusUk = _get(productFeatures, 'financialMonitoringUk.featureStatus', '');
    const fulfillmentStatusUk = _get(productFeatures, 'financialMonitoringUk.fulfillmentStatus', '');
    const featureStatusMultiAggregator = _get(productFeatures, 'fwMultiAggregator.featureStatus', '');
    const fulfillmentStatusMultiAggregator = _get(productFeatures, 'fwMultiAggregator.fulfillmentStatus', '');

    return (
      (featureStatus === 'AVAILABLE' && fulfillmentStatus === 'ENROLLED') ||
      (featureStatusJp === 'AVAILABLE' && fulfillmentStatusJp === 'ENROLLED') ||
      (featureStatusUk === 'AVAILABLE' && fulfillmentStatusUk === 'ENROLLED') ||
      (featureStatusMultiAggregator === 'AVAILABLE' && fulfillmentStatusMultiAggregator === 'ENROLLED')
    );
  }

  /**
   * Check if the language code is french dialect.
   *
   * @returns {boolean}
   */
  static isFrenchLangCode() {
    const langCode = getLangCode().toLowerCase();
    return langCode.includes('fr');
  }

  /**
   * Checks if the user has Transactions fulfillmentStatus is pending.
   *
   * @param productFeatures - user's plan specific product features
   * @returns {boolean}
   */
  static isTransactionsPending(productFeatures: ProductFeatures) {
    const featureStatus = _get(productFeatures, 'transactionMonitoring.featureStatus', '');
    const fulfillmentStatus = _get(productFeatures, 'transactionMonitoring.fulfillmentStatus', '');
    const featureStatusJp = _get(productFeatures, 'transactionMonitoringJp.featureStatus', '');
    const fulfillmentStatusJp = _get(productFeatures, 'transactionMonitoringJp.fulfillmentStatus', '');
    const featureStatusUK = _get(productFeatures, 'financialMonitoringUk.featureStatus', '');
    const fulfillmentStatusUK = _get(productFeatures, 'financialMonitoringUk.fulfillmentStatus', '');
    const featureStatusMultiAggregator = _get(productFeatures, 'fwMultiAggregator.featureStatus', '');
    const fulfillmentStatusMultiAggregator = _get(productFeatures, 'fwMultiAggregator.fulfillmentStatus', '');

    return (
      (featureStatus === 'AVAILABLE' && fulfillmentStatus === 'PENDING') ||
      (featureStatusJp === 'AVAILABLE' && fulfillmentStatusJp === 'PENDING') ||
      (featureStatusUK === 'AVAILABLE' && fulfillmentStatusUK === 'PENDING') ||
      (featureStatusMultiAggregator === 'AVAILABLE' && fulfillmentStatusMultiAggregator === 'PENDING')
    );
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'institutionId' implicitly has an 'any' ... Remove this comment to see the full error message
  static getBankInfoByInstitutionId(institutionId) {
    const InstitutionIdMap = {
      '39': {
        id: 0,
        label: 'Chase',
        altText: 'Chase Bank',
      },
      '2852': {
        id: 1,
        label: 'BOA',
        altText: 'Bank of America',
      },
      '1503': {
        id: 2,
        label: 'Citi',
        altText: 'Citibank',
      },
      '524': {
        id: 3,
        label: 'US',
        altText: 'U.S. Bank',
      },
      '1649': {
        id: 4,
        label: 'MS',
        altText: 'Morgan Stanley',
      },
      '2162': {
        id: 5,
        label: 'PNC',
        altText: 'PNC',
      },
      '2856': {
        id: 6,
        label: 'CapOne',
        altText: 'Capital One',
      },
      '3612': {
        id: 7,
        label: 'HSBC',
        altText: 'HSBC Bank',
      },
      '4132': {
        id: 9,
        label: 'TD',
        altText: 'TD Bank',
      },
      '3010': {
        id: 11,
        label: 'BB&T',
        altText: 'BB&T',
      },
      '2383': {
        id: 12,
        label: 'SunTrust',
        altText: 'SunTrust Bank',
      },
      '9565': {
        id: 13,
        label: 'Ally',
        altText: 'Ally Bank',
      },
      '16441': {
        id: 14,
        altText: 'RBC',
      },
      '16486': {
        id: 15,
        altText: 'Citizens Bank',
      },
      '3278': {
        id: 16,
        altText: 'USAA',
      },
      '5': {
        id: 17,
        label: 'Wells',
        altText: 'Wells Fargo',
      },
      '21': {
        id: 18.5,
        label: 'Charles',
        altText: 'Charles Schwab',
      },
      '12': {
        id: 20,
        label: 'Amex',
        altText: 'American Express',
      },
    };

    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (InstitutionIdMap[institutionId] !== (null || undefined)) {
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const institution = InstitutionIdMap[institutionId];
      return {
        linearLogo: `bank-linear-logo-${institution.id + 1}`,
        label: institution.label,
        altText: institution.altText,
        id: institution.id,
      };
    }

    return {
      linearLogo: null,
      label: '',
      altText: '',
      id: 0,
    };
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'stringIncludingASCII' implicitly has an... Remove this comment to see the full error message
  static asc2Str(stringIncludingASCII) {
    if (_isEmpty(stringIncludingASCII)) return '';

    // @ts-expect-error TS(7006) FIXME: Parameter 'ascii' implicitly has an 'any' type.
    return stringIncludingASCII.replace(/&#([0-9]{2,3});/g, (ascii) => {
      return String.fromCharCode(parseInt(ascii.substring(2, ascii.length - 1), 10));
    });
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'name' implicitly has an 'any' type.
  static getParameterByName(name, url) {
    /* eslint-disable no-useless-escape */
    const updatedName = name.replace(/[\[\]]/g, '\\$&');
    /* eslint-disable no-useless-escape */
    const regex = new RegExp(`[?&]${updatedName}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url || window.location.href);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'evt' implicitly has an 'any' type.
  static numericTextInputOnly(evt) {
    const event = evt || window.event;
    event.persist();
    let key = event.keyCode || event.which;
    key = String.fromCharCode(key);
    const regex = /[0-9\uFF10-\uFF19]|\./;
    if (!regex.test(key)) {
      event.returnValue = false;
      if (event.preventDefault) event.preventDefault();
    }
  }

  /**
   * A helper function to convert full width numbers to half width
   * Example: half width - 3568, full width - ３５６８
   * @param {string}
   * @return {string} returns a number in string format
   **/
  // @ts-expect-error TS(7006) FIXME: Parameter 'cardNumber' implicitly has an 'any' typ... Remove this comment to see the full error message
  static getHalfWidthNumber(cardNumber) {
    let convertedNumber = cardNumber;
    //checks if number is full width and converts to half width
    const fullWidthRegex = /[\uFF10-\uFF19]/g;
    // @ts-expect-error TS(7006) FIXME: Parameter 'ch' implicitly has an 'any' type.
    convertedNumber = convertedNumber.replace(fullWidthRegex, function(ch) {
      return String.fromCharCode(ch.charCodeAt(0) - 65248);
    });
    return convertedNumber;
  }

  /*eslint-disable no-bitwise*/
  // @ts-expect-error TS(7006) FIXME: Parameter 'value' implicitly has an 'any' type.
  static isValidLuhn(value) {
    const cardNumber = this.getHalfWidthNumber(value);
    let len = cardNumber.length;
    let mul = 0;
    const prodArr = [
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      [0, 2, 4, 6, 8, 1, 3, 5, 7, 9],
    ];
    let sum = 0;
    while (len--) {
      sum += prodArr[mul][parseInt(cardNumber.charAt(len), 10)];
      mul ^= 1;
    }
    return sum % 10 === 0 && sum > 0;
  }

  /**
   * Returns URL of image corresponding to path that is relative to memberportal img folder.
   * @param {string} path - path of the image file, somewhere in memberportal img folder
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user. Used to select partner-specific image.
   * @returns
   */
  static getCdnPath(path: string, whiteLabelClient?: string) {
    if (whiteLabelClient) {
      // CDN paths "avast" and "avg" should be used for avast_intl and avg_intl
      const client =
        whiteLabelClient === 'avast_intl' ? 'avast' : whiteLabelClient === 'avg_intl' ? 'avg' : whiteLabelClient;
      return `${window.REACT_APP_CDN_HOST}${window.REACT_APP_WHITE_LABEL_CDN_IMAGES_PATH}/${client}/memberportal/img${path}`;
    }
    return `${window.REACT_APP_CDN_HOST}${window.REACT_APP_ASSETS_CDN_PATH}${path}`;
  }

  /**
   * Returns URL of image corresponding to path that is relative to either whitelabel images folder or secure images folder.
   * White label images are in their own directory structure, separate from NortonLifeLock images.
   *
   * @param {string} path - path of the image file.  Must have a leading '/' character.
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user. Used to select partner-specific image.
   * @returns {string} - CDN URL of the image file
   */
  static getCdnImagesPath(path: string, whiteLabelClient: string | null) {
    if (whiteLabelClient) {
      // CDN paths "avast" and "avg" should be used for avast_intl and avg_intl
      const client =
        whiteLabelClient === 'avast_intl' ? 'avast' : whiteLabelClient === 'avg_intl' ? 'avg' : whiteLabelClient;
      return window.REACT_APP_CDN_HOST + window.REACT_APP_WHITE_LABEL_CDN_IMAGES_PATH + '/' + client + path;
    }
    return `${window.REACT_APP_CDN_HOST}${window.REACT_APP_ASSETS_CDN_IMAGES_PATH}${path}`;
  }

  /**
   * Returns the CDN URL of a Reimagine (Norton ODS) image. Reimagine assets live under the shared
   * `/reimagine` directory, so this prepends that prefix and delegates to getCdnImagesPath,
   * keeping white-label client resolution identical to the base helper.
   *
   * @param {string} path - path of the image file relative to the reimagine folder. Must have a leading '/' character.
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user. Used to select partner-specific image.
   * @returns {string} - CDN URL of the reimagine image file
   */
  static getCdnReimagineImagesPath(path: string, whiteLabelClient: string | null) {
    return utils.getCdnImagesPath(`/reimagine${path}`, whiteLabelClient);
  }

  /**
   * Returns the CDN URL for a reimagine or legacy image based on theme eligibility.
   *
   * @param {boolean} isReimagineThemeEligible - result of `utils.isReimagineThemeEligible(auth)`.
   * @param {string | null} whiteLabelClient - Identifies the white label client; used to select partner-specific image.
   * @param {string} reimaginePath - Path relative to `/reimagine` (typically a `REIMAGINE_IMAGES` value).
   * @param {string} legacyPath - Full path for `getCdnImagesPath` (typically `/dsp-northstar/...`).
   * @returns {string} - Fully resolved CDN URL for the selected theme.
   */
  static getThemedCdnImagesPath(
    isReimagineThemeEligible: boolean,
    whiteLabelClient: string | null,
    reimaginePath: string,
    legacyPath: string
  ): string {
    return isReimagineThemeEligible
      ? utils.getCdnReimagineImagesPath(reimaginePath, whiteLabelClient)
      : utils.getCdnImagesPath(legacyPath, whiteLabelClient);
  }

  /**
   * Gets country specific values of plan details by auth countryCode
   *
   * @param {string} countryCode - auth countryCode
   * @param {StringMap} localizedStringBundle - string bundle for messaging
   * @param {string} displayLang - value for displayLang in site director support url
   * @param {string} partnerId - value for partnerId in site director support url
   * @param {string} puId - value for puid in site director support url
   * @returns {object} - Returns an object of country specific values
   */
  static getCountrySpecificPlanDetailsValues(
    countryCode: string,
    localizedStringBundle: StringMap,
    displayLang: string,
    partnerId: string,
    puId: string
  ) {
    let supportPhone,
      countryName,
      disclaimer2,
      langCode,
      supportLinkRestoration,
      supportPhoneRestoration,
      timingsRestoration,
      lmsSupportTimings,
      lmsSupportPhone,
      legalUrl = '',
      restorationMessageSuffix = '';
    supportLinkRestoration = utils.getSupportUrl('Restoration_Support', displayLang, countryCode, partnerId, puId);
    switch (countryCode) {
      case 'NZ':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_NZ;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_NZ || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_NZ;
        supportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_NZ;
        lmsSupportTimings = '';
        countryName = localizedStringBundle.COUNTRY_ADJECTIVE_NZ;
        legalUrl = window.PLAN_DETAILS_LEGAL_URL_NZ;
        disclaimer2 = interpolate(localizedStringBundle.PLAN_DETAILS_DISCLAIMER2_NZ, {legalUrl});
        langCode = 'en-NZ';
        restorationMessageSuffix = interpolate(localizedStringBundle.TO_OPEN_ONE, {supportPhone});
        break;
      case 'FR':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_FR;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_FR || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_FR;
        lmsSupportTimings =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_FR || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_FR;
        lmsSupportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_FR;
        break;
      case 'US':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_US;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_US || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_US;
        lmsSupportTimings =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_US || localizedStringBundle.CSPB_LMS_SUPPORT_TIMINGS_US;
        lmsSupportPhone = window.REACT_APP_LMS_SUPPORT_NUMBER_US;
        restorationMessageSuffix = '';
        break;
      case 'IN':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_IN;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_IN || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_IN;
        lmsSupportTimings =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_IN || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_IN;
        restorationMessageSuffix = '';
        supportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_IN;
        break;
      case 'DE':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_DE;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_DE || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_DE;
        lmsSupportTimings =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_DE || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_DE;
        lmsSupportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_DE;
        break;
      case 'GB':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_GB;
        lmsSupportTimings =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_GB || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_GB;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_GB || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_GB;
        break;
      case 'BR':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_BR;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_BR || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_BR;
        supportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_BR;
        lmsSupportTimings = '';
        legalUrl = '';
        disclaimer2 = '';
        langCode = 'pt-BR';
        restorationMessageSuffix = interpolate(localizedStringBundle.TO_OPEN_ONE, {supportPhone});
        break;
      case 'MX':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_MX;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_MX || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_MX;
        supportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_MX;
        lmsSupportTimings = '';
        legalUrl = '';
        disclaimer2 = interpolate(localizedStringBundle.PLAN_DETAILS_DISCLAIMER2_NZ, {legalUrl});
        langCode = 'es-MX';
        restorationMessageSuffix = interpolate(localizedStringBundle.TO_OPEN_ONE, {supportPhone});
        break;
      case 'JP':
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_JP;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_JP || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_JP;
        break;
      case 'AU':
      default:
        supportPhoneRestoration = window.REACT_APP_RESTORATION_SUPPORT_PHONE_AU;
        timingsRestoration =
          window.REACT_APP_RESTORATION_SUPPORT_TIMINGS_AU || localizedStringBundle.RESTORATION_SUPPORT_TIMINGS_AU;
        supportPhone = window.REACT_APP_RESTORATION_SUPPORT_PHONE_AU;
        lmsSupportTimings = '';
        countryName = localizedStringBundle.COUNTRY_ADJECTIVE_AU;
        legalUrl = window.PLAN_DETAILS_LEGAL_URL_AU;
        disclaimer2 = interpolate(localizedStringBundle.PLAN_DETAILS_DISCLAIMER2_AU, {legalUrl});
        langCode = 'en-AU';
        restorationMessageSuffix = interpolate(localizedStringBundle.TO_OPEN_ONE, {supportPhone});
        break;
    }

    return {
      supportPhone,
      countryName,
      disclaimer2,
      langCode,
      supportPhoneRestoration,
      supportLinkRestoration,
      timingsRestoration,
      lmsSupportTimings,
      lmsSupportPhone,
      restorationMessageSuffix,
    };
  }

  /**
   * Gets country specific values of restoration support number and timings by auth countryCode
   *
   * @param {string} countryCode - user's countryCode
   * @param {StringMap} stringBundle - lang string bundle
   *
   * @returns {JSX.Element} - Returns JSX element with country specific support timings and phone number
   */
  static getRestorationSupportInfo(countryCode: string, stringBundle: StringMap): JSX.Element {
    let stringBundleKey = 'RESTORATION_SUPPORT_TIMINGS_US';

    if (['IT', 'NL', 'PL', 'CH', 'BE', 'SE', 'IE'].includes(countryCode)) {
      stringBundleKey = 'RESTORATION_SUPPORT_TIMINGS_EU';
    } else if (countryCode && stringBundle[`RESTORATION_SUPPORT_TIMINGS_${countryCode}`]) {
      stringBundleKey = `RESTORATION_SUPPORT_TIMINGS_${countryCode}`;
    }
    const phoneNumber = window[`REACT_APP_RESTORATION_SUPPORT_PHONE_${countryCode}`]
      ? window[`REACT_APP_RESTORATION_SUPPORT_PHONE_${countryCode}`]
      : window.REACT_APP_RESTORATION_SUPPORT_PHONE_US;

    return (
      <>
        <a className="text-accent underline underline-offset-4 hover:no-underline" href={`tel:${phoneNumber}`}>
          {phoneNumber}
        </a>
        ,&nbsp;<span>{stringBundle[stringBundleKey]}.</span>
      </>
    );
  }

  /**
   * Gets country specific values by countryCode
   *
   * @param {string} countryCode - user countryCode
   * @param {string} param - default variable name from config file(Ex: if variable is window.REACT_APP_TXM_ERROR_PHONE(this will be US related variable), param will be 'REACT_APP_TXM_ERROR_PHONE')
   * @returns {object} - Returns country specific value(phone number, url etc.)
   */
  static getCountrySpecificValue(countryCode: string, param: string) {
    if (!countryCode || countryCode === 'US') {
      return window[param];
    }

    return window[`${param}_${countryCode}`];
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'bureau' implicitly has an 'any' type.
  static getCreditReportHistoryPath(bureau) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_CREDIT_REPORT_HISTORY_PATH}${bureau}`;
  }
  static getCreditPullDatePath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_CREDIT_NEXT_PULL_DATE}`;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'bureau' implicitly has an 'any' type.
  static getCreditReportPath(bureau) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_CREDIT_REPORT_PATH}${bureau}`;
  }

  static getRedirectToPrivacyResults() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_PRIVACY_RESULTS_PATH}`;
  }

  static getModifyAccountPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_MODIFY_ACCOUNT_PATH}`;
  }

  static getContactPreferencesPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_CONTACT_PREFERENCES_PATH}`;
  }

  static getStatesPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_STATES_PATH}`;
  }

  static getLogPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_LOG_PATH}`;
  }

  static getMembersPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_MEMBERS_PATH}`;
  }

  static getRefreshTokenPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_NSL_REFRESH_TOKEN_PATH}`;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'userId' implicitly has an 'any' type.
  static getSmmAccounts(userId) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_ACCOUNTS}/${userId}/accounts`;
  }
  // @ts-expect-error TS(7006) FIXME: Parameter 'userId' implicitly has an 'any' type.
  static addSmmAccount(userId, network, type) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_INIT_ACCOUNT}/?network=${network}&id=${userId}&mode=${type}`;
  }
  // @ts-expect-error TS(7006) FIXME: Parameter 'userAccountId' implicitly has an 'any' ... Remove this comment to see the full error message
  static reconnectSmmAccount(userAccountId, onlineAccountId, accountNetwork) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_INIT_ACCOUNT}/?network=${accountNetwork}&accountId=${onlineAccountId}&id=${userAccountId}`;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'userId' implicitly has an 'any' type.
  static deleteSmmPersonalAccount(userId, onlineAccountId, network) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_ACCOUNTS}/${userId}/account/${onlineAccountId}?network=${network}`;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'userId' implicitly has an 'any' type.
  static deleteSmmBusinessAccount(userId, network, smmAccountType) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_ACCOUNTS}/${userId}?network=${network}&mode=${smmAccountType}`;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'accountId' implicitly has an 'any' type... Remove this comment to see the full error message
  static connectSmmAccount(accountId) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_ACCOUNTS}/${accountId}`;
  }

  static getSmmOnboardPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_ONBOARD_STATUS_PATH}`;
  }

  static smmAlertUpdate() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SMM_ALERT_UPDATE}`;
  }

  static getPlanDetailsPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/planDetails`;
  }

  static getLogoutPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/logout`;
  }

  static getAlertSummaryPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERTS_SUMMARY_PATH}`;
  }

  static getAlertListInboxPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERTS_INBOX_PATH}`;
  }

  static getAlertListDarkWebPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERTS_DARK_WEB_INBOX_PATH}`;
  }

  static getAlertListDisputedPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERTS_DISPUTED_PATH}`;
  }

  /** Card Shield: merchant list URL (direct merchant-store API; REACT_APP_CARD_SHIELD_MERCHANT_API_BASE required). */
  static getCardShieldMerchantsPath(filters?: {
    page?: number;
    size?: number;
    /** Single status or multiple — repeated `status` query params (merchant-store may only accept one; check API) */
    status?: string | string[];
    /** Merchant-store list filter: HIGH | MEDIUM | LOW | UNKNOWN */
    riskLevel?: string;
    /**
     * Merchant name search; sent as query param `displayName`. Card Shield list UIs pass values from
     * `buildCardShieldMerchantListNameSearchParam` (plain text → `*text*` contains) or user-supplied `*` patterns.
     */
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const base = (window as {REACT_APP_CARD_SHIELD_MERCHANT_API_BASE?: string}).REACT_APP_CARD_SHIELD_MERCHANT_API_BASE;
    if (!base) return '';
    const params = new URLSearchParams();
    const page = filters?.page !== undefined ? filters.page + 1 : 1;
    const pageSize = filters?.size ?? 25;
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
    if (filters?.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      statuses.forEach((s) => {
        const t = typeof s === 'string' ? s.trim() : '';
        if (t) {
          params.append('status', t);
        }
      });
    }
    if (filters?.riskLevel) {
      params.append('riskLevel', filters.riskLevel);
    }
    if (filters?.search?.trim()) {
      params.append('displayName', filters.search.trim());
    }
    if (filters?.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters?.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }
    return utils.getCardShieldDirectApiUrl('', params.toString());
  }

  /**
   * Build Card Shield direct-API URL for the merchant-store gateway.
   * REACT_APP_CARD_SHIELD_MERCHANT_API_BASE may include or omit a trailing /api segment;
   * we normalize by stripping any trailing /api (or /) then appending /api once, so the
   * path is always .../api/merchants{/{merchantsPath}}.
   * @param {string} merchantsPath - optional path segment after .../merchants (e.g. '', 'summary', '{merchantId}')
   * @param {string} [queryString] - optional query string (without leading ?)
   * @returns {string} Full URL or '' if base is not set
   */
  static getCardShieldDirectApiUrl(merchantsPath: string, queryString = '') {
    const base = (window as {REACT_APP_CARD_SHIELD_MERCHANT_API_BASE?: string}).REACT_APP_CARD_SHIELD_MERCHANT_API_BASE;
    if (!base) return '';
    const withoutTrailingSlash = base.replace(/\/$/, '');
    const baseWithoutApi = withoutTrailingSlash.replace(/\/api\/?$/, '');
    const apiRoot = `${baseWithoutApi}/api`;
    const trimmedPath = merchantsPath.trim().replace(/^\/+/, '');
    const pathSuffix = trimmedPath ? `/${trimmedPath}` : '';
    return `${apiRoot}/merchants${pathSuffix}${queryString ? `?${queryString}` : ''}`;
  }

  /** Card Shield: summary URL (totalMerchants, lastScanDate, etc.). REACT_APP_CARD_SHIELD_MERCHANT_API_BASE required. */
  static getCardShieldSummaryPath() {
    return utils.getCardShieldDirectApiUrl('summary');
  }

  /**
   * Card Shield: GET `/api/health` on the merchant-store gateway (not under `/merchants/`).
   * Same base resolution as {@link getCardShieldDirectApiUrl}.
   */
  static getCardShieldHealthPath() {
    const base = (window as {REACT_APP_CARD_SHIELD_MERCHANT_API_BASE?: string}).REACT_APP_CARD_SHIELD_MERCHANT_API_BASE;
    if (!base) return '';
    const withoutTrailingSlash = base.replace(/\/$/, '');
    const baseWithoutApi = withoutTrailingSlash.replace(/\/api\/?$/, '');
    const apiRoot = `${baseWithoutApi}/api`;
    return `${apiRoot}/health`;
  }

  /** Card Shield merchant detail: GET .../merchants/{merchantId}. REACT_APP_CARD_SHIELD_MERCHANT_API_BASE required. */
  static getCardShieldMerchantDetailPath(merchantId: string) {
    const safeMerchantId = encodeURIComponent((merchantId || '').trim());
    if (!safeMerchantId) return '';
    return utils.getCardShieldDirectApiUrl(safeMerchantId);
  }

  /** Card Shield: opt-out info URL for a merchant (used by Remove Card modal). REACT_APP_CARD_SHIELD_MERCHANT_API_BASE required. */
  static getCardShieldOptOutInfoPath(merchantId: string) {
    return utils.getCardShieldDirectApiUrl(`${merchantId}/opt-out-info`);
  }

  static getTxmJpInitPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_TXM_JP_INIT_PATH}`;
  }

  static getTxmJpAccountPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_TXM_JP_ACCOUNT_PATH}`;
  }

  static getAllRecurringTransactionsPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALL_RECURRING_TRANSACTIONS_PATH}`;
  }

  static getLLOnboardingPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/members/onboardingFlag`;
  }

  static getFMActivationPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/members/fmActivationFlag`;
  }

  static getBreachSummaryPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/breach/summary`;
  }

  static getCardProtectionDataPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/cardProtectionData`;
  }

  static getTransactionsUnifiedPath(params: $TSFixMe) {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/finance/transactions?${params}`;
  }

  /**
   * Single unified transaction — path segment may be FM txnId or transactionId (member-api resolves either).
   * Card Shield merchant-store {@code lastTransactionId} is the txnId, not the list row {@code id}.
   */
  static getTransactionsUnifiedByIdPath(txnOrTransactionId: string) {
    const safeId = encodeURIComponent((txnOrTransactionId || '').trim());
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/finance/transactions/${safeId}`;
  }

  static getConnectionsPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/finance/connections`;
  }

  static getBalanceTrendPath(params: string) {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/finance/stats/balance-trend?${params}`;
  }

  /**
   * Get the fastlinktoken endpoint path for Yodlee migration
   * @returns {string} The fastlinktoken endpoint URL
   */
  static getFastLinkTokenPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/finance/fastlinktoken`;
  }
  /**
   * Get ISO-format date string for the given number of days backwards in time, or an empty string when
   * input is invalid or exceeds maxDaysBack (default 25 years)
   *
   * @param {number} daysBack - The number of days prior to the current moment in time
   * @param {number} maxDaysBack - The maximum number of days accepted, default of 25 years
   * @returns {string} An ISO-format date string, or empty string when input is invalid or beyond maximum value
   */
  static getIsoDateStringFromDaysBack(daysBack: number, maxDaysBack: number = 25 * 365) {
    const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

    // ensure the input is of proper type, and makes sense.  Otherwise, return empty string (no filtering)
    if (typeof daysBack !== 'number' || daysBack > maxDaysBack) {
      return '';
    }

    // generate the startDate query param string, which should be returned in all cases
    const startDateMilliseconds = Date.now() - daysBack * MILLISECONDS_PER_DAY;
    return new Date(startDateMilliseconds).toISOString();
  }

  /**
   * Generates a query-param string with "startDate" and "endDate" (if needed) parameters, used with alerts queries
   *
   * @param {number} daysBackToStartDate - Specifies the start date as number of days backwards in time from today
   * @param {number} daysBackToEndDate - Specifies the end date as number of days backwards in time from today
   * @returns {string} Query parameter string containing startDate and endDate (if applicable) for a data-api fiql-query
   */
  static getDateFilterQueryParams(daysBackToStartDate: number, daysBackToEndDate: number) {
    const startDate = daysBackToStartDate ? this.getIsoDateStringFromDaysBack(daysBackToStartDate) : '';
    const endDate = daysBackToEndDate ? this.getIsoDateStringFromDaysBack(daysBackToEndDate) : '';
    const startDateQueryParam = startDate ? `&startDate=${startDate}` : '';
    const endDateQueryParam = endDate ? `&endDate=${endDate}` : '';

    return startDateQueryParam + endDateQueryParam;
  }

  /**
   * Get the path to backend api for fetching archived alerts, using the date filter start/end date if provided
   *
   * @param {number} daysBackToStartDate - number of days backwards in time to use as start date in date filter
   * @param {number} daysBackToEndDate - - number of days backwards in time to use as end date in date filter
   * @returns {string} The host name, url and query params for fetching our archived alerts, with date filtering
   */
  static getAlertListArchivedPath(daysBackToStartDate: number, daysBackToEndDate: number) {
    let path = `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERTS_ARCHIVED_PATH}`;
    if (daysBackToStartDate > 0) {
      path += this.getDateFilterQueryParams(daysBackToStartDate, daysBackToEndDate);
    }
    return path;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'alertId' implicitly has an 'any' type.
  static getAlertDetailPath(alertId) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERT_DETAIL_PATH}/${alertId}`;
  }

  static getAlertsCountsPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/alerts/counts`;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'alertId' implicitly has an 'any' type.
  static getAlertDetailViewPath(alertId) {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_ALERT_DETAIL_VIEW_PATH}/${alertId}?useFallback=true`;
  }

  static getRestorationCasesPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_RESTORATION_CASES_PATH}`;
  }
  static getScamVerificationCasesPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SCAM_PROTECT_CASES_PATH}`;
  }

  static getUploadScamVerificationDocumentPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_SCAM_PROTECT_UPLOAD_PATH}`;
  }

  static getLPMPrivacyResultsSummaryPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_PRIVACY_RESULTS_SUMMARY_PATH}`;
  }

  /**
   * Returns member-api url path for the license api to validate the user entitlement.
   *
   * @returns {string} - Returns the url path for license api call
   */
  static getValidateLicensePath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_PRIVACY_MONITOR_VALIDATE_LICENSE}`;
  }

  /**
   * PDF API — relatives scan (proxied to local pdf-api in dev via /pdf-api).
   * The server decides cache vs. re-scan from freshness; no query params.
   * @returns {string}
   */
  static getPdfRelativesPath() {
    return `${window.REACT_APP_PDF_API_HOST || ''}${window.REACT_APP_PDF_RELATIVES_PATH}`;
  }

  static getPdfRelativesFeedbackPath() {
    return `${window.REACT_APP_PDF_API_HOST || ''}${window.REACT_APP_PDF_RELATIVES_FEEDBACK_PATH}`;
  }

  static isPdfRelativesEnabled() {
    return window.REACT_APP_ENABLE_PDF_RELATIVES === true && !!window.REACT_APP_PDF_RELATIVES_PATH;
  }

  /**
   * Whether the family-exposure (PDF-relatives) flow owns the dashboard family slot, meaning the
   * generic Add Family banner/tile must be suppressed. True when the feature is enabled AND either
   * the member is on manual renewal (slot reserved for the renewal upsell) OR the scan matched
   * exposed relatives (the review-exposure banner shows instead).
   *
   * @param isPdfAutoRenewalOpted - Whether the member has opted into auto-renewal (COLP).
   * @param hasPdfExposedRelatives - Whether the settled scan matched exposed relatives.
   * @returns {boolean} True when the family-exposure flow controls the slot (hide generic Add Family).
   */
  static isFamilyExposureFlow(isPdfAutoRenewalOpted: boolean, hasPdfExposedRelatives: boolean) {
    return utils.isPdfRelativesEnabled() && (!isPdfAutoRenewalOpted || hasPdfExposedRelatives);
  }

  static readonly FAMILY_EXPOSURE_PARTNER_ID = 1000;
  /**
   * DTC LifeLock product families (member `plan.productFamilyId`) eligible for the add-family surfaces —
   * shared by the family-exposure banner plan gate ({@link isFamilyExposurePlanEligible}) and the
   * add-family zero-state default-content gate ({@link useAddFamilyZeroStateOffer}). Single source of truth.
   */
  static readonly DTC_ADD_FAMILY_PRODUCT_FAMILY_IDS: readonly number[] = [38];

  /**
   * Additional plan gate layered on top of the base family-exposure eligibility: the member must be on
   * one of the gated LifeLock Standard product ids (`plan.productId` in
   * {@link LIFELOCK_STANDARD_STANDALONE_AND_FAMILY_PRODUCT_IDS}) AND on the DTC/eStore partner
   * (`plan.partnerDetails.partnerId` === 1000, via {@link isDirectToConsumer}). The product id is
   * compared numerically so it matches whether the API sends it as a number or string.
   *
   * @param auth - Redux `auth` state (`user.primaryMember.plan`).
   * @returns {boolean} True when the product id is one of the gated ids and the member is DTC.
   */
  static isFamilyExposurePlanEligible(auth: AuthState): boolean {
    const partnerId = _get(auth, 'user.primaryMember.plan.partnerDetails.partnerId', null);
    const productFamilyId = _get(auth, 'user.primaryMember.plan.productFamilyId', null);
    return (
      Number(partnerId) === utils.FAMILY_EXPOSURE_PARTNER_ID &&
      utils.DTC_ADD_FAMILY_PRODUCT_FAMILY_IDS.includes(Number(productFamilyId))
    );
  }

  static getLocksOnboardPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_LOCKS_ONBOARD_STATUS_PATH}`;
  }

  static getFcraConsentPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_FCRA_CONTENT_PATH}`;
  }

  static getReimbursementConsentPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/consent/reimbursement`;
  }

  static getCreditPersonalizationConsentPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/consent/credit-personalization`;
  }

  static getConsentPath(consentType: string) {
    switch (consentType) {
      case 'fcra':
        return this.getFcraConsentPath();
      case 'reimbursement':
        return this.getReimbursementConsentPath();
      case 'credit-personalization':
        return this.getCreditPersonalizationConsentPath();
      default:
        return `${window.REACT_APP_MEMBER_API_HOST}/v1/consent/${consentType}`;
    }
  }

  static getQuebecOptInPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_QUEBEC_OPT_IN_PATH}`;
  }

  static getAppDownloadUiPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_APP_DOWNLOAD_PATH}`;
  }

  static getGenerateOtpPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/auth/otp`;
  }

  static getBillingNotificationsPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/notifications/billing`;
  }

  static getColpSubscriptionPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/colpSubscription`;
  }

  static uploadRestorationDocumentPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_RESTORATION_UPLOAD_PATH}`;
  }

  static getEmailOtpGeneratePath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_EMAIL_OTP_GENERATE_PATH}`;
  }

  static getEmailOtpVerifyPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_EMAIL_OTP_VERIFY_PATH}`;
  }

  static getHomePropertiesPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_HOMEPROPERTIES_PATH}`;
  }

  static getTxmSearchProvidersPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/transactions/search-providers`;
  }

  /**
   * returns credit uk token endpoint path(token register)
   */
  static getTransunionUkRegisterPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_TRANSUNION_UK_REGISTER_PATH}`;
  }

  static resizeIframe(windowSpec?: GetWindowSpecPayload, setHeight?: number) {
    //use this function in componentDidMount and componentDidUpdate
    let minHeight = 0;
    if (windowSpec || (this as $TSFixMe).windowSpecTracker) {
      (this as $TSFixMe).windowSpecTracker = windowSpec || (this as $TSFixMe).windowSpecTracker;
      minHeight =
        (this as $TSFixMe).windowSpecTracker.windowHeight -
        (this as $TSFixMe).windowSpecTracker.footerHeight -
        (this as $TSFixMe).windowSpecTracker.headerHeight;
    }
    //the logic above is to handle an edge case where a modal opens up and the height of the iframe is not large enough to display.
    //assigning windowSpec to utils class as a form of a cache for another edge case where on refresh of page, multiple calls to this method are made and windowSpec is no longer available

    let payload = minHeight > document.body.clientHeight ? minHeight : document.body.clientHeight;

    if (setHeight) {
      payload = setHeight;
    }
    window.parent.postMessage({type: 'RESIZED', payload: payload}, window.REACT_APP_PARENT_HOST);
  }

  static scrollTo(scrollPosition = 0) {
    window.parent.postMessage({type: 'SCROLL_PARENT', payload: scrollPosition}, window.REACT_APP_PARENT_HOST);
  }

  /**
   * Scroll the hosted parent (iframe) and the in-app scroll container to the top.
   * Use after Card Shield (and similar) in-flow navigations so the next section starts at the top.
   */
  static scrollAppToTop() {
    utils.scrollTo(0);
    if (typeof document !== 'undefined') {
      document.getElementById('csp-app-container')?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  }

  static updateOnboardingFlag() {
    return `${window.REACT_APP_MEMBER_API_HOST}${window.REACT_APP_UPDATE_ONBOARDING_FLAG_PATH}`;
  }

  static getReimagineWelcomeFlagPath() {
    return `${window.REACT_APP_MEMBER_API_HOST}/v1/members/reimagineWelcomeFlag`;
  }

  /**
   * @typedef {"AVAILABLE"|"UPGRADE"|"NOT_AVAILABLE"|"ACTIVATE"} FeatureStatus
   */

  /**
   * @typedef {"ENROLLED"|"PENDING"|"CANCELLED"|"ERROR"|"NOT_AVAILABLE"|"DEFERRED"} FulfillmentStatus
   */

  /**
   * A number, or a string containing a number.
   * @typedef {Object} ProductStatus
   * @property {FeatureStatus} featureStatus
   * @property {FulfillmentStatus} fulfillmentStatus
   */

  /**
   * Given the full set of product features, returns true if the given feature is present.
   * @param {ProductFeatures} productFeatures
   * @param {string} feature - feature that is available for the current user.
   * @return {boolean} - returns true if given feature is present.
   */
  static isFeaturePresent(productFeatures: ProductFeatures | {}, feature: string) {
    return _get(productFeatures, `${feature}.featureStatus`, 'NOT_AVAILABLE') !== 'NOT_AVAILABLE';
  }

  /**
   * Returns true if credit score content should be visible, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static creditScoreVisible(productFeatures: ProductFeatures) {
    return (
      !utils.isPendingPIICollectionState(productFeatures) &&
      (utils.isFeaturePresent(productFeatures, 'creditScore_1B') ||
        utils.isFeaturePresent(productFeatures, 'creditScore_3B') ||
        utils.isFeaturePresent(productFeatures, 'creditScore1bCa') ||
        utils.isCreditUk(productFeatures))
    );
  }

  /**
   * Returns true if transaction monitoring content should be visible, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static txmVisible(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'transactionMonitoring') ||
      utils.isFeaturePresent(productFeatures, 'transactionMonitoringJp') ||
      utils.isFeaturePresent(productFeatures, 'financialMonitoringUk') ||
      utils.isFeaturePresent(productFeatures, 'fwMultiAggregator')
    );
  }

  /**
   * Returns true if Yodlee migration is enabled for financial monitoring
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static isYodleeMigrationEnabled(productFeatures: ProductFeatures) {
    const financialMonitoringUk = utils.safeGet(productFeatures, 'financialMonitoringUk.featureStatus', null);
    const transactionMonitoring = utils.safeGet(productFeatures, 'transactionMonitoring.featureStatus', null);
    return financialMonitoringUk === 'AVAILABLE' || transactionMonitoring === 'AVAILABLE';
  }

  static showUnifiedComponents({productFeatures}: {productFeatures: ProductFeatures}) {
    const isYodleeMigrationEnabled =
      utils.isFeaturePresent(productFeatures, 'financialMonitoringUk') ||
      utils.isFeaturePresent(productFeatures, 'transactionMonitoring');
    return isYodleeMigrationEnabled || utils.isFeaturePresent(productFeatures, 'fwMultiAggregator');
  }

  static isMultiAggregatorEnabled(productFeatures: ProductFeatures) {
    const featureStatusMultiAggregator = _get(productFeatures, 'fwMultiAggregator.featureStatus', null);
    const fulfillmentStatusMultiAggregator = _get(productFeatures, 'fwMultiAggregator.fulfillmentStatus', null);
    return featureStatusMultiAggregator === 'AVAILABLE' && fulfillmentStatusMultiAggregator === 'ENROLLED';
  }

  /**
   * Appends ` bdl` to GA event labels for users in the EBDL (fwMultiAggregator enrolled) cohort.
   * Use for Financial Monitoring / unified TXM surfaces only unless explicitly skipped.
   */
  static withFmEbdlCohortLabel(
    label: string,
    productFeatures: ProductFeatures | null | undefined,
    opts?: {skipEbdl?: boolean}
  ): string {
    if (opts?.skipEbdl) {
      return label;
    }
    if (productFeatures == null) {
      return label;
    }
    return this.isMultiAggregatorEnabled(productFeatures) ? `${label} bdl` : label;
  }

  /**
   * Returns true if the product features indicate a TXM upgrade scenario
   * @param {ProductFeatures} productFeatures
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user. Used to select partner-specific image.
   * @return {boolean}
   */
  static isTxmUpgradeScenario(productFeatures: ProductFeatures | {}, whiteLabelClient: string | null) {
    if (whiteLabelClient) {
      return false;
    }
    const transactionFeatureStatus = _get(productFeatures, 'transactionMonitoring.featureStatus', null);
    const transactionFulfillmentStatus = _get(productFeatures, 'transactionMonitoring.fulfillmentStatus', null);
    return transactionFeatureStatus === 'UPGRADE' && transactionFulfillmentStatus === 'NOT_AVAILABLE';
  }

  /**
   * Returns true if restorationInternational available, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static isRestorationIntl(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'restorationInternational') &&
      _get(productFeatures, 'restorationInternational.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if restorationSummary available, based on productFeatures and serviceCode
   * @param {ProductFeatures} productFeatures feature status and fulfillment status of various product features
   * @return {boolean}
   */

  static isRestorationAvailable(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'restorationSummary') &&
      _get(productFeatures, 'restorationSummary.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if scamProtect feature is available in the product features.
   * @param {ProductFeatures} productFeatures
   * @returns {boolean}
   */
  static hasScamVerificationFeature(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'scamProtect') &&
      _get(productFeatures, 'scamProtect.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if scamProtection feature is available in the product features.
   * @param {ProductFeatures} productFeatures
   * @returns {boolean}
   */
  static hasScamProtectionFeature(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'scamSupport') &&
      _get(productFeatures, 'scamSupport.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if AI Assistant feature is available, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasAiAssistantFeature(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'aiAssistant') &&
      _get(productFeatures, 'aiAssistant.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if restorationInternational available, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static isReimbursement(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'reimbursement') &&
      _get(productFeatures, 'reimbursement.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if planDetails feature is available, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static planDetailsVisible(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'planDetails') &&
      _get(productFeatures, 'planDetails.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true if txm jp available, based on productFeatures
   * @param {ProductFeatures} productFeatures feature status and fulfillment status of various product features
   * @return {boolean}
   */
  static isTxmJpAvailable(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'transactionMonitoringJp') &&
      _get(productFeatures, 'transactionMonitoringJp.featureStatus') === 'AVAILABLE'
    );
  }

  /**
   * Returns true when Card Shield UI should render in DSP.
   *
   * Eligibility (US-only, FM backing, credit entitlement, LL Std exclusion, partner gating, etc.) is computed in
   * Member API as `productFeatures.cardShield`. DSP applies deploy config (`REACT_APP_CARD_SHIELD_ENABLED`) and
   * reads that feature only — no separate country gate here.
   *
   * @see docs/features/card-shield/card-shield-visibility-and-entitlement.md
   *
   * @param {ProductFeatures} productFeatures - primary member plan.productFeatures
   * @param {string} [membershipCountryCode] - optional context for visibility debug logs (e.g. user.countryCode)
   * @param {{ surface?: string }} [options] - optional surface label for visibility debug logs
   * @return {boolean}
   */
  static cardShieldVisible(
    productFeatures: ProductFeatures | Record<string, never>,
    membershipCountryCode = '',
    options?: {surface?: string}
  ) {
    const configEnabled =
      typeof window !== 'undefined' &&
      (window as {REACT_APP_CARD_SHIELD_ENABLED?: boolean}).REACT_APP_CARD_SHIELD_ENABLED !== false;
    const memberApiCardShield = (productFeatures as ProductFeatures)?.cardShield;
    const featureAvailable = utils.isFeatureAvailable(productFeatures, 'cardShield');
    const visible = configEnabled && featureAvailable;

    if (options?.surface) {
      logCardShieldVisibilityFromMemberApi({
        surface: options.surface,
        membershipCountryCode,
        memberApiCardShield,
        configEnabled,
        featureAvailable,
        visible,
      });
    }

    return visible;
  }

  /**
   * Returns transaction threshold drop down list associated with countryCode
   * @param {string} countryCode - country code of user, to determine which drop down list to utilize
   * @param {StringMap} localizedStringBundle - string bundle for messaging
   * @return {object[]}
   */
  static getTxmPrefDropDownList(
    countryCode: string,
    localizedStringBundle: StringMap
  ): {name: string; value: string}[] {
    let dropdownList: number[] = [];
    switch (countryCode) {
      case 'AU':
      case 'GB':
      case 'CA':
      case 'NZ':
      case 'US': {
        dropdownList = [100, 200, 500, 1000, 2000, 5000, 10000, 25000];
        break;
      }
      case 'JP': {
        dropdownList = [
          300, 500, 1000, 3000, 5000, 10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 100000,
        ];
        break;
      }
    }
    return dropdownList.map((amount) => utils.getThresholdDropListEntry(amount, countryCode, localizedStringBundle));
  }

  /**
   * Returns properly formatted threshold values for associated country
   * @param {number} amount - drop down list amount, to be formatted to specific currency and returned in object.
   * @param {string} countryCode - country code of user, to determine which drop down list to utilize
   * @param {StringMap} localizedStringBundle - string bundle for messaging
   * @return {Object}
   */
  static getThresholdDropListEntry(amount: number, countryCode: string, localizedStringBundle: StringMap) {
    // Default currency is USD
    let memberCurrency = 'USD';

    if (countryCode) {
      memberCurrency = this.getCurrencyCode(countryCode.toUpperCase());
    }

    const langCode = getLangCode();
    const currencyDisplay = this.getCurrencyDisplay(langCode, memberCurrency);

    const formattedAmount = new Intl.NumberFormat(getLangCode(), {
      currency: memberCurrency,
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currencyDisplay,
    }).format(amount);

    return {
      name: interpolate(localizedStringBundle.MANAGE_AP_TXM_OVER_AMOUNT, {formattedAmount}),
      value: amount.toString(),
    };
  }

  /**
   * Returns true if creditScore and txm are visible, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static isOnboardingFlowOne(productFeatures: ProductFeatures) {
    return utils.creditScoreVisible(productFeatures) && utils.txmVisible(productFeatures);
  }

  /**
   * Returns true if restorationInternational.featureStatus is AVAILABLE based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static isOnboardingFlowTwo(productFeatures: ProductFeatures) {
    return utils.isRestorationIntl(productFeatures) || utils.isReimbursement(productFeatures);
  }

  /**
   * Returns product features for the primary member's plan.
   * @param {Object} auth
   * @return {ProductFeatures}
   */
  static getProductFeatures(auth: $TSFixMe) {
    return _get(auth, 'user.primaryMember.plan.productFeatures', {});
  }

  /**
   * Returns true if locks feature content should be visible, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static locksVisible(productFeatures: ProductFeatures) {
    return (
      utils.isFeaturePresent(productFeatures, 'creditLocks') || utils.isFeaturePresent(productFeatures, 'phoneLocks')
    );
  }

  /**
   * Returns true if locks feature content should be visible, based on productFeatures and feature flag
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static creditLocksAvailable(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'creditLocks');
  }

  /**
   * Returns true if locks feature content should be visible, based on productFeatures and feature flag
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static phoneLocksAvailable(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'phoneLocks');
  }

  /**
   * Returns true if monitored info feature content should be visible, based on productFeatures
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static monitoredInfoVisible(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'monitoredInfo');
  }

  /**
   * Returns true/false based on user's access to a page
   * @param {string} path to a particular page
   * @param {ProductFeatures} productFeatures feature status and fulfillment status of various product features
   * @param {Object} marketingDetails of the product
   * @param {string} whiteLabelClient - Identifies the white label client associated with the current user.
   * @param {Object} rest additional arguments (`isMLCardOffersActive`, `membershipCountryCode`, `privacyLicense`, `isPrivacyLicensePending`, …)
   * @return {boolean}
   */
  static hasAccessToRoute(
    path: string,
    productFeatures: ProductFeatures,
    marketingDetails: MarketingDetails,
    whiteLabelClient: string,
    rest?: $TSFixMe
  ) {
    // Destructure the rest parameter to get the isMLCardOffersActive flag. Default to false if not provided.
    const {isMLCardOffersActive = false, isOffersMarketplaceActive = false} = rest || {};
    if (path.startsWith('/card-shield/merchant/')) {
      return utils.cardShieldVisible(productFeatures, rest?.membershipCountryCode);
    }
    if (path === '/card-shield/past-removals' || path === '/card-shield/trusted') {
      return utils.cardShieldVisible(productFeatures, rest?.membershipCountryCode);
    }
    switch (path) {
      case '/transactions':
      case '/transactions/accounts':
      case '/transactions/all/search':
      case '/transactions/all':
      case '/txm-transactions/txmJp-add-account-error':
      case '/txm-monitoring/txmJp-add-account-error':
        return (
          (utils.txmVisible(productFeatures) && !utils.isTxmUpgradeScenario(productFeatures, whiteLabelClient)) ||
          utils.isFeaturePresent(productFeatures, 'fwMultiAggregator')
        );
      case '/card-shield':
        return utils.cardShieldVisible(productFeatures, rest?.membershipCountryCode);
      case '/transactions/recurring/all':
        return (
          utils.isFeaturePresent(productFeatures, 'financialMonitoringUk') ||
          this.showFmUiUsCa(productFeatures) ||
          utils.isFeaturePresent(productFeatures, 'fwMultiAggregator')
        );
      case '/monitoring':
      case '/monitoring/:section?/:accountId?':
        return utils.monitoredInfoVisible(productFeatures);
      case '/credit':
      case '/credit/:tab':
        return utils.creditScoreVisible(productFeatures);
      case '/credit-au':
        return utils.isCreditAu(productFeatures);
      case '/credit-monitor':
        return isMLCardOffersActive && utils.isCreditMonitorScenario(productFeatures);
      case '/dashboard':
        return utils.dashboardVisible(productFeatures, marketingDetails);
      case '/locks':
      case '/locks/freeze':
        return (
          utils.creditLocksAvailable(productFeatures) ||
          utils.phoneLocksAvailable(productFeatures) ||
          utils.isFeaturePresent(productFeatures, 'creditFreeze')
        );
      case '/hometitle':
        return utils.hasHomeTitleMonitoring(productFeatures);
      case '/privacymonitor':
        return (
          Boolean(rest?.isPrivacyLicensePending) ||
          utils.hasPrivacyMonitorAccess(productFeatures, rest?.privacyLicense ?? null)
        );
      case '/privacyadvisor':
        return utils.hasPrivacyAdvisor(productFeatures);
      case '/social-media-monitoring/add-account-error':
      case '/social-media-monitoring/add-account-success': {
        //if SMM is not available for primary member,check SMM feature is enabled for juniors,
        const isPrimarySmmEnabled = utils.hasSocialMediaMonitoring(productFeatures);
        return isPrimarySmmEnabled ? isPrimarySmmEnabled : utils.associatedMemberSmmFeatureAvailable(rest);
      }
      case '/plandetails':
        return utils.planDetailsVisible(productFeatures);
      case '/scam-verification':
        return utils.hasScamVerificationFeature(productFeatures);
      case '/scam-protection':
        return utils.hasScamProtectionFeature(productFeatures);
      case '/estate-planning':
      case '/estate-planning/documents':
      case '/estate-planning/notarize':
      case '/estate-planning/fund-trust':
        return utils.isEstatePlanningVisible(productFeatures);
      case '/genie':
        return utils.hasAiAssistantFeature(productFeatures);
      case '/marketplace':
      case '/marketplace/credit':
      case '/marketplace/savings':
        // Same conditions as credit-monitor or credit (accessible for credit users)
        return (
          isMLCardOffersActive &&
          isOffersMarketplaceActive &&
          (utils.isCreditMonitorScenario(productFeatures) || utils.creditScoreVisible(productFeatures))
        );
      default:
        return false;
    }
  }

  /**
   * Returns the title string for the App header
   * @param {ProductFeatures} productFeatures - object specifying product features for current user's plan
   * @param {object} marketingDetails - object specifying marketing details for current user's plan
   * @param {StringMap} localizedStringBundle - An object map with localized (translated) strings
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user. Used to select partner-specific image.
   * @returns {string}
   */
  static getHeaderTitleString(
    productFeatures: ProductFeatures,
    marketingDetails: MarketingDetails,
    localizedStringBundle: StringMap,
    whiteLabelClient: string
  ) {
    const restorationServiceCode = _get(productFeatures, 'restorationSummary.serviceCode', '');
    if (whiteLabelClient) {
      if (window.REACT_APP_PRODUCT_NAME === 'NortonLifelock') {
        return localizedStringBundle.ID_THEFT_PROTECTION;
      } else {
        return window.REACT_APP_PRODUCT_NAME;
      }
    }
    switch (restorationServiceCode) {
      case 'LLS_RESTORATIONLITE':
        return localizedStringBundle.LIFELOCK_IDENTITY_ADVISOR;
      case 'LLS_RESTORATIONLITE_NOCASES':
        return utils.hasScamVerificationFeature(productFeatures)
          ? localizedStringBundle.ID_THEFT_PROTECTION
          : localizedStringBundle.ID_ADVISOR_JAPAN_TITLE;
      case 'LLS_RESTORATIONASST':
        return localizedStringBundle.ID_NAVIGATOR;
      case 'LLS_RESTORATION_HOMETITLE':
        return utils.isHomeTitleStandalone(marketingDetails)
          ? localizedStringBundle.HOMETITLE_PROTECT
          : localizedStringBundle.ID_THEFT_PROTECTION;
      case 'LLS_RESTORATION_ANZ':
        if (utils.isReimbursement(productFeatures)) {
          return utils.isIdAdvisorStandalone(marketingDetails)
            ? localizedStringBundle.ID_ADVISOR_TITLE
            : localizedStringBundle.IDENTITY_THEFT;
        }
        return utils.isIdAdvisorStandalone(marketingDetails)
          ? localizedStringBundle.ID_ADVISOR_TITLE
          : localizedStringBundle.ID_THEFT_PROTECTION;
      case 'LLS_RESTORATION_IND':
        return utils.isIdAdvisorStandalone(marketingDetails)
          ? localizedStringBundle.IDENTITY_THEFT
          : localizedStringBundle.ID_ADVISOR_IDENTITY_TITLE;
      default: {
        if (window.REACT_APP_PRODUCT_NAME === 'NortonLifelock') {
          return localizedStringBundle.ID_THEFT_PROTECTION;
        } else {
          return window.REACT_APP_PRODUCT_NAME;
        }
      }
    }
  }

  /**
   * Returns true when user has freeze access only
   * @param {ProductFeatures} productFeatures - object specifying product features for current user's plan
   * @returns {boolean}
   */
  static showFreeze(productFeatures: ProductFeatures) {
    const isCreditLocksNotAvailable = _get(productFeatures, 'creditLocks.featureStatus', '') !== 'AVAILABLE';

    const isPaydayLoanLocksNotAvailable = _get(productFeatures, 'paydayLoanLocks.featureStatus', '') !== 'AVAILABLE';
    const isPhoneLocksNotAvailable = _get(productFeatures, 'phoneLocks.featureStatus', '') !== 'AVAILABLE';

    const isCreditFreezeAvailable = _get(productFeatures, 'creditFreeze.featureStatus', '') === 'AVAILABLE';
    const isCreditActivated = !(_get(productFeatures, 'creditScore_3B.featureStatus', '') === 'ACTIVATE');
    return (
      isCreditActivated &&
      isCreditLocksNotAvailable &&
      isPaydayLoanLocksNotAvailable &&
      isPhoneLocksNotAvailable &&
      isCreditFreezeAvailable
    );
  }

  /**
   * Returns whether the user has US credit features.
   * Used to add Credit Offers tile to the appropriate parent page.
   * @param {ProductFeatures} productFeatures
   */
  static hasUSCredit(productFeatures: ProductFeatures) {
    return (
      !utils.isPendingPIICollectionState(productFeatures) &&
      (utils.isFeaturePresent(productFeatures, 'creditScore_1B') ||
        utils.isFeaturePresent(productFeatures, 'creditScore_3B')) &&
      !utils.isCreditUpgradeFeatures(productFeatures)
    );
  }

  /**
   * Returns whether the user is enrolled and not in upgrade scenario for credit features
   * @param {ProductFeatures} productFeatures
   * @returns {boolean}
   */
  static isCreditUser(productFeatures: ProductFeatures): boolean {
    return utils.creditScoreVisible(productFeatures) && !utils.isCreditUpgradeFeatures(productFeatures);
  }

  /**
   * Returns boolean indicating if the user has financial offers available in their product features
   * @param {ProductFeatures} productFeatures
   * @returns
   */
  static hasFinancialOffers(productFeatures: ProductFeatures): boolean {
    return utils.isFeatureAvailable(productFeatures, 'financialOffers');
  }

  /**
   * Returns boolean indicating if the user has mFeed feature enabled.
   * If the user is a white label client, the feature is enabled if the partner config variable has mFeed enabled.
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user.
   * @returns {boolean}
   */
  static hasMFeed(whiteLabelClient: string): boolean {
    if (whiteLabelClient) {
      return window.REACT_APP_SHOW_CONTENTFUL === true && window.REACT_APP_PARTNER_MFEED_ENABLED === true;
    }
    return window.REACT_APP_SHOW_CONTENTFUL === true;
  }

  /** Protection Pulse thumb feedback (dashboard carousel + Discover pulse card). Enabled only when config sets it true (local/dev). */
  static isMfeedFeedbackEnabled(): boolean {
    return window.REACT_APP_MFEED_FEEDBACK_ENABLED === true;
  }

  static showWarningIconForUnified(dynamicData: {connectionsResponse: ConnectionsResponse}): boolean {
    const connectionsResponse = _get(dynamicData, 'connectionsResponse', {});
    const connectionsData = _get(connectionsResponse, 'connections', []);

    let showWarningIconForUnified = false;
    connectionsData.forEach((response: {institutionStatusKey: string}) => {
      if (response.institutionStatusKey !== 'ACTIVE') {
        showWarningIconForUnified = true;
        return;
      }
    });
    return showWarningIconForUnified;
  }

  /**
   * Returns an array of menu items.  Typically used for the left nav with North Star design.
   * @param {ProductFeatures} productFeatures feature status and fulfillment status of various product features
   * @param {Object} marketingDetails of the product
   * @param {Object} dynamicData containing information about the user's alerts and other dynamic data to be added
   * @param {string} whiteLabelClient - Identifies white label client associated with the current user. Used to select partner-specific image.
   * @param {string} countryCode - countryCode of the user
   * @param {boolean} isMLCardOffersActive - boolean indicating if the AB test for MoneyLion's card offers is active. Can be deprecated when this feature is fully integrated into DSP.
   * @param {boolean} isOffersMarketplaceActive - boolean indicating if the AB test for the offers marketplace feature page is active. (combo of savings OR credit offers)
   * @param {boolean} showContentful - boolean indicating if the user has showContentful flag enabled in primaryMember
   * @param {boolean} isReimagineThemeEligible - boolean indicating if the user is eligible for the reimagine theme.
   * @returns {MenuItems}
   */
  static dspMenuList(
    productFeatures: ProductFeatures,
    marketingDetails: MarketingDetails,
    dynamicData: $TSFixMe,
    whiteLabelClient: string,
    isMLCardOffersActive = false,
    privacyLicense: $TSFixMe,
    isOffersMarketplaceActive = false,
    showContentful = false,
    isReimagineThemeEligible = false
  ) {
    const result: MenuItems = [];
    const showFreeze = this.showFreeze(productFeatures);
    const option = {category: 'Left-Nav', action: 'click', label: ''};
    const triangleWarningUrl = utils.getAttentionTriangleIconUrl(isReimagineThemeEligible, whiteLabelClient);
    let isTxmNeedAttention = false;

    if (utils.dashboardVisible(productFeatures, marketingDetails)) {
      result.push({
        value: '/dashboard',
        name: 'LEFT_NAV_DASHBOARD',
        option: {...option, ...{label: 'Dashboard'}},
        iconName: 'dashboard',
        supportComponent: '',
      });
    }

    /* Alerts */

    const showWarningIconUnified = _get(dynamicData, 'showWarningIconUnified', false);
    const alertListInbox = _get(dynamicData, 'alertListInbox', []);
    const unreadAlerts =
      !_isEmpty(alertListInbox) && alertListInbox?.length > 0
        ? alertListInbox?.filter((alert: {isRead: boolean}) => {
            return !alert.isRead;
          })
        : [];
    const alertsSupportText = unreadAlerts.length === 0 ? '' : unreadAlerts.length;
    const alertIconName = unreadAlerts.length === 0 ? 'alerts' : 'alerts-notification';
    result.push({
      value: '/alerts',
      name: 'LEFT_NAV_ALERTS',
      option: {
        ...option,
        ...{label: 'Alerts'},
      },
      iconName: alertIconName,
      supportComponent: alertsSupportText,
    });

    const showTransactionUpgrade = utils.isTxmUpgradeScenario(productFeatures, whiteLabelClient);
    const isUnifiedFeature = utils.showUnifiedComponents({productFeatures});

    // Add credit if visible and check that user isn't in a credit upgrade scenario before rendering
    if (utils.isCreditUser(productFeatures)) {
      result.push({
        value: '/credit',
        name: 'LEFT_NAV_CREDIT',
        option: {...option, ...{label: 'Credit'}},
        iconName: 'credit',
        supportComponent: '',
      });
    } else if (isMLCardOffersActive && utils.isCreditMonitorScenario(productFeatures)) {
      // Users without credit may have access to the credit monitor page
      result.push({
        value: '/credit-monitor',
        name: 'LEFT_NAV_CREDIT_MONITOR',
        option: {...option, ...{label: 'Credit Monitor'}},
        iconName: 'credit_monitor_standalone',
        supportComponent: '',
      });
    }
    if (utils.isCreditAu(productFeatures)) {
      result.push({
        value: '/credit-au',
        name: 'LEFT_NAV_CREDIT_AU',
        option: {...option, ...{label: 'Credit AU'}},
        iconName: 'credit',
        supportComponent: '',
      });
    }

    /* Transactions */

    if (
      !utils.isPendingPIICollectionState(productFeatures) &&
      (utils.creditLocksAvailable(productFeatures) ||
        utils.phoneLocksAvailable(productFeatures) ||
        utils.isFeaturePresent(productFeatures, 'creditFreeze'))
    ) {
      result.push({
        value: '/locks',
        name: showFreeze ? 'FREEZES' : 'LEFT_NAV_IDENTITY_LOCK',
        clickTracking: true,
        option: {...option, ...{label: 'Identity Lock'}},
        iconName: 'identity-lock',
        supportComponent: '',
      });
    }

    if (
      (utils.txmVisible(productFeatures) && !showTransactionUpgrade) ||
      utils.isFeaturePresent(productFeatures, 'fwMultiAggregator')
    ) {
      const institutionsList = _get(dynamicData, 'institutionsList', []);
      const {connectedInstitutionsCount} = utils.showUnifiedComponents({productFeatures})
        ? getConnectedInstitutionsCountUnified(institutionsList)
        : utils.getConnectedInstitutionsCount(institutionsList);

      const triangleWarningIcon =
        (!_isEmpty(institutionsList) && connectedInstitutionsCount < institutionsList?.length) ||
        (isUnifiedFeature && showWarningIconUnified) ? (
          <Image url={triangleWarningUrl} alt="Error" classes="w-5" dataTestId={'error-image-icon'} />
        ) : (
          ''
        );
      isTxmNeedAttention = triangleWarningIcon !== '';
      result.push({
        value: '/transactions',
        name: utils.getTxmNameKey(productFeatures),
        option: {
          ...option,
          label: 'Financial Monitoring',
        },
        iconName: 'transactions',
        supportComponent: triangleWarningIcon,
      });
    }

    if (utils.hasPrivacyAdvisor(productFeatures)) {
      result.push({
        value: '/privacyadvisor',
        name: 'LEFT_NAV_PRIVACY_ADVISOR',
        option: {...option, ...{label: 'Privacy Advisor'}},
        iconName: 'privacy-advisor',
        supportComponent: '',
      });
    }

    if (utils.hasHomeTitleMonitoring(productFeatures)) {
      const homeTitleMenu = {
        value: '/hometitle',
        name: 'LEFT_NAV_HOMETITLE',
        option: {...option, ...{label: 'Home Title'}},
        iconName: 'home-title',
        supportComponent: '',
      };
      utils.isHomeTitleStandalone(marketingDetails) ? result.unshift(homeTitleMenu) : result.push(homeTitleMenu);
    }

    /* Privacy Monitor */

    if (utils.hasPrivacyMonitorAccess(productFeatures, privacyLicense)) {
      const licenseType = utils.safeGet(privacyLicense, 'licenseType', '');

      const isUserPMA = licenseType === 'PMA';
      const isUserPMALite = licenseType === 'PMALITE';

      const privacyMonitorGaLabel = isUserPMA
        ? 'PrivacyMonitorAssistant'
        : isUserPMALite
        ? 'DataBrokerRemoval'
        : 'PrivacyMonitor';
      result.push({
        value: '/privacymonitor',
        name: isUserPMA ? 'LPM_PMA' : isUserPMALite ? 'LPM_PMA_LITE' : 'LPM',
        option: {...option, category: 'PrivacyMonitor_SidePanel', label: privacyMonitorGaLabel},
        iconName: isUserPMA || isUserPMALite ? 'privacy-monitor-assistant' : 'privacy-monitor',
        supportComponent: '',
      });
    }

    /* Scam Verification */

    const scamVerificationData: ReturnedCase[] =
      _get(dynamicData, 'scamVerificationCases.responseData.parentCaseDetails', []) || [];
    const filteredScamVerificationCase: ReturnedCase[] = scamVerificationData.filter(
      (value: ReturnedCase) => value.parentCaseEndDateTime === null
    );
    const scamVerificationCaseCount: string =
      filteredScamVerificationCase.length === 0 ? '' : filteredScamVerificationCase.length.toString();

    if (utils.hasScamVerificationFeature(productFeatures)) {
      result.push({
        value: '/scam-verification',
        name: 'LEFT_NAV_SCAM_PROTECTION',
        option: {...option, ...{label: 'Scam Verification'}},
        iconName: 'scam-protection',
        supportComponent: scamVerificationCaseCount,
      });
    }

    if (utils.hasScamProtectionFeature(productFeatures)) {
      result.push({
        value: '/scam-protection',
        name: 'LEFT_NAV_SCAM_PROTECTION_NEW',
        option: {...option, ...{category: 'ScamProtection_SidePanel', label: 'Scam Protection'}},
        iconName: 'scam-protection-new',
        supportComponent: '',
      });
    }

    /* Restoration */

    const restorationData: ReturnedCase[] =
      _get(dynamicData, 'restorationCases.responseData.parentCaseDetails', []) || [];

    const filteredRestorationCases: ReturnedCase[] = restorationData.filter(
      (value: ReturnedCase) => value.parentCaseEndDateTime === null
    );
    const restorationFilterResults =
      filteredRestorationCases.length === 0 ? '' : filteredRestorationCases.length.toString();

    const restorationIconName =
      filteredRestorationCases.length === 0 ? 'id-restoration' : 'id-restoration-notification';

    if (utils.isRestorationAvailable(productFeatures)) {
      result.push({
        value: '/restoration',
        name: 'LEFT_NAV_ID_RESTORATION',
        option: {...option, ...{label: 'Restoration'}},
        iconName: restorationIconName,
        supportComponent: restorationFilterResults,
      });
    }

    if (utils.isEstatePlanningVisible(productFeatures)) {
      result.push({
        value: '/estate-planning',
        name: 'LEFT_NAV_ESTATE_PLANNING',
        option: {...option, ...{label: 'Estate Planning'}},
        iconName: 'estate-planning',
        supportComponent: '',
        // Kept off `option` so it never flows into the `trackEvent` analytics payload.
        inid: 'menu_web_estate',
      });
    }

    if (utils.monitoredInfoVisible(productFeatures)) {
      const smmAccounts = _get(dynamicData, 'smmAccounts', {});
      const triangleWarningIcon =
        isTxmNeedAttention ||
        this.isAnySmmAccountDisconnected(smmAccounts) ||
        (isUnifiedFeature && showWarningIconUnified) ? (
          <Image url={triangleWarningUrl} alt="Error" classes="w-5" dataTestId={'error-image-icon'} />
        ) : (
          ''
        );
      result.push({
        value: '/monitoring',
        name: 'LEFT_NAV_MONITORED_INFO',
        option: {
          ...option,
          ...{label: 'Monitored Info'},
        },
        iconName: 'monitored-info',
        supportComponent: triangleWarningIcon,
      });
    }

    /* Marketplace - if adobe tests for card offers and marketplace feature page are enabled and the user has credit monitoring or credit score */
    if (
      isMLCardOffersActive &&
      isOffersMarketplaceActive &&
      (utils.isCreditMonitorScenario(productFeatures) || utils.creditScoreVisible(productFeatures))
    ) {
      result.push({
        value: '/marketplace',
        name: 'LEFT_NAV_MARKETPLACE',
        option: {...option, ...{label: 'Marketplace'}},
        iconName: 'marketplace',
        supportComponent: '',
      });
    }

    /* Discover - Contentful CMS Feed */
    if (utils.hasMFeed(whiteLabelClient) && showContentful === true) {
      result.push({
        value: '/discover',
        name: 'LEFT_NAV_DISCOVER',
        option: {...option, ...{label: 'Protection Pulse'}},
        iconName: 'discover/explore', // Icon located in discover folder on CDN
        supportComponent: '',
        showIconBadge: true,
      });
    }

    if (utils.planDetailsVisible(productFeatures)) {
      result.push({
        value: '/plandetails',
        name: 'LEFT_NAV_PLAN_DETAILS',
        option: {...option, ...{label: 'Plan Details'}},
        iconName: 'plan-details',
        supportComponent: '',
      });
    }

    return result;
  }

  /**
   * Returns true if user has credit score, txm, or credit monitoring
   * @param {ProductFeatures} productFeatures
   * @param marketingDetails
   * @return {boolean}
   */
  static dashboardVisible(productFeatures: ProductFeatures, marketingDetails = {}) {
    // Show dashboard tab only if plan has credit feature and/or txm feature or has credit monitoring
    return (
      utils.isIdAdvisorStandalone(marketingDetails) ||
      utils.creditScoreVisible(productFeatures) ||
      utils.txmVisible(productFeatures) ||
      utils.hasCreditMonitoring(productFeatures) ||
      utils.isRestorationIntl(productFeatures) ||
      utils.isReimbursement(productFeatures) ||
      utils.hasSocialMediaMonitoring(productFeatures) ||
      (utils.hasHomeTitleMonitoring(productFeatures) &&
        utils.isRestorationAvailable(productFeatures) &&
        utils.monitoredInfoVisible(productFeatures))
    );
  }

  /**
   * Returns true if user has credit monitoring
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasCreditMonitoring(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'creditMonitoring_1B');
  }

  /**
   * Checks if a feature is both AVAILABLE and ENROLLED
   * @param {ProductFeatures} productFeatures
   * @param {string} feature - the feature name to check
   * @return {boolean}
   */
  static isFeatureAvailableAndEnrolled(productFeatures: ProductFeatures, feature: string) {
    return (
      utils.isFeatureAvailable(productFeatures, feature) &&
      _get(productFeatures, `${feature}.fulfillmentStatus`, '') === 'ENROLLED'
    );
  }

  /**
   * Returns true if user has 1B or 3B credit monitoring available and enrolled
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static has1bOr3bCreditMonitoring(productFeatures: ProductFeatures) {
    return (
      utils.isFeatureAvailableAndEnrolled(productFeatures, 'creditMonitoring_1B') ||
      utils.isFeatureAvailableAndEnrolled(productFeatures, 'creditMonitoring_3B')
    );
  }

  /**
   * Returns true if user has home title monitoring
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasHomeTitleMonitoring(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'homeTitle');
  }

  /**
   * Returns true if user has social media monitoring
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasSocialMediaMonitoring(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'socialMediaMonitoring');
  }

  /**
   * Returns true if user has privacy monitoring
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasPrivacyMonitoring(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'privacyMonitor');
  }

  /**
   * Returns true if the given license is validated for Privacy Monitor
   * @param {Object} privacyLicense
   * @return {boolean}
   */
  static hasValidPrivacyMonitorLicense(privacyLicense: $TSFixMe) {
    return Boolean(
      utils.safeGet(privacyLicense, 'validateLicense', false) && utils.safeGet(privacyLicense, 'validLicense', false)
    );
  }

  /**
   * Returns true when Privacy Monitor UI should be reachable: plan feature flag and/or a validated PMA license.
   * Some entitled members lack `productFeatures.privacyMonitor` even with a valid license response.
   */
  static hasPrivacyMonitorAccess(productFeatures: ProductFeatures, privacyLicense: $TSFixMe) {
    return utils.hasPrivacyMonitoring(productFeatures) || utils.hasValidPrivacyMonitorLicense(privacyLicense);
  }

  /**
   * Returns true if user has privacy advisor
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasPrivacyAdvisor(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'privacyAdvisor');
  }

  /**
   * Returns true if user has standalone home title monitoring
   * @param {Object} marketingDetails
   * @returns {boolean}
   */
  static isHomeTitleStandalone(marketingDetails: MarketingDetails) {
    return _get(marketingDetails, 'homeTitle.isStandalone', false) === true;
  }

  /**
   * Returns true if user has standalone id advisor
   * @param {Object} marketingDetails
   * @return {boolean}
   */
  static isIdAdvisorStandalone(marketingDetails: MarketingDetails | {}) {
    return _get(marketingDetails, 'idAdvisor.isStandalone', false) === true;
  }

  /**
   * Returns boolean val depending on service codes of home title and restoration to display home title widget 1/2 vs full on dashboard
   * When plan is premium like ultimatePlus or CSP_6 half width is displayed, when plan is DWM with homeTitle full width is displayed
   * @param {Object} productFeatures
   * @return {boolean}
   * */
  static isHomeTitleWidgetFullWidth(productFeatures: ProductFeatures) {
    const isHomeTitleAvailable = utils.hasHomeTitleMonitoring(productFeatures);
    const isRestorationHomeTitle =
      _get(productFeatures, 'restorationSummary.serviceCode', '') === 'LLS_RESTORATION_HOMETITLE';
    return isHomeTitleAvailable && isRestorationHomeTitle;
  }

  /**
   * react-modal setup + notify DWM parent that a modal is open. Intended to run once per modal shell
   * (see ModalWrapper). Do not invoke from modal content that already mounts ModalWrapper — duplicate
   * calls emit multiple `MODAL_OPENED: true` while `closeModal` sends a single `false`.
   */
  // @ts-expect-error TS(7006) FIXME: Parameter 'Modal' implicitly has an 'any' type.
  static initializeModal(Modal) {
    Modal.setAppElement('body');
    window.parent.postMessage(
      {
        type: 'GET_PARENT_WINDOW_SPEC',
        payload: {actionToTake: 'updateModalSpec'},
      },
      window.REACT_APP_PARENT_HOST
    );
    window.parent.postMessage({type: 'MODAL_OPENED', payload: true}, window.REACT_APP_PARENT_HOST);
  }

  /**
   * Tell the DWM / iframe parent host that no modal is open so it can restore page scroll (`overflow`).
   * Posts `MODAL_OPENED: false` twice (sync + rAF): React 18 StrictMode re-runs mount effects in dev, so
   * `initializeModal` can emit two `true`s from one logical modal while a single `false` leaves a
   * refcount-style parent wedged. A second `false` is a no-op for boolean parents.
   * Then `resizeIframe` (debounced): nudges the shell with updated `RESIZED` payload after DOM settles,
   * which some DWM layouts need before the outer page scrolls again.
   */
  static signalParentModalClosed(): void {
    const host = window.REACT_APP_PARENT_HOST;
    const closedMsg = {type: 'MODAL_OPENED', payload: false} as const;
    window.parent.postMessage(closedMsg, host);
    const postClosed = (): void => {
      window.parent.postMessage(closedMsg, host);
      utils.resizeIframe();
    };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(postClosed);
    } else {
      window.setTimeout(postClosed, 0);
    }
  }

  /**
   * Dismiss modal Redux state and tell the DWM parent scrolling may resume.
   * @see {@link utils.signalParentModalClosed}
   */
  // @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
  static closeModal(props) {
    props.dispatch({type: 'HIDE_MODAL'});
    props.dispatch(setModalSizeSpec({content: {}}));
    utils.signalParentModalClosed();
  }

  /**
   * Opens the add-family phase-2 eStore checkout in the top browsing context (`_top`) so the full
   * tab navigates even when DSP is embedded in an iframe. Shared by {@link AddFamilyInterstitial}
   * and {@link FamilyExposureModal} — both build the same `baseUrl + inid + PSN` link.
   *
   * Optional `originId` follows {@link utils.getUpgradeUrl}: when set, it is appended to the INID
   * as `_${originId}` (e.g. `'scan'` → `…_pdf_relatives_scan`). Omit it for the generic add-family
   * interstitial so that cart INID stays unchanged.
   */
  static openAddFamilyEstoreCheckout(baseUrl: string, inidStr: string, psn: string, originId = ''): Window | null {
    const inidWithOrigin = _isEmpty(originId) ? inidStr : `${inidStr}_${originId}`;
    const finalCtaLink = `${baseUrl}${inidWithOrigin}&PSN=${psn}`;
    return window.open(finalCtaLink, '_top', 'noopener,noreferrer');
  }

  // Resets the iframe height to the height of its content and resets modal flow
  static resetIframeHeight() {
    window.parent.postMessage({type: 'SET_IFRAME_HEIGHT', payload: false}, window.REACT_APP_PARENT_HOST);
    window.parent.postMessage({type: 'MODAL_OPENED', payload: false}, window.REACT_APP_PARENT_HOST);
    window.parent.postMessage(
      {type: 'GET_PARENT_WINDOW_SPEC', payload: {actionToTake: 'updateModalSpec'}},
      window.REACT_APP_PARENT_HOST
    );
  }

  /**
   * Sets the iframe height to available viewport height
   * (viewport height minus Norton header and footer heights).
   * This is used for external modals/iframes (e.g., Plaid, Genie) to ensure proper display.
   */
  static setIframeHeightToAvailableViewport() {
    window.parent.postMessage({type: 'SET_IFRAME_HEIGHT', payload: true}, window.REACT_APP_PARENT_HOST);
    window.parent.postMessage({type: 'MODAL_OPENED', payload: true}, window.REACT_APP_PARENT_HOST);
    utils.scrollTo(0);
    utils.resizeIframe();
  }

  static polyfillsForIe() {
    //IE11 polyfill for startsWith() method//
    if (!String.prototype.startsWith) {
      // eslint-disable-next-line
      String.prototype.startsWith = function (search, pos) {
        return this.substr(!pos || pos < 0 ? 0 : +pos, search.length) === search;
      };
    }

    //IE11 polyfill for find() method//
    if (!Array.prototype.find) {
      // @ts-expect-error TS(7006) FIXME: Parameter 'func' implicitly has an 'any' type.
      Array.prototype.find = function(func) {
        let selectedItem = null;
        if (func && typeof func === 'function') {
          for (let i = 0; i < this.length; i++) {
            if (func(this[i])) {
              selectedItem = this[i];
              break;
            }
          }
        }
        return selectedItem;
      };
    }

    //IE11 polyfill for parseInt//
    if (Number.parseInt === undefined) Number.parseInt = window.parseInt;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'modalType' implicitly has an 'any' type... Remove this comment to see the full error message
  static checkModalState(modalType) {
    return modalType !== null;
  }

  static isIeOrEdge() {
    const userAgent = window.navigator.userAgent;
    if (userAgent.indexOf('MSIE') > 0 || userAgent.indexOf('Trident') > 0 || userAgent.indexOf('Edge') > 0) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Returns the platform type based on the user agent: 'iOS', 'Android', or 'Web' (default).
   * @returns {'iOS' | 'Android' | 'Web'}
   */
  static getPlatformType(): 'iOS' | 'Android' | 'Web' {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return 'iOS';
    }
    if (/Android/i.test(ua)) {
      return 'Android';
    }
    return 'Web';
  }

  /**
   * Returns a boolean of whether the user agent is from a mobile device
   * Considers tablets mobile as well
   * @returns {boolean}
   */
  static isMobileBrowser() {
    const ua = window.navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      // tablet (considered mobile)
      return true;
    } else if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
    ) {
      // mobile
      return true;
    }
    return false;
  }

  /**
   * Returns a boolean of whether the user agent is from a standard mobile device
   * @returns {boolean}
   */
  static isMobileDevice() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  /**
   * Returns a boolean of whether the user agent is from a standard mobile device that isn't an iPad
   * @returns {boolean}
   */
  static isMobileNonIpadDevice() {
    return /iPhone|iPod|Android/i.test(navigator.userAgent);
  }

  /**
   * Returns a boolean of whether the user agent is an android device
   * @returns {boolean}
   */
  static isAndroid() {
    return /Android/i.test(navigator.userAgent);
  }

  /**
   * Sends message to window parent.  If this window is an iframe, the message is sent to the parent of the iframe.
   * @param {Message} payload - The message to send.
   * @param {string} host - The host to send the message to.
   */
  static sendMessage(payload: Message, host: string) {
    if (typeof payload !== 'object') {
      throw new Error('expected payload to be a JSON object');
    }
    try {
      window.parent.postMessage(payload, host);
    } catch (error) {
      log.error(error);
    }
  }

  /**
   * Processes download report data and opens the credit report.
   * Extracts URL and Authorization token from download response, validates them,
   * constructs the full URL with access_token query parameter, and sends message to open the report.
   *
   * @param {KMMDownloadResponse} downloadReportData - The download response containing URL and headers
   * @throws {Error} If URL or Authorization token is missing
   */
  static processAndOpenCreditReport(downloadReportData: KMMDownloadResponse | null | undefined): void {
    if (!downloadReportData) {
      throw new Error('Download report data is missing');
    }

    const url = utils.safeGet(downloadReportData, 'downloadURL', '');
    let token = '';

    if (downloadReportData.headers) {
      // Try direct property access first (for converted JS object)
      const authHeader = downloadReportData.headers.Authorization || downloadReportData.headers.authorization;
      if (authHeader) {
        token = authHeader.startsWith('Bearer ') ? authHeader.substring('Bearer '.length) : authHeader;
      }
    }

    if (token === '' || url === '') {
      throw new Error('Url or Authorization token is missing in the download response');
    }

    const reportFullUrl = url + '?access_token=' + token;
    utils.sendMessage({type: 'OPEN_CREDIT_REPORT', payload: {reportFullUrl}}, window.REACT_APP_PARENT_HOST);
  }

  // To find account which are in Priority Polling--Active--Unauthorized/needs_reconnect status
  // @ts-expect-error TS(7006) FIXME: Parameter 'connectedAccounts' implicitly has an 'a... Remove this comment to see the full error message
  static getConnectedAccountsPollingStatus(connectedAccounts) {
    if (!connectedAccounts) {
      return {};
    }
    // @ts-expect-error TS(7006) FIXME: Parameter 'accounts' implicitly has an 'any' type.
    return connectedAccounts.reduce((accounts, account) => {
      if (!accounts[account.provider] || account.status === 'polling') {
        accounts[account.provider] = account.status;
      }
      if (accounts[account.provider] !== 'polling' && account.status === 'active') {
        accounts[account.provider] = account.status;
      }
      return accounts;
    }, {});
  }

  /**
   * Ensures that the input value is an array, or else it returns a new array containing the value
   *
   * Note: This is required when parsing some data-api responses, where a field can be either an array
   * of objects, or sometimes comes as a single object.
   *
   * @param {*} value - either an array (to be returned as is) or any non-array variable to be placed inside an array
   * @returns {[]} - the input array, or a new array that contains any input that is non-array and non-null
   */
  static ensureArray(value: $TSFixMe) {
    if (Array.isArray(value)) {
      return value;
    } else if (value === undefined || value === null) {
      return [];
    } else {
      return [value];
    }
  }

  /**
   * Iterates through the institutions in specified list, counting connected and disabled accounts.
   *
   * @param {object[]} institutionsList - institutions with transactions-monitoring accounts data
   * @returns {{connectedAccountsCount: number, disabledAccountsCount: number}}
   */
  static getConnectedAccountsCount(institutionsList: Institution[]) {
    let connectedAccountsCount = 0,
      disabledAccountsCount = 0;

    // bumpCounts is an internal function to increment our counts (connected/disabled) for a single accounts list
    // it gets called below, six times for each institution (once for each of the six types of accounts)
    // @ts-expect-error TS(7006) FIXME: Parameter 'accounts' implicitly has an 'any' type.
    const bumpCounts = (accounts, useStateTest = false) => {
      if (!_isEmpty(accounts)) {
        // make sure we have an array of accounts (sometimes it's a single account object, sometimes an array of them)
        const accountsArray = utils.ensureArray(accounts);
        if (!useStateTest) {
          // count all accounts in list (with no check for disabled ones) and then exit bumpCounts function
          connectedAccountsCount += accountsArray.length;
          return;
        }
        accountsArray.forEach((account) => {
          if (typeof account === 'object' && typeof account.state === 'string') {
            if (account.state === 'DISABLED') disabledAccountsCount++;
            else connectedAccountsCount++;
          }
        });
      }
    };

    // inspect each institution, bumping our counters for each of the six different types of accounts
    if (!_isEmpty(institutionsList) && !(institutionsList as $TSFixMe).noResults) {
      if (Array.isArray(institutionsList)) {
        institutionsList.forEach((institution) => {
          bumpCounts((institution as $TSFixMe).bankingAccounts);
          bumpCounts((institution as $TSFixMe).cardAccounts);
          bumpCounts((institution as $TSFixMe).insuranceAccounts);
          bumpCounts((institution as $TSFixMe).rewardsAccounts);
          bumpCounts((institution as $TSFixMe).investmentAccounts, true);
          bumpCounts((institution as $TSFixMe).loanAccounts, true);
        });
      }
    }
    return {connectedAccountsCount, disabledAccountsCount};
  }

  /**
   * Iterates through the institutions in specified list, counting connected and disabled accounts.
   *
   * @param {object[]} institutionsList - institutions with transactions-monitoring accounts data
   * @returns {{connectedInstitutionsCount: number}}
   */
  static getConnectedInstitutionsCount(institutionsList: Institution[]): {connectedInstitutionsCount: number} {
    const connectedInstitutionsCount =
      _isArray(institutionsList) && !_isEmpty(institutionsList)
        ? institutionsList?.reduce(
            (acc: number, obj: {institutionState: string | string[]; autoRefreshState: string | string[]}) => {
              return (
                acc +
                ((obj.institutionState === 'ACTIVE' &&
                  (obj.autoRefreshState === 'ENABLED_SCHEDULED' ||
                    obj.autoRefreshState === 'UNKNOWN_AUTO_REFRESH_STATE')) ||
                obj.institutionState === 'MT_SUCCESS'
                  ? 1
                  : 0)
              );
            },
            0
          )
        : 0;

    return {connectedInstitutionsCount};
  }

  /**
   * Returns a state of monitored accounts in the format of a fraction if 1 or more monitered accounts are not in connected state; otherwise a number string (eg. 2/5, 99+)
   *
   * @param {Institution[] | Institution | string} institutionsList - institutions array, object, or a string containing financial-monitoring accounts data
   * @param {number} connectedInstitutionsCount - count of connected institutions
   * @param {number} maxCountToShow - max count to show (ie. 99)
   * @returns {string} state of monitored accounts in the format of a fraction if 1 or more monitered accounts are not in connected state; otherwise a number string
   */
  static getMonitoredAccountState(
    institutionsList: Institution | Institution[] | string,
    connectedInstitutionsCount: number,
    maxCountToShow: number
  ) {
    if (
      typeof institutionsList === 'string' ||
      _isEmpty(institutionsList) ||
      !_isEmpty(_get(institutionsList, 'noResults', ''))
    ) {
      return '0';
    }

    if (Array.isArray(institutionsList)) {
      const connectedInstitutionsCountString =
        connectedInstitutionsCount > 99 ? `${maxCountToShow}+` : connectedInstitutionsCount.toString();
      const institutionsListString =
        institutionsList.length > 99 ? `${maxCountToShow}+` : institutionsList.length.toString();

      if (connectedInstitutionsCount === institutionsList.length) {
        return connectedInstitutionsCountString;
      }
      return `${connectedInstitutionsCountString}/${institutionsListString}`;
    }

    if (typeof institutionsList === 'object') {
      if (connectedInstitutionsCount === 1) {
        return '1';
      }
      return '0/1';
    }
    return '0';
  }

  /**
   * Iterates through the institutions in specified list, returning only those that are not connected
   *
   * @param {object[]} institutionsList - institutions with transactions-monitoring accounts data
   * @returns {object[]} disconnectedAccounts - Array of disconnected accounts
   */
  static getNonConnectedInstitutions(institutionsList: Institution[]): Institution[] {
    return !_isEmpty(institutionsList) && !_get(institutionsList, 'noResults')
      ? institutionsList.filter((institution) => {
          const {institutionState, autoRefreshState} = institution;
          const isYodleeActive = institutionState === 'ACTIVE';
          const isYodleeAutoRefreshEnabled =
            autoRefreshState === 'ENABLED_SCHEDULED' || autoRefreshState === 'UNKNOWN_AUTO_REFRESH_STATE';
          const isMtSuccess = institutionState === 'MT_SUCCESS';
          return !((isYodleeActive && isYodleeAutoRefreshEnabled) || isMtSuccess);
        })
      : [];
  }
  // @ts-expect-error TS(7006) FIXME: Parameter 'text' implicitly has an 'any' type.
  static getMaskedThreatFromEnd(text) {
    if (_isEmpty(text)) return text;
    // It will catch all the last X&XX-XXX-XXX@X.XXX-X.XX scenario in the threats.
    return text.replace(new RegExp(/[^a-vyz0-9{, ?:}]{2}[ X.{},_@&?!^:\[\]\-\(\)]*$/gim), '');
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'text' implicitly has an 'any' type.
  static getMaskedThreatFromStart(text) {
    if (_isEmpty(text)) return text;
    // It will catch all the start X&XX-XXX-XXX@X.XXX-X.XX scenario in the threats.
    return text.replace(new RegExp(/^[^a-vyz0-9{, ?:}][ X.{},_@&?!^:\[\]\-\(\)]*/gim), '');
  }

  /**
   * Derive alert counts from alertList prop and sets it to the analytics object
   *
   * @param alertListData
   */
  // @ts-expect-error TS(7006) FIXME: Parameter 'alertListData' implicitly has an 'any' ... Remove this comment to see the full error message
  static setAlertCountToAnalytics(alertListData) {
    const alertCounts = {
      inboxAlertCount: (_get(alertListData, 'alertListInbox') || []).length,
      disputedAlertCount: (_get(alertListData, 'alertListCaseOpened') || []).length,
      archivedAlertCount: (_get(alertListData, 'alertListArchived') || []).length,
    };
    window.analytics = window.analytics || {};
    Object.assign(window.analytics, alertCounts);
  }

  /**
   * Returns the last matching paramName from params found in input textString
   * @param {object[]} params - array of parameter objects
   * @param {string} textString - text to search within for parameters
   * @param {object} localizedStringBundle - provided for replacing texts within the template strings
   * @param {string} whiteLabelClient - string indicating partner client app
   * @return {string} - matching parameter OR empty string if none found
   */
  static getParamNameForTextString(
    params: $TSFixMe[] = [],
    textString: string,
    localizedStringBundle: StringMap,
    whiteLabelClient: string
  ) {
    let paramName = '';
    if (textString) {
      const paramsDictionary = this.getParamsDictionary(params, localizedStringBundle, whiteLabelClient);
      Object.keys(paramsDictionary).forEach((key) => {
        if (textString.includes('${' + key + '}')) {
          paramName = key;
        }
      });
    }
    return paramName;
  }

  /**
   * Replaces ${key0} ${key1} ${key2} ... strings in textArray with templatized params.
   * @param {array} params - template string such as "${support}}"
   * @param {array} textArray - lines where the template strings will be retrieved from
   * @param {object} localizedStringBundle - provided for replacing texts within the template strings
   * @param {string} whiteLabelClient - string indicating partner client app
   * @return {array} - interpolated array of string with placeholders replaced by corresponding values in replacements
   */
  static parseParamsInTextArray(
    params: Array<any> = [],
    textArray: Array<any>,
    localizedStringBundle: StringMap,
    whiteLabelClient: string
  ) {
    /* eslint-disable no-template-curly-in-string */
    const paramsDictionary = this.getParamsDictionary(params, localizedStringBundle, whiteLabelClient);
    return textArray.map((text) => {
      Object.keys(paramsDictionary).forEach((key) => {
        text = text.replace('${' + key + '}', paramsDictionary[key]);
      });
      return text;
    });
    /* eslint-enable no-template-curly-in-string */
  }

  /**
   * Replaces ${key0} ${key1} ${key2} ... string in textString with templatized params.
   * @param {array} params - template string such as "${support}}"
   * @param {string} textString - lines where the template strings will be retrieved from
   * @param {object} localizedStringBundle - provided for replacing texts within the template strings
   * @param {string} whiteLabelClient - string indicating partner client app
   * @return {string} - interpolated string with placeholders replaced by corresponding values in replacements
   */
  static parseParamsInTextString(
    params: Array<any> = [],
    textString: string,
    localizedStringBundle: StringMap,
    whiteLabelClient: string
  ) {
    if (_isEmpty(textString)) return '';
    const paramsDictionary = this.getParamsDictionary(params, localizedStringBundle, whiteLabelClient);
    Object.keys(paramsDictionary).forEach((key) => {
      textString = textString.replace('${' + key + '}', paramsDictionary[key]);
    });
    return textString;
  }

  /**
   * generates an object of param names and the link to be replaced.
   * @param {string} hrefUrl - url string href attr
   * @param {string} displayText - display text for <a> tag
   * @param {string} dataTestId - data-testid string
   * @returns {string} - string JSX for the anchor element
   */

  static getAnchorTag(hrefUrl: string, displayText: string, dataTestId: string) {
    return `<a class="text-accent underline underline-offset-4 hover:no-underline" data-testid=${dataTestId} target='_blank' rel="noopener noreferrer" href='${hrefUrl}'>${displayText}</a>`;
  }

  /**
   * generates an object of param names and the link to be replaced.
   * @param {array} params - template string such as "${support}}"
   * @param {object} localizedStringBundle - string bundle containing localized messages
   * @param {string} whiteLabelClient - string indicating the partner client app
   * @return {object} - an object of param names and the link to be replaced
   */
  static getParamsDictionary(params: Array<any> = [], localizedStringBundle: StringMap, whiteLabelClient: string) {
    const paramsDictionary: StringMap = {};
    params.forEach(function(param) {
      const paramName = _get(param, 'paramName', '');

      switch (param.action) {
        case 'verifyEmail':
          {
            const displayText = _get(param, 'displayText');
            const actionValue = _get(param, 'actionValue');
            paramsDictionary[
              paramName
            ] = `<div class="text-accent cursor-pointer hover:text-accent" data-cta="verifyEmail" data-email=${actionValue}>${displayText}</div>`;
          }
          break;
        case 'emailVerified':
          {
            const displayText = _get(param, 'displayText');
            paramsDictionary[paramName] = `<img alt=${localizedStringBundle.EMAIL_VERIFIED} src=${utils.getCdnPath(
              '/verified.png',
              whiteLabelClient
            )} /> <span class="font-normal ml-2">${displayText}</span>`;
          }
          break;
        case 'support':
          {
            let supportUrl = _get(param, 'articleUrl', window.REACT_APP_ALERT_DETAIL_FOOTER_SUPPORT_INFO);

            // for partners, ignore URL from member-api (NortonLifeLock support article), use partner override instead
            if (whiteLabelClient) {
              supportUrl = window.REACT_APP_ALERT_DETAIL_FOOTER_SUPPORT_INFO;
            }
            const supportDisplayText = _get(
              param,
              'displayText',
              localizedStringBundle.ALERT_FOOTER_DEFAULT_SUPPORT_TEXT
            );
            paramsDictionary[
              paramName
            ] = `<a class="font-bold text-accent support" id="dw-support-link" target='_blank' rel="noopener noreferrer" href='${supportUrl}'>${supportDisplayText}</a>`;
          }
          break;
        case 'review':
          {
            const reviewSiteUrl = _get(param, 'reviewSiteUrl', '');
            const reviewDisplayText = _get(param, 'displayText', '');
            if (reviewSiteUrl) {
              const gaTrackingValues = _get(param, 'gaTrackingValues', {});
              const category = _get(gaTrackingValues, 'category', ''),
                label = _get(gaTrackingValues, 'label', '');
              const option = {
                category: category,
                action: 'View',
                label: label,
              };
              if (category && category !== '' && label && label !== '') {
                utils.trackEvent(option);
              }
              paramsDictionary[
                paramName
              ] = `<a class="font-bold text-accent review" id="review-link" data-gaCategory='${category}' data-gaLabel='${label}' target='_blank' rel="noopener noreferrer" href='${reviewSiteUrl}'>${reviewDisplayText}</a>`;
            } else {
              // should-not-happen, but if we don't receive a link URL, then we just show the simple display-text, not a link
              paramsDictionary[paramName] = reviewDisplayText;
            }
          }
          break;
        case 'upgrade':
          {
            const upgradeDisplayText = _get(
              param,
              'displayText',
              localizedStringBundle.ALERT_FOOTER_DEFAULT_UPGRADE_TEXT
            );
            paramsDictionary[
              paramName
            ] = `<span class="cursor-pointer text-accent"><span class="upgradeButton">${upgradeDisplayText}</span></span>`;
          }
          break;
        case 'call':
          {
            paramsDictionary[paramName] = _get(param, 'displayText', '');
          }
          break;
        case 'sendEmail':
          {
            paramsDictionary[paramName] = _get(param, 'displayText', '');
          }
          break;
        case 'disposition_yes':
          {
            const dispositionYesText = _get(param, 'displayText', 'Yes');
            paramsDictionary[
              paramName
            ] = `<div class="p-4 font-bold text-center rounded-full cursor-pointer w-25 text-surface disposition_yes bg-success md:w-50" data-cta=${param.action}>${dispositionYesText}</div>`;
          }
          break;
        case 'disposition_no':
          {
            const dispositionNoText = _get(param, 'displayText', 'No');
            paramsDictionary[
              paramName
            ] = `<div class="p-4 font-bold text-center rounded-full cursor-pointer w-25 text-surface disposition_no bg-critical red md:w-50" data-cta=${param.action}>${dispositionNoText}</div>`;
          }
          break;
        case 'navigate':
          {
            const navigateText = _get(param, 'displayText', 'No');
            paramsDictionary[
              paramName
            ] = `<div class="p-4 font-bold text-center rounded-full cursor-pointer w-25 text-surface disposition_navigate bg-critical red md:w-50" data-cta=${param.action}>${navigateText}</div>`;
          }
          break;
        case 'show-disposition-yes':
        case 'show-disposition-no':
          {
            paramsDictionary[paramName] = _get(param, 'displayText', '');
          }
          break;
        case 'manage_freezes':
        case 'manage_identity_lock':
          {
            const manageLocksFreezeText = _get(param, 'displayText', '');
            const ctaAction = _get(param, 'ctaAction', '');
            paramsDictionary[
              paramName
            ] = `<div id="manage-locks-freeze" data-cta=${ctaAction} class="p-4 text-sm font-bold text-center uppercase cursor-pointer w-60 text-onAccent-primary locks-freeze bg-accent"> ${manageLocksFreezeText}</div>`;
          }
          break;
        case 'button':
          {
            const buttonText = _get(param, 'displayText', '');
            const ctaAction = _get(param, 'ctaAction', '');
            paramsDictionary[
              paramName
            ] = `<button type="submit" data-cta= ${ctaAction} class="p-2 w-full text-sm font-bold text-center uppercase rounded border-2 border-solid cursor-pointer text-accent border-accent md:w-50">${buttonText}</button>`;
          }
          break;
        case 'manage_credit':
        case 'manage_freezes_link':
        case 'manage_identity_locks_link':
        case 'manage_pii':
        case 'manage_restoration':
        case 'manage_txm':
        case 'view_recurring_txm_details':
        case 'restoration_insurance':
        case 'identity_theft':
        case 'password_manager':
        case 'alert_preferences':
          {
            const appUrl = _get(param, 'articleUrl', window.REACT_APP_ALERT_DETAIL_FOOTER_SUPPORT_INFO);
            const internalTarget = _get(param, 'internalTarget', '') || '';
            const targetId = _get(param, 'targetId', '') || '';
            const targetType = _get(param, 'targetType', '') || '';
            const appNavText = _get(param, 'displayText');
            if (internalTarget) {
              paramsDictionary[
                paramName
              ] = `<a class="font-bold text-accent internal-nav-${internalTarget}" data-target-id=${targetId} data-target-type=${targetType} id="internal-nav-${internalTarget}" href='${internalTarget}'>${appNavText}</a>`;
            } else {
              paramsDictionary[
                paramName
              ] = `<a class="font-bold text-accent support" id="app_nav_${param.action}" target='_blank' rel="noopener noreferrer" href='${appUrl}'>${appNavText}</a>`;
            }
          }
          break;
        case 'legal':
          {
            let legalUrl: string = _get(param, 'articleUrl', window.REACT_APP_LEGAL_URL);
            if (whiteLabelClient) {
              legalUrl = window.REACT_APP_LEGAL_URL;
            }
            const legalDisplayText: string = _get(param, 'displayText', window.REACT_APP_LEGAL_LINK_TEXT);
            paramsDictionary[paramName] = utils.getAnchorTag(legalUrl, legalDisplayText, 'gen-legal-link');
          }
          break;
        case 'smmAlerts':
          {
            let smmUrl: string = _get(param, 'articleUrl', window.REACT_APP_SMM_LINK);
            if (whiteLabelClient) {
              smmUrl = window.REACT_APP_SMM_LINK;
            }
            let smmLinkDisplayText: string = _get(param, 'displayText', window.REACT_APP_SMM_LINK_TEXT);
            if (whiteLabelClient) {
              smmLinkDisplayText = window.REACT_APP_SMM_LINK_TEXT;
            }
            paramsDictionary[paramName] = utils.getAnchorTag(smmUrl, smmLinkDisplayText, 'gen-smm-link');
          }
          break;
        default:
          break;
      }
    });
    return paramsDictionary;
  }

  /**
   * generates original or toggled value with toggle icon
   * @param {ToggleProps} toggle - toggle data for alert
   * @param {string} value - original value
   * @param {Record<string, unknown>} option - ga event
   * @return {JSX.Element} - returns jsx element with original or toggled value including toggle
   */
  static parseAlertValue = (toggle: ToggleProps, value: string, option: Record<string, unknown>) => {
    return _isEmpty(toggle) ? <>{value}</> : <AlertToggle toggleData={toggle} value={value} option={option} />;
  };

  /**
   * @param {array} array containing credit feature statuses
   * @param {array} array containing api call statuses
   * @return {boolean} - returns true/false if credit component is loading
   */
  // @ts-expect-error TS(7006) FIXME: Parameter 'creditFeatureStatuses' implicitly has a... Remove this comment to see the full error message
  static isCreditLoading(creditFeatureStatuses, creditApiStatuses) {
    // @ts-expect-error TS(7006) FIXME: Parameter 'status' implicitly has an 'any' type.
    if (creditFeatureStatuses.some((status) => status === 'LOADING')) return true;
    //if 3b is not available then we remove the last api status since it is not applicable
    if (creditFeatureStatuses[2] !== 'AVAILABLE') {
      creditApiStatuses.pop();
    }
    // @ts-expect-error TS(7006) FIXME: Parameter 'status' implicitly has an 'any' type.
    if (creditFeatureStatuses.some((status) => status === 'AVAILABLE')) {
      // @ts-expect-error TS(7006) FIXME: Parameter 'status' implicitly has an 'any' type.
      return creditApiStatuses.some((status) => status === 'loading');
    }
    return false;
  }

  /**
   * @param {string} queryString containing the query params string from browser url
   * @return {object} - an object of param names and values
   */
  static parseQueryString(queryString: string) {
    let params = {},
      i,
      l;

    const queries = queryString.split('&');
    for (i = 0, l = queries.length; i < l; i++) {
      const temp = queries[i].split('=');
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      params[temp[0]] = temp[1];
    }
    return params;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'bureau' implicitly has an 'any' type.
  static getBureauInfo(bureau) {
    let bureauDetails = {};
    switch (bureau) {
      case TU_BUREAU_CODE:
        bureauDetails = {
          dispute: 'https://www.transunion.com/credit-disputes/dispute-your-credit',
        };
        break;
      case EXP_BUREAU_CODE:
        bureauDetails = {
          dispute: 'https://www.experian.com/help/dispute-credit/',
        };
        break;
      case EFX_BUREAU_CODE:
        bureauDetails = {
          dispute: 'https://www.equifax.com/personal/credit-report-services/credit-dispute/',
        };
        break;
      default:
        break;
    }
    return bureauDetails;
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'text' implicitly has an 'any' type.
  static hasHtml(text) {
    return /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/.test(text);
  }

  static isCreditLocksFeatureAvailable(productFeatures: ProductFeatures) {
    return utils.locksVisible(productFeatures);
  }

  static isCreditLockFullfillmentAvailable(authProps: AuthState) {
    return (
      _get(authProps, 'user.primaryMember.plan.productFeatures.creditLocks.fulfillmentStatus', 'NOT_AVAILABLE') !==
      'NOT_AVAILABLE'
    );
  }

  static isCreditLockPending(authProps: AuthState) {
    return _get(authProps, 'user.primaryMember.plan.productFeatures.creditLocks.fulfillmentStatus', null) === 'PENDING';
  }

  static isCreditLockDeferred(authProps: AuthState) {
    return (
      _get(authProps, 'user.primaryMember.plan.productFeatures.creditLocks.fulfillmentStatus', null) === 'DEFERRED'
    );
  }

  /** Member API / commerce: LifeLock Standard product family. */
  static readonly LIFELOCK_STANDARD_PRODUCT_FAMILY_ID = 38;

  /** LifeLock Standard standalone & family plan product IDs (member `plan.productId`). */
  static readonly LIFELOCK_STANDARD_STANDALONE_AND_FAMILY_PRODUCT_IDS: readonly number[] = [826, 827, 998, 1001];

  /**
   * True when the primary member plan is LifeLock Standard (standalone or family):
   * `plan.productFamilyId` 38 and `plan.productId` ∈ {826, 827, 998, 1001}.
   * Uses Redux `auth` (`user.primaryMember.plan`).
   */
  static isLifeLockStdStandaloneOrFamilyPlan(auth: AuthState): boolean {
    const productFamilyId = _get(auth, 'user.primaryMember.plan.productFamilyId', null);
    const productId = _get(auth, 'user.primaryMember.plan.productId', null);

    if (
      productFamilyId !== utils.LIFELOCK_STANDARD_PRODUCT_FAMILY_ID ||
      !utils.LIFELOCK_STANDARD_STANDALONE_AND_FAMILY_PRODUCT_IDS.includes(productId)
    ) {
      return false;
    }

    return true;
  }

  /**
   * True when the member has a pre-purchase Estate Planning upgrade offer: at least one of
   * `estateWill` / `estateTrust` has `featureStatus` `UPGRADE` and neither is `AVAILABLE`.
   * Mixed UPGRADE + AVAILABLE is treated as post-purchase (see `isEstatePlanningAvailable`).
   */
  static isEligibleForEstatePlanning(productFeatures: ProductFeatures | {}): boolean {
    const resolvedProductFeatures = withEstatePlanningEligibilityMock(productFeatures);
    const estateWillStatus = utils.safeGet(resolvedProductFeatures, 'estateWill.featureStatus', '');
    const estateTrustStatus = utils.safeGet(resolvedProductFeatures, 'estateTrust.featureStatus', '');

    const hasEstatePlanningUpgrade = estateWillStatus === 'UPGRADE' || estateTrustStatus === 'UPGRADE';
    const hasEstatePlanningAvailable = estateWillStatus === 'AVAILABLE' || estateTrustStatus === 'AVAILABLE';

    return hasEstatePlanningUpgrade && !hasEstatePlanningAvailable;
  }

  /**
   * True when Estate Planning should appear in nav or be routable: pre-purchase upgrade offer
   * (`isEligibleForEstatePlanning`) or post-purchase available features (`isEstatePlanningAvailable`).
   */
  static isEstatePlanningVisible(productFeatures: ProductFeatures | {}): boolean {
    const resolvedProductFeatures = withEstatePlanningEligibilityMock(productFeatures);
    if (utils.isEligibleForEstatePlanning(resolvedProductFeatures)) {
      return true;
    }

    return (
      utils.safeGet(resolvedProductFeatures, 'estateWill.featureStatus', '') === 'AVAILABLE' ||
      utils.safeGet(resolvedProductFeatures, 'estateTrust.featureStatus', '') === 'AVAILABLE'
    );
  }

  /**
   * Returns fulfillment statuses for `estateWill` and `estateTrust` when each feature's
   * `featureStatus` is `AVAILABLE`; otherwise `null` for that feature.
   */
  static isEstatePlanningAvailable(productFeatures: ProductFeatures | {}): {
    estateWill: FulfillmentStatus | null;
    estateTrust: FulfillmentStatus | null;
  } {
    const getFulfillmentIfAvailable = (featureKey: 'estateWill' | 'estateTrust'): FulfillmentStatus | null => {
      if (utils.safeGet(productFeatures, `${featureKey}.featureStatus`, '') !== 'AVAILABLE') {
        return null;
      }
      return utils.safeGet(productFeatures, `${featureKey}.fulfillmentStatus`, null);
    };

    return {
      estateWill: getFulfillmentIfAvailable('estateWill'),
      estateTrust: getFulfillmentIfAvailable('estateTrust'),
    };
  }

  /**
   * True when the Reimagine (Norton ODS) theme should be shown for the current member.
   * Driven entirely by the backend `showReimaginedExperience` flag on the primary member of the member
   * response; the legacy Tailwind theme is retained for everyone else (Norton/CSP, partner/white-label,
   * International). Uses Redux `auth`.
   */
  static isReimagineThemeEligible(auth: AuthState): boolean {
    return utils.safeGet(auth, 'user.primaryMember.showReimaginedExperienceWeb', false) === true;
  }

  static isUsMember(auth: AuthState): boolean {
    return utils.safeGet(auth, 'user.countryCode', '') === 'US';
  }

  /**
   * ITPS-15562 dashboard greeting + icon-less nav updates apply to US members only.
   * Reimagine is US-only, so this single gate covers both CSP and reimagine members.
   */
  static isUsDashboardDesignUpdateEligible(auth: AuthState, whiteLabelClient: string): boolean {
    return !whiteLabelClient && utils.isUsMember(auth);
  }

  /** Static "Dashboard" title for whitelabel members and non-US members. */
  static showStaticDashboardTitle(auth: AuthState, whiteLabelClient: string): boolean {
    if (whiteLabelClient) {
      return true;
    }
    return !utils.isUsMember(auth);
  }

  /**
   * CDN basename for the AI-assistant ("Ask LifeLock" / Genie) icon. Reimagine-eligible members
   * get the refreshed reimagine icon; everyone else keeps the legacy icon.
   *
   * @param isReimagineEligible - result of `utils.isReimagineThemeEligible(auth)`.
   * @returns CDN basename to pass to `utils.getCdnImagesPath`.
   */
  static getAiAssistantIconPath(isReimagineEligible: boolean): string {
    return isReimagineEligible ? `/reimagine${REIMAGINE_IMAGES.aiAssistant}` : '/dsp-northstar/ai-assistant';
  }

  /**
   * CDN URL for the attention-triangle / warning icon. Reimagine-eligible members get
   * `ic_simple_status_attention_triangle`; everyone else keeps the legacy TriangleWarning art.
   *
   * @param isReimagineThemeEligible - result of `utils.isReimagineThemeEligible(auth)`.
   * @param whiteLabelClient - Identifies the white label client; used to select partner-specific image.
   * @returns Fully resolved CDN URL for the warning icon.
   */
  static getAttentionTriangleIconUrl(isReimagineThemeEligible: boolean, whiteLabelClient: string | null): string {
    return utils.getThemedCdnImagesPath(
      isReimagineThemeEligible,
      whiteLabelClient,
      REIMAGINE_IMAGES.icSimpleStatusAttentionTriangle,
      '/dsp-northstar/TriangleWarning'
    );
  }

  /**
   * String-bundle key for the reimagine dashboard greeting, chosen by the member's local
   * time of day. Uses `Date#getHours` so it respects the device time zone.
   * Boundaries: morning < 12:00, afternoon 12:00–16:59, evening >= 17:00.
   *
   * @param hour - hour of day (0–23); defaults to the current local hour.
   * @returns one of `DASHBOARD_GREETING_MORNING` | `DASHBOARD_GREETING_AFTERNOON` | `DASHBOARD_GREETING_EVENING`.
   */
  static getDashboardGreetingKey(hour: number = new Date().getHours()): string {
    if (hour < 12) {
      return 'DASHBOARD_GREETING_MORNING';
    }
    if (hour < 17) {
      return 'DASHBOARD_GREETING_AFTERNOON';
    }
    return 'DASHBOARD_GREETING_EVENING';
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'jsonString' implicitly has an 'any' typ... Remove this comment to see the full error message
  static tryParseJson(jsonString) {
    try {
      const o = JSON.parse(jsonString);

      // Handle non-exception-throwing cases:
      // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
      // but... JSON.parse(null) returns null, and typeof null === "object",
      // so we must check for that, too. Thankfully, null is falsey, so this suffices:
      if (o && typeof o === 'object') {
        return o;
      }
    } catch (e) {
      // eslint-disable-next-line no-empty
    }

    return false;
  }

  /**
   * Pushes page_viewed or modal_impression to genDL for Amplitude (parallel with Adobe trackPageView).
   * @param pageName - raw pageName from setPageNameForAnalytics; modal impressions when it contains "MODAL"
   */
  static pushAmplitudePageView(pageName: string) {
    if (window.REACT_APP_ENABLE_AMPLITUDE_PAGE_VIEWS === false) {
      return;
    }

    const nortonAnalytics = window.nortonAnalytics || {};
    const pageNameExt = nortonAnalytics.page_name;

    const productName: string[] = [];
    if (nortonAnalytics.lifelock_product_name) {
      productName.push(nortonAnalytics.lifelock_product_name);
    }

    const basePayload = {
      environment: window.REACT_APP_LABEL,
      site_domain: window.REACT_APP_BRAND,
      site_property: window.REACT_APP_BRAND,
      site_section: 'identity portal',
      page_name_ext: pageNameExt,
      page_country: nortonAnalytics.site_country,
      page_language: getBrowserLanguage(),
      product_name: productName,
      logged_in: true,
      internal_id: nortonAnalytics.inid ? nortonAnalytics.inid : undefined,
      gen_id: nortonAnalytics.gen_id ? nortonAnalytics.gen_id : undefined,
      account_id: nortonAnalytics.account_id,
    };

    window.genDL = window.genDL || [];
    if (pageName.includes('MODAL')) {
      window.genDL.push({
        event: 'modal_impression',
        ...basePayload,
        modal_name: pageNameExt,
        modal_type: 'overlay',
      });
    } else {
      window.genDL.push({
        event: 'page_viewed',
        ...basePayload,
      });
    }
  }

  /**
   * @param {string} pageName
   * @param {string} spaExperience Spa Header experience > home title standalone|id advisor standalone|ipts|dwm (for analytics purposes)
   * @param {number} errorCode
   */
  static setPageNameForAnalytics(pageName: string, spaExperience: string, errorCode = 0) {
    let currentPageName;
    currentPageName = this.getPageNameForAnalytics(pageName);
    if (!currentPageName) {
      currentPageName = pageName;
    }

    // Ensure lastPageAnalytics property exists on window
    if (!window.hasOwnProperty('lastPageAnalytics')) {
      window.lastPageAnalytics = '';
    }

    //for dwm, there is custom logic to always redirect to /alerts in containers/alerts/DarkWebLanding.js
    if (currentPageName === '/') {
      currentPageName = spaExperience === 'dark web' ? 'alerts inbox' : 'dashboard';
    }
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    let errorDesc = errorCodeMapForAnalytics.adobeErrorCodeMap[errorCode];
    if (!errorDesc) {
      errorDesc = 'unknown error';
    }
    const finalPageNameForAnalytics =
      spaExperience !== 'error'
        ? `${spaExperience} ${currentPageName}`
        : `${currentPageName} ${errorCode} ${errorDesc}`;

    // Guard: skip if page + spaExperience + errorCode didn't change
    const key = `${spaExperience}|${currentPageName}|${errorCode}`;
    if (window.lastPageAnalytics === key) return;
    window.lastPageAnalytics = key;

    if (!window.nortonAnalytics) {
      window.nortonAnalytics = {};
    }
    if (!_isEmpty(window.adobeAnalytics)) {
      // if window.adobeAnalytics.norton_guid is an empty string, delete the property so that it doesn't get assigned back to window.nortonAnalytics as empty string
      if (window.adobeAnalytics.norton_guid === '') {
        delete window.adobeAnalytics.norton_guid;
      }
      Object.assign(window.nortonAnalytics, window.adobeAnalytics);
    }
    if (!window.nortonAnalytics.site_country) {
      window.nortonAnalytics.site_country = 'us';
    }
    window.nortonAnalytics.gen_id = this.getGenIdFromGdIdCookie();
    window.nortonAnalytics.content_title = window.document.title;
    if (finalPageNameForAnalytics) {
      window.nortonAnalytics.page_name = finalPageNameForAnalytics;
      if (window.s) {
        window.s.clearVars();

        // One-time override so AA's doPlugins/getQueryParam can read analytics
        // params (inid, om_em_cid) that were captured before handleRedirect
        // navigated away from the landing route.  pageURL is not cleared by
        // manageVars("clearVars"), so it survives into s.t().
        if (window._analyticsLandingParams) {
          const origin = document.location.origin + document.location.pathname;
          const hash = document.location.hash;
          window.s.pageURL = origin + '?' + window._analyticsLandingParams + hash;

          // pull the inid out and store it in window.nortonAnalytics
          const inid = new URLSearchParams(window._analyticsLandingParams).get('inid');
          if (inid) {
            window.nortonAnalytics.inid = inid;
          }

          delete window._analyticsLandingParams;
        }

        if (window.s.trackPageView) {
          window.s.trackPageView(window.nortonAnalytics);
          this.pushAmplitudePageView(pageName);
        }

        // Clear so subsequent page views use the natural location.href
        delete window.s.pageURL;
      }
    }
  }

  /**
   * @param  {string} currentPagePath page pathname
   * @returns {string}
   */
  static getPageNameForAnalytics(currentPagePath: string) {
    let strArr: string[] = [];
    if (currentPagePath && typeof currentPagePath === 'string') {
      // Strip URL-encoded query params that leak into the pathname (e.g. %3F = ?, %26 = &)
      currentPagePath = currentPagePath.split(/[?&]|%3F|%26/i)[0];
      strArr = currentPagePath.split('/');
    }
    // Handle /transactions/recurring paths first (before the general transactions truncation)
    if (strArr.length >= 4 && strArr[1] === 'transactions' && strArr[2] === 'recurring') {
      if (strArr[3] === 'all') {
        currentPagePath = '/transactions/recurring/all';
      } else if (strArr[3] === 'details') {
        currentPagePath = '/transactions/recurring/details';
      }
    } else if (strArr.length === 4 && strArr[1] === 'transactions' && strArr[2] === 'all' && strArr[3] === 'search') {
      currentPagePath = '/transactions/all/search';
    } else if (strArr.length >= 4 && strArr[1] === 'card-shield' && strArr[2] === 'merchant') {
      currentPagePath = '/card-shield/merchant';
    } else if (strArr.length > 3 && (strArr[1] === 'alerts' || strArr[1] === 'transactions')) {
      currentPagePath = `/${strArr[1]}/${strArr[2]}`;
    } else if (strArr.length > 2 && strArr[1] === 'credit') {
      // /credit/:tab/:gaCategory → /credit/:tab (e.g. /credit/efx)
      currentPagePath = `/${strArr[1]}/${strArr[2]}`;
    } else if (strArr.length > 2 && strArr[1] === 'credit-monitor') {
      // /credit-monitor/:gaCategory → /credit-monitor
      currentPagePath = `/${strArr[1]}`;
    } else if (strArr.length > 2 && strArr[1] === 'restoration') {
      currentPagePath = `/${strArr[1]}`;
    } else if (strArr.length > 2 && strArr[1] === 'monitoring') {
      currentPagePath = `/${strArr[1]}`;
    } else if (strArr.length > 2 && strArr[1] === 'privacymonitor') {
      currentPagePath = `/${strArr[1]}`;
    } else if (strArr.length > 2 && strArr[1] === 'scam-verification') {
      currentPagePath = `/${strArr[1]}`;
    } else if (strArr.length > 2 && strArr[1] === 'alerts' && strArr[2].match(/^\d+$/)) {
      currentPagePath = `/${strArr[1]}/detail`;
    }
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return pageNameMapForAnalytics.adobePageNameMap[currentPagePath];
  }

  /**
   * sets cookie to expire in 20 minutes, the duration of a web session &
   * member will see given prompt/modal every login
   */
  static saveCookieForModalShown(modalTypePrefix: string, accountId: string) {
    const expires = new Date();
    expires.setTime(new Date().getTime() + 20 * 60 * 1000);

    // Derive the registrable domain (e.g. ".norton.com", ".avast.com") from the
    // current hostname so the cookie works on any partner deployment.
    // A hardcoded ".norton.com" domain silently fails on non-Norton hosts —
    // browsers reject cookies whose domain attribute doesn't match the current host.
    const parts = window.location.hostname.split('.');
    const domain = parts.length >= 3 ? '.' + parts.slice(-2).join('.') : undefined;

    cookie.save(modalTypePrefix + accountId, SHOWN, {
      path: '/',
      expires,
      ...(domain ? {domain} : {}),
    });
  }

  /**
   * checks cookie whether the modal has been shown to the member in the current login session
   */
  static checkCookieForModalShown(modalTypePrefix: string, accountId: string) {
    return cookie.load(modalTypePrefix + accountId) === SHOWN;
  }

  // show phone prompt modal only to non-DWM members without a primary phone, and only once every 20 minute session
  // only prompt when SSN is on file: users with SSN+no phone need the phone for restoration; users with neither
  // have more fundamental PII gaps that should not be blocked behind a phone prompt (GUTS-7162)
  // @ts-expect-error TS(7006) FIXME: Parameter 'auth' implicitly has an 'any' type.
  static needsPrimaryPhoneModal(auth) {
    const productFeatures = _get(auth, 'user.primaryMember.plan.productFeatures', {});
    const isDwm = _get(auth, 'user.primaryMember.plan.isDwm', true);
    const isDashboardVisible = utils.dashboardVisible(productFeatures);
    const isPrimaryPhoneAvailable = !_isEmpty(_get(auth, 'user.primaryMember.primaryPhone', null));
    let primaryPhoneNumber;
    if (isPrimaryPhoneAvailable) {
      primaryPhoneNumber = _get(auth, 'user.primaryMember.primaryPhone.phoneNumber', '');
    }
    const accountId = _get(auth, 'user.primaryMember.accountId', '');
    const hasAlreadyBeenShown = utils.checkCookieForModalShown(PRIMARY_PHONE_MODAL_PREFIX, accountId);
    const hasFullItps = _get(productFeatures, 'restorationSummary.serviceCode', '') === 'LLS_RESTORATION';
    const hasSsn = !!_get(auth, 'user.primaryMember.countryIdentificationNumber.value', '');

    return isDashboardVisible && !isDwm && !primaryPhoneNumber && !hasAlreadyBeenShown && hasFullItps && hasSsn;
  }

  /**
   * Returns if needsDSPOnboarding is true or false
   * Returns false if the isAgent flag is true
   * @param {Object} auth
   * @returns {boolean}
   */
  static needsDspOnBoarding(auth: $TSFixMe) {
    if (_get(auth, 'user.isAgent') === true) {
      return false;
    }
    return _get(auth, 'user.primaryMember.needsDSPOnboarding', false);
  }

  /**
   * Returns if showQuebecReimbursementOptIn is true or false
   * Returns false if the isAgent flag is true
   * @param {Object} auth
   * @returns {boolean}
   */
  static showQuebecReimbursementOptIn(auth: AuthState) {
    if (_get(auth, 'user.isAgent', false)) {
      return false;
    }
    return _get(auth, 'user.primaryMember.plan.showQuebecReimbursementOptIn', false);
  }

  /**
   * SMM : check if we need to get image form social media or from CDN
   * @param {string} url path
   * @param {string} defaultImage path
   * @param {string} whiteLabelClient indicates partner client app
   * @returns {string} Returns path to the image
   */
  static getImagePath(url: string, defaultImage: string, whiteLabelClient: string) {
    const path = url.split('/')[0];
    if (path === 'http:' || _isEmpty(path)) {
      return utils.getCdnPath(defaultImage, whiteLabelClient);
    }
    return path === 'https:' ? url : utils.getCdnPath(`/social_media_monitoring/${url}@3x.png`, whiteLabelClient);
  }

  /**
   *
   * @param productFeatures, user's plan specific product features
   * @returns {boolean}, true for IDNAVIGATOR user, false for rest of the them
   */
  static isIdNavigator(productFeatures: ProductFeatures) {
    return _get(productFeatures, 'restorationSummary.serviceCode', '') === 'LLS_RESTORATIONASST';
  }

  /**
   * Returns true if credit score 1b or 3b is AVAILABLE based on productFeatures
   * @param {Object<string,ProductStatus>} productFeatures
   * @return {boolean}
   */
  static creditScoreFeatureAvailable(productFeatures: ProductFeatures) {
    return (
      utils.isFeatureAvailable(productFeatures, 'creditScore_1B') ||
      utils.isFeatureAvailable(productFeatures, 'creditScore_3B')
    );
  }

  /**
   * Returns true if credit reports 1b or 3b is AVAILABLE based on productFeatures
   * @param {Object<string,ProductStatus>} productFeatures
   * @return {boolean}
   */
  static creditReportsFeatureAvailable(productFeatures: ProductFeatures) {
    return (
      utils.isFeatureAvailable(productFeatures, 'creditReports_1B') ||
      utils.isFeatureAvailable(productFeatures, 'creditReports_3B')
    );
  }

  /**
   * Returns true if credit score 1b or 3b is AVAILABLE and ENROLLED
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static hasEquifaxCredit(productFeatures: ProductFeatures) {
    const creditScore1bCaFeatureStatus = _get(productFeatures, 'creditScore1bCa.featureStatus', '');

    return (
      creditScore1bCaFeatureStatus !== 'AVAILABLE' &&
      (utils.isFeatureAvailableAndEnrolled(productFeatures, 'creditScore_1B') ||
        utils.isFeatureAvailableAndEnrolled(productFeatures, 'creditScore_3B'))
    );
  }

  /**
   * Returns true if user is enrolled in 1B US credit score and not 3B US credit score
   * @param {ProductFeatures} productFeatures
   * @return {boolean}
   */
  static has1BUSCredit(productFeatures: ProductFeatures) {
    const creditScore1BFeatureStatus = _get(productFeatures, 'creditScore_1B.featureStatus', '');
    const creditScore1BFulfillmentStatus = _get(productFeatures, 'creditScore_1B.fulfillmentStatus', '');
    const creditScore1BCaFeatureStatus = _get(productFeatures, 'creditScore1bCa.featureStatus', '');
    const creditScore3BFeatureStatus = _get(productFeatures, 'creditScore_3B.featureStatus', '');

    return (
      creditScore1BCaFeatureStatus !== 'AVAILABLE' &&
      creditScore1BFeatureStatus === 'AVAILABLE' &&
      creditScore1BFulfillmentStatus === 'ENROLLED' &&
      creditScore3BFeatureStatus !== 'AVAILABLE' &&
      creditScore3BFeatureStatus !== 'ACTIVATE'
    );
  }

  /**
   * Given the full set of product features, checks whether the given feature is AVAILABLE
   * @param productFeatures
   * @param feature
   */
  static isFeatureAvailable(productFeatures: ProductFeatures | Record<string, never>, feature: string): boolean {
    return _get(productFeatures, `${feature}.featureStatus`, 'NOT_AVAILABLE') === 'AVAILABLE';
  }

  /**
   * Returns true if user is allowed to upgrade to a credit product. False otherwise.
   * @param {object} productFeatures - object with status information on various products
   * @param {string} primaryBureau - identifies primary credit bureau associated with current user's existing credit product
   * @param {string} creditPartnerId - identifies credit bureau associated with product that is an upgrade
   * @param {string} whiteLabelClient - identifies the white label client associated with the current user
   * @returns {boolean}
   */
  static isCreditUpgradeScenario(
    productFeatures: object,
    primaryBureau: string,
    creditPartnerId: string,
    whiteLabelClient: string
  ) {
    if (whiteLabelClient) {
      return false;
    }

    const scoreFeatureStatus1B = _get(productFeatures, 'creditScore_1B.featureStatus', '');
    const scoreFeatureStatus3B = _get(productFeatures, 'creditScore_3B.featureStatus', '');
    const scoreFeatureStatus1bCa = _get(productFeatures, 'creditScore1bCa.featureStatus', '');

    const is1BUpgradable =
      creditPartnerId === EFX_BUREAU_CODE && scoreFeatureStatus1B === 'UPGRADE' && primaryBureau === EFX_BUREAU_CODE;
    const is3BUpgradable =
      (creditPartnerId === TU_BUREAU_CODE || creditPartnerId === EXP_BUREAU_CODE) &&
      scoreFeatureStatus3B === 'UPGRADE' &&
      primaryBureau === EFX_BUREAU_CODE;
    const is1BCaUpgradable = scoreFeatureStatus1bCa === 'UPGRADE' && primaryBureau === TU_BUREAU_CODE;

    return is1BUpgradable || is3BUpgradable || is1BCaUpgradable;
  }

  /**
   * Returns a boolean indicating whether the credit score feature is available but fulfillment is not
   * @param {ProductFeatures} productFeatures
   * @returns {boolean}
   */
  static isCreditScoreUnavailable(productFeatures: ProductFeatures) {
    const scoreFeatureStatus1B = _get(productFeatures, 'creditScore_1B.featureStatus', '');
    const scoreFeatureStatus3B = _get(productFeatures, 'creditScore_3B.featureStatus', '');
    const scoreFeatureStatus1bCa = _get(productFeatures, 'creditScore1bCa.featureStatus', '');
    const fulfillmentStatus1B = _get(productFeatures, 'creditScore_1B.fulfillmentStatus', '');
    const fulfillmentStatus3B = _get(productFeatures, 'creditScore_3B.fulfillmentStatus', '');
    const fulfillmentStatus1bCa = _get(productFeatures, 'creditScore1bCa.fulfillmentStatus', '');

    return (
      (scoreFeatureStatus3B === 'AVAILABLE' && fulfillmentStatus3B === 'NOT_AVAILABLE') ||
      (scoreFeatureStatus1B === 'AVAILABLE' && fulfillmentStatus1B === 'NOT_AVAILABLE') ||
      (scoreFeatureStatus1bCa === 'AVAILABLE' && fulfillmentStatus1bCa === 'NOT_AVAILABLE')
    );
  }

  /**
   * Returns if the user has credit score features in UPGRADE feature status and without any ACTIVATE or AVAILABLE feature statuses
   * @param productFeatures - user's plan product features
   * @returns {boolean}
   */
  static isCreditUpgradeFeatures(productFeatures: object) {
    const scoreFeatureStatus1B = _get(productFeatures, 'creditScore_1B.featureStatus', '');
    const scoreFeatureStatus3B = _get(productFeatures, 'creditScore_3B.featureStatus', '');
    const scoreFeatureStatus1bCa = _get(productFeatures, 'creditScore1bCa.featureStatus', '');

    const hasUpgradeStatus =
      scoreFeatureStatus1B === 'UPGRADE' || scoreFeatureStatus3B === 'UPGRADE' || scoreFeatureStatus1bCa === 'UPGRADE';
    const hasNoAvailableStatus = !(
      scoreFeatureStatus1B === 'AVAILABLE' ||
      scoreFeatureStatus3B === 'AVAILABLE' ||
      scoreFeatureStatus1bCa === 'AVAILABLE'
    );
    const hasNoActiveStatus = !(
      scoreFeatureStatus1B === 'ACTIVE' ||
      scoreFeatureStatus3B === 'ACTIVE' ||
      scoreFeatureStatus1bCa === 'ACTIVE'
    );

    return hasUpgradeStatus && hasNoActiveStatus && hasNoAvailableStatus;
  }

  static isCreditActivateScenario(
    productFeatures: object,
    primaryBureau: string,
    creditPartnerId: string = EFX_BUREAU_CODE
  ) {
    const scoreFeatureStatus1B = _get(productFeatures, 'creditScore_1B.featureStatus', '');
    const scoreFeatureStatus3B = _get(productFeatures, 'creditScore_3B.featureStatus', '');
    const scoreFeatureStatus1bCa = _get(productFeatures, 'creditScore1bCa.featureStatus', '');
    const creditMonitoring1B = _get(productFeatures, 'creditMonitoring_1B.featureStatus', '');
    const creditMonitoring3B = _get(productFeatures, 'creditMonitoring_3B.featureStatus', '');
    const isEfxActivateRequired = creditPartnerId === EFX_BUREAU_CODE && scoreFeatureStatus1B === 'ACTIVATE';
    const isTuExpActivateRequired =
      (creditPartnerId === TU_BUREAU_CODE || creditPartnerId === EXP_BUREAU_CODE) &&
      scoreFeatureStatus3B === 'ACTIVATE';
    const isTuCaActivateRequired = scoreFeatureStatus1bCa === 'ACTIVATE';

    return (
      ((isEfxActivateRequired ||
        isTuExpActivateRequired ||
        creditMonitoring1B === 'ACTIVATE' ||
        creditMonitoring3B === 'ACTIVATE') &&
        primaryBureau === EFX_BUREAU_CODE) ||
      (isTuCaActivateRequired && primaryBureau === TU_BUREAU_CODE)
    );
  }

  /**
   * Returns boolean true if the user has 1B credit monitoring and no credit scores or reports
   * Used for determining if a user should have the credit monitor feature
   * @param productFeatures {ProductFeatures}
   * @returns {boolean}
   */
  static isCreditMonitorScenario(productFeatures: ProductFeatures) {
    const creditMonitoring1B = _get(productFeatures, 'creditMonitoring_1B.featureStatus', '');
    const hasCreditScore = this.creditScoreFeatureAvailable(productFeatures);
    const hasCreditReports = this.creditReportsFeatureAvailable(productFeatures);
    return creditMonitoring1B === 'AVAILABLE' && !hasCreditScore && !hasCreditReports;
  }

  /**
   * Returns the transaction preference values
   * @param {Object}  inputData
   * @returns {Object}
   */
  static getTransactionPrefs(inputData: $TSFixMe) {
    const pIndex = _findIndex(inputData, {type: 'PURCHASE'});
    const tIndex = _findIndex(inputData, {type: 'TRANSFER'});
    const wIndex = _findIndex(inputData, {type: 'WITHDRAWAL'});
    const jpTransactionIndex = _findIndex(inputData, {type: 'TRANSACTION'});
    return {
      transaction: _get(inputData[pIndex], 'value', '') || _get(inputData[jpTransactionIndex], 'value', ''),
      transfer: _get(inputData[tIndex], 'value', ''),
      withdrawal: _get(inputData[wIndex], 'value', ''),
    };
  }

  /**
   * compare function that defines the sort order. its an input to Array.prototype.sort
   * @param {string} field - to sort on
   * @param {boolean} reverse
   * @param {function} primer
   */
  static sortBy(field: string, reverse: boolean, primer: $TSFixMeFunction) {
    let reverseVal = reverse;
    const key = primer
      ? // @ts-expect-error TS(7006) FIXME: Parameter 'x' implicitly has an 'any' type.
        function(x) {
          return primer(x[field]);
        }
      : // @ts-expect-error TS(7006) FIXME: Parameter 'x' implicitly has an 'any' type.
        function(x) {
          return x[field];
        };
    // @ts-expect-error TS(2322) FIXME: Type 'number' is not assignable to type 'boolean'.
    reverseVal = !reverseVal ? 1 : -1;
    // @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
    return function(a, b) {
      const val1 = key(a);
      const val2 = key(b);
      // @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
      return reverseVal * ((val1 > val2) - (val2 > val1));
    };
  }

  /** General function for PhoneModal and PrimaryPhonePromptModal
   * for evaluating success msg and dispatch PersonalInfoStatusModal
   * @param {function} localizedStringBundle
   * @param {function} dispatch
   * **/
  static dispatchPersonalInfoStatusModal(
    localizedStringBundle: StringMap,
    dispatch: $TSFixMeFunction,
    options?: {previousModal?: $TSFixMe}
  ) {
    const statusInfo: $TSFixMe = {
      header: localizedStringBundle.MANAGE_MODAL_PHONE_HEADER,
      icon: localizedStringBundle.MANAGE_MODAL_PHONE_ICON,
      message: '',
      origin: 'PHONE',
    };
    if (options?.previousModal) {
      statusInfo.previousModal = options.previousModal;
    }
    dispatch({
      type: 'SHOW_MODAL',
      modalType: 'MA_PERSONAL_INFO_STATUS_MODAL',
      modalProps: {
        data: statusInfo,
      },
    });
    utils.trackEventForMonitoredInfo('Phone save');
  }

  /** General function to check if smm needs to be displayed
   * @param {object} auth
   * @return {boolean}
   * **/
  static smmFeatureAvailable(auth: $TSFixMe) {
    let smmFulfillmentStatus = _get(
      auth,
      'user.primaryMember.plan.productFeatures.socialMediaMonitoring.fulfillmentStatus'
    );

    //SMM feature is not enabled for primary, check SMM is enabled for junior
    if (smmFulfillmentStatus === 'NOT_AVAILABLE') {
      // check if any associated member has SMM feature enabled, in case SMM is not enabled for primary member
      const associatedMembers = _get(auth, 'user.associatedMembers', []);
      if (Array.isArray(associatedMembers) && associatedMembers.length > 0) {
        const associatedMemberSmmEnabled = utils.getSmmEnabledAssociatedMember(associatedMembers);
        const productFeatures =
          Array.isArray(associatedMemberSmmEnabled) && associatedMemberSmmEnabled.length > 0
            ? _get(associatedMemberSmmEnabled[0], 'plan.productFeatures')
            : null;
        if (productFeatures) {
          smmFulfillmentStatus = _get(productFeatures, 'socialMediaMonitoring.fulfillmentStatus');
        }
      }
    }

    return smmFulfillmentStatus === 'ENROLLED' || smmFulfillmentStatus === 'PENDING';
  }

  /** General function to check if smm needs to be enabled for primary member
   * @param {object} auth
   * @return {boolean}
   * **/
  static smmFeatureAvailableForPrimary(auth: $TSFixMe) {
    const smmFulfillmentStatus = _get(
      auth,
      'user.primaryMember.plan.productFeatures.socialMediaMonitoring.fulfillmentStatus'
    );

    return (
      window.REACT_APP_ENABLE_SOCIAL_MEDIA_MONITORING === true &&
      (smmFulfillmentStatus === 'ENROLLED' || smmFulfillmentStatus === 'PENDING')
    );
  }

  /**
   * Check SMM feature avaiable for associated members
   * @param {Array} associatedMembers - Array of household members object
   * @return {boolean}
   */
  static associatedMemberSmmFeatureAvailable(associatedMembers: Array<any>) {
    const associatedMemberSmmEnabled = utils.getSmmEnabledAssociatedMember(associatedMembers);
    return !!(Array.isArray(associatedMemberSmmEnabled) && associatedMemberSmmEnabled.length > 0);
  }

  /**
   * Function to get SMM enabled associated member product features
   * @param {Array} associatedMembers - Array of household members object
   * @return {Array} - SMM enabled associated members object
   */
  static getSmmEnabledAssociatedMember(associatedMembers: Array<any>) {
    return associatedMembers?.filter(
      (member) => utils.hasSocialMediaMonitoring(member.plan.productFeatures) && member.relationship === 'Guardian'
    );
  }

  /**
   * Checks if given feature is available for associated members
   * @param {Array} associatedMembers - Array of household members object
   * @param {string} featureName - feature name string like 'childFreezes'
   * @return {boolean}
   */
  static associatedMemberFeatureAvailable(associatedMembers: Array<any>, featureName: string) {
    const associatedMemberFeatureEnabled = utils.getFeatureEnabledAssociatedMember(associatedMembers, featureName);
    return !!(Array.isArray(associatedMemberFeatureEnabled) && associatedMemberFeatureEnabled.length > 0);
  }

  /**
   * Function to get feature enabled associated member product features
   * @param {Array} associatedMembers - Array of household members object
   * @param {string} featureName - feature name string like 'childFreezes'
   * @return {Array} - feature name enabled associated members object
   */
  static getFeatureEnabledAssociatedMember(associatedMembers: Array<any>, featureName: string) {
    return associatedMembers.filter(
      (member) => utils.isFeaturePresent(member.plan.productFeatures, featureName) && member.relationship === 'Guardian'
    );
  }

  /** General function for Transactions and TransactionsOverview
   * for evaluating success msg and dispatch DeleteInstitutionModal
   * @param {string} institutionId - institutionId
   * @param {string} memSiteAccountId - memSiteAccountId
   * @param e - the triggering event
   * @param {function} localizedStringBundle
   * @param {function} dispatch
   * @param {ProductFeatures} productFeatures - product features status information
   * **/
  static dispatchTransactionsDeleteAccountModal(
    institutionId: string,
    memSiteAccountId: string,
    // @ts-expect-error TS(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
    e,
    localizedStringBundle: StringMap,
    dispatch: $TSFixMeFunction,
    productFeatures: ProductFeatures
  ) {
    e.preventDefault();
    utils.trackEventForManageAccount('TXM_Monitor', localizedStringBundle.MANAGE_ACCOUNT_EDIT, true);
    dispatch({
      type: 'SHOW_MODAL',
      modalType: 'DELETE_INSTITUTION_MODAL',
      modalProps: {
        data: {
          institutionId,
          memSiteAccountId,
        },
        static: {
          header: localizedStringBundle.MANAGE_MODAL_TRANSACTION_MONITORING_HEADER,
          icon: localizedStringBundle.MANAGE_MODAL_TRANSACTION_MONITORING_ICON,
          title: localizedStringBundle.MANAGE_MODAL_TRANSACTION_MONITORING_HEADER_REMOVE,
          feature: '',
          infoTitle: localizedStringBundle.MANAGE_MODAL_TRANSACTION_MONITORING_INFO_TITLE,
          text1: utils.isRestorationAvailable(productFeatures)
            ? localizedStringBundle.MANAGE_MODAL_TRANSACTION_MONITORING_INFO_TEXT1
            : localizedStringBundle.MANAGE_MODAL_TRANSACTION_MONITORING_INFO_TEXT1_NO_RESTORATION,
        },
      },
    });
  }

  /** General function to check valid url
   * @param {string} url - Url to be tested
   * @return {boolean} - returns true/false if url is valid
   */
  static isValidUrl(url: string) {
    const matchPattern =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/gm;
    return matchPattern.test(url);
  }

  /**
   * Returns the DSP shell URL (redirects, modal defaults, credit activation exiturl).
   * Avast partners override REACT_APP_DSP_EXIT_LINK at theme load; Norton uses REACT_APP_NORTON_DSP_LINK.
   */
  static getDspUrl(partnerClientId = ''): string {
    return utils.resolvePartnerUrl(
      window.REACT_APP_DSP_EXIT_LINK || window.REACT_APP_NORTON_DSP_LINK,
      '/extspa/identity',
      partnerClientId
    );
  }

  static isAvastHostname(hostname = window.location.hostname): boolean {
    if (!hostname) {
      return false;
    }
    return hostname === 'avast.com' || hostname.endsWith('.avast.com');
  }

  static isAvast3PartnerContext(partnerClientId = ''): boolean {
    const storedWhiteLabelClient =
      typeof window.sessionStorage?.getItem === 'function' ? window.sessionStorage.getItem('whiteLabelClient') : null;
    return (
      partnerClientId === 'avast3' ||
      storedWhiteLabelClient === 'avast3' ||
      window.clientId === 'avast3' ||
      utils.isAvastHostname()
    );
  }

  /**
   * Shared resolver: overridden config when partner theme is loaded; else Avast NGP path pre-theme; else config.
   */
  static resolvePartnerUrl(configValue: string, avastPath: string, partnerClientId = ''): string {
    if (window.REACT_APP_DSP_EXIT_LINK) {
      return configValue;
    }
    if (utils.isAvast3PartnerContext(partnerClientId) && window.REACT_APP_NGP_AVAST_HOST) {
      return `${window.REACT_APP_NGP_AVAST_HOST}${avastPath}`;
    }
    return configValue;
  }

  /**
   * Returns the LifeLock welcome / LSA consent URL.
   * Avast partners use NGP /extspa/consent with returnurl=/extspa/identity.
   */
  static getLsaLink(partnerClientId = ''): string {
    if (window.REACT_APP_DSP_EXIT_LINK) {
      return window.REACT_APP_LSA_LINK;
    }
    if (utils.isAvast3PartnerContext(partnerClientId) && window.REACT_APP_NGP_AVAST_HOST) {
      return buildAvastLsaConsentUrl(window.REACT_APP_NGP_AVAST_HOST);
    }
    return window.REACT_APP_LSA_LINK;
  }

  /**
   * Returns the LSA consent URL when token exchange fails with subscription error 20014.
   * Avast partners use NGP /extspa/consent with returnurl=/extspa/identity.
   */
  static getLsaLinkOn20008Error(partnerClientId = ''): string {
    if (window.REACT_APP_DSP_EXIT_LINK) {
      return window.REACT_APP_LSA_LINK_ON_20008_ERROR;
    }
    if (utils.isAvast3PartnerContext(partnerClientId) && window.REACT_APP_NGP_AVAST_HOST) {
      return buildAvastLsaConsentUrl(window.REACT_APP_NGP_AVAST_HOST);
    }
    return window.REACT_APP_LSA_LINK_ON_20008_ERROR;
  }

  /**
   * Returns the identity enrollment URL for Apple relay / no-account flows (error 20009).
   * Avast partners override REACT_IDENTITY_ENROLLMENT_LINK at theme load; before theme load,
   * Avast NGP context (avast3 client or avast.com host) uses REACT_APP_NGP_AVAST_HOST.
   */
  static getIdentityEnrollmentLink(partnerClientId = ''): string {
    return utils.resolvePartnerUrl(window.REACT_IDENTITY_ENROLLMENT_LINK, '/extspa/idenroll', partnerClientId);
  }

  /**
   * Returns the NGP dashboard URL for error copy and non-DSP user redirects.
   * Avast partners override REACT_APP_NORTON_NGP_LINK at theme load.
   */
  static getNgpDashboardLink(partnerClientId = ''): string {
    return utils.resolvePartnerUrl(window.REACT_APP_NORTON_NGP_LINK, '/extspa/dashboard', partnerClientId);
  }

  /**
   * Returns the NGP account URL for subscription-renewal error copy.
   * Avast partners override REACT_APP_NORTON_ACCOUNT_LINK at theme load.
   */
  static getNgpAccountLink(partnerClientId = ''): string {
    return utils.resolvePartnerUrl(
      window.REACT_APP_NORTON_ACCOUNT_LINK,
      '/extspa/account/personalinfo',
      partnerClientId
    );
  }

  /** General function to open credit kba window
   */
  static openTuKba() {
    utils.sendMessage(
      {type: 'CHANGE_WINDOW_LOCATION', payload: window.REACT_APP_ACTIVATE_CREDIT_KBA},
      window.REACT_APP_PARENT_HOST
    );
  }

  /** function to open credit activation window - pending credit
   */
  static openCreditActivationPendingCredit() {
    const exitUrl = utils.getDspUrl();
    utils.sendMessage(
      {
        type: 'CHANGE_WINDOW_LOCATION',
        payload: window.REACT_APP_ACTIVATE_CREDIT_KBA + '?exiturl=' + exitUrl,
      },
      window.REACT_APP_PARENT_HOST
    );
  }

  /** function to open credit activation window - pending pii
   */
  static openCreditActivationPendingPii() {
    const exitUrl = utils.getDspUrl();
    utils.sendMessage(
      {
        type: 'CHANGE_WINDOW_LOCATION',
        payload: window.REACT_APP_ACTIVATE_CREDIT + '?exiturl=' + exitUrl,
      },
      window.REACT_APP_PARENT_HOST
    );
  }

  /**
   * General function to scroll component to specific top
   * @param {string} section - Id of the section to be scrolled
   * @param {string[]} availableSections - Group of section that can be scrolled to
   */
  static scrollToSection(section = '', availableSections: string[] = []) {
    if (section && availableSections.indexOf(section) !== -1) {
      const el = document.getElementById(section);
      const elementSpec = el?.getBoundingClientRect();
      if (elementSpec && typeof elementSpec?.top === 'number') {
        const isInIframe = (() => {
          try {
            return window.self !== window.top;
          } catch (_e) {
            // Accessing window.top can throw for cross-origin iframes.
            // In that case, we are definitely not the top window.
            return true;
          }
        })();

        // In the "normal" embedded iframe experience, ask the parent container to scroll.
        if (isInIframe) {
          try {
            window.parent.postMessage(
              {type: 'SCROLL_PARENT', payload: _get(elementSpec, 'top', 0)},
              window.REACT_APP_PARENT_HOST
            );
            window.parent.postMessage(
              {
                type: 'GET_PARENT_WINDOW_SPEC',
                payload: {actionToTake: 'updateModalSpec'},
              },
              window.REACT_APP_PARENT_HOST
            );
            return;
          } catch (_e) {
            // Fall back to in-document scrolling below.
          }
        }

        // In mobile app webviews (top-level window) there's no parent scroll handler.
        // Scroll within the current document/scroll container instead.
        try {
          el?.scrollIntoView({behavior: 'smooth', block: 'start'});
        } catch (_e) {
          window.scrollTo({top: 0, behavior: 'smooth'});
        }
      }
    }
  }

  /**
   * Returns true when plan contains credit uk feature
   * @param {ProductFeatures} productFeatures - contains all the features of a plan
   * @returns {boolean} true when plan contains credit uk feature, false otherwise
   */
  static isCreditUk(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'creditView_1B_Gb');
  }

  /**
   * Returns true when plan contains credit australia feature
   * @param {ProductFeatures} productFeatures - contains all the features of a plan
   * @returns {boolean} true when plan contains credit australia feature, false otherwise
   */
  static isCreditAu(productFeatures: ProductFeatures) {
    const creditAUFulfillmentStatus = _get(productFeatures, 'credit_1b_AU.fulfillmentStatus', '');
    return utils.isFeaturePresent(productFeatures, 'credit_1b_AU') && creditAUFulfillmentStatus === 'ENROLLED';
  }

  /**
   * Send GA event
   * @param {string} category - denotes the category
   * @param {string} action - denotes the action(click,...)
   * @param {string} label - denotes the label(it can be page name,....)
   * @param {number} value - denotes the value
   */
  static triggerGaEvent(category: string, action: string, label: string, value = 1) {
    const option = {
      category,
      action,
      label,
      value,
    };
    utils.trackEvent(option);
  }

  /**
   * Renders footer for the credit uk feature
   * @param {object} localizedStringBundle contains the strings corresponding to user locale
   * @returns {JSX.Element} JSX for the credit uk feature
   */
  static renderCreditUkFooter(localizedStringBundle: StringMap) {
    return (
      <div
        className="m-4 flex-grow basis-full px-4 text-center text-sm text-onSurface-secondary lg:px-0"
        data-testid="page-footer"
      >
        {localizedStringBundle.CREDIT_UK_FOOTER}
      </div>
    );
  }

  /**
   * Gets currency code by country code
   * @param {string} countryCode - countryCode string of a given member
   * @return {string} currencyCode - Currency code (e.g. USD, JPY, CAD) for use in formatting currency amounts.
   */
  static getCurrencyCode(countryCode: string) {
    switch (countryCode) {
      case 'AU':
        return 'AUD';
      case 'CA':
        return 'CAD';
      case 'JP':
        return 'JPY';
      case 'GB':
        return 'GBP';
      case 'NZ':
        return 'NZD';
      case 'US':
      default:
        return 'USD';
    }
  }

  /**
   * Gets page title by location pathname
   * @param {string} pathname - Location pathname
   * @param {StringMap} localizedStringBundle - string bundle
   * @param dynamicData - Optional dynamic data used for determining certain titles (e.g., privacyMonitor)
   * @return {string} title (Dashboard, Alerts, etc.)
   */
  static getPageTitle(pathname: string, localizedStringBundle: StringMap, dynamicData?: $TSFixMe) {
    // GUTS-3080: Adds non translated default title incase localizedStringBundle is not initialized.
    let pageTitle: string = localizedStringBundle.LEFT_NAV_DASHBOARD || 'Dashboard';
    const privacyLicense = this.safeGet(dynamicData, 'privacyLicense', {});
    const licenseType = utils.safeGet(privacyLicense, 'licenseType', '');
    const isUserPMA = licenseType === 'PMA';
    const isUserPMALite = licenseType === 'PMA lite';
    switch (pathname) {
      case 'dashboard':
        pageTitle = localizedStringBundle.LEFT_NAV_DASHBOARD;
        break;
      case 'alerts':
        pageTitle = localizedStringBundle.LEFT_NAV_ALERTS;
        break;
      case 'credit':
        pageTitle = localizedStringBundle.LEFT_NAV_CREDIT;
        break;
      case 'credit-monitor':
        pageTitle = localizedStringBundle.LEFT_NAV_CREDIT_MONITOR;
        break;
      case 'credit-au':
        pageTitle = localizedStringBundle.LEFT_NAV_CREDIT_AU;
        break;
      case 'locks':
        pageTitle = localizedStringBundle.LEFT_NAV_IDENTITY_LOCK;
        break;
      case 'hometitle':
        pageTitle = localizedStringBundle.LEFT_NAV_HOMETITLE;
        break;
      case 'transactions':
        pageTitle = localizedStringBundle.LEFT_NAV_TRANSACTIONS;
        break;
      case 'restoration':
        pageTitle = localizedStringBundle.LEFT_NAV_ID_RESTORATION;
        break;
      case 'estate-planning':
        pageTitle = localizedStringBundle.LEFT_NAV_ESTATE_PLANNING;
        break;
      case 'monitoring':
        pageTitle = localizedStringBundle.LEFT_NAV_MONITORED_INFO;
        break;
      case 'plandetails':
        pageTitle = localizedStringBundle.LEFT_NAV_PLAN_DETAILS;
        break;
      case 'privacyadvisor':
        pageTitle = localizedStringBundle.LEFT_NAV_PRIVACY_ADVISOR;
        break;
      case 'privacymonitor':
        pageTitle = isUserPMA
          ? localizedStringBundle.LPM_PMA
          : isUserPMALite
          ? localizedStringBundle.LPM_PMA_LITE
          : localizedStringBundle.LPM;
        break;
    }
    return pageTitle;
  }

  static isTxmUkAvailable(productFeatures: ProductFeatures) {
    return _get(productFeatures, 'financialMonitoringUk.featureStatus', 'NOT_AVAILABLE') === 'AVAILABLE';
  }

  /**
   * show financial monitoring UK UI for US and Canada
   * @param {ProductFeatures} productFeatures - object specifying product features for current user's plan
   * @return {boolean} - returns true if user has transactionMonitoring enabled
   */
  static showFmUiUsCa(productFeatures: ProductFeatures) {
    return utils.isFeaturePresent(productFeatures, 'transactionMonitoring');
  }

  /**
   * Gets transaction item key for navigation menu by coountryCode
   * @param {ProductFeatures} productFeatures - object specifying product features for current user's plan
   * @return {string} transaction item key
   */
  static getTxmNameKey(productFeatures: ProductFeatures) {
    return this.isTxmUkAvailable(productFeatures) ||
      this.showFmUiUsCa(productFeatures) ||
      utils.isFeatureAvailable(productFeatures, 'fwMultiAggregator')
      ? 'LEFT_NAV_FINANCIAL_MONITORING'
      : 'LEFT_NAV_TRANSACTIONS';
  }

  /**
   * Converts a rem value to pixels based on the current font size of the document
   * @param {number} rem - the rem number to convert to pixels
   * @param {HTMLElement | null} documentElement - the element who's font size to use
   * @returns {number} - pixel value of the input rem, defaults to 0
   */
  static convertRemToPixels(rem: number, documentElement: HTMLElement | null) {
    return documentElement ? rem * parseFloat(getComputedStyle(documentElement).fontSize) : 0;
  }

  /**
   * Tests a candidate URL against a list of white-listed URL domain values
   * @param {string} url - The candidate URL to be checked against the white-listed domains
   * @param {string[]} whiteListedDomains - list of pure domain strings (eg. api.equifax.com) that are white-listed
   *
   * @returns {boolean} True if the candidate URL has a domain value that is white-listed
   */
  static isUrlWhiteListed(url: string, whiteListedDomains: string[]) {
    // Use the JavaScript URL class to get the hostname, which is scheme prefix ("www.") + full domain
    try {
      const urlObj = new URL(url);
      const urlDomain = urlObj.hostname;
      return whiteListedDomains.includes(urlDomain);
    } catch {
      // This is mainly to catch "TypeError" exceptions thrown by the URL constructor for invalid URL string input
      return false;
    }
  }

  /**
   * Returns true if product features have deferred fulfillment eligible and deferred state is pending pii
   * @param {ProductFeatures} productFeatures - contains all the features of a plan
   * @returns {boolean} Returns true if product features have deferred fulfillment eligible and deferred state is pending pii
   */
  static isPendingPIICollectionState(productFeatures: ProductFeatures): boolean {
    const deferredEnrollmentEligible = _get(productFeatures, 'deferredServicesSummary.deferredEligible', false);
    const deferredState = _get(productFeatures, 'deferredServicesSummary.deferredState', '');
    return deferredEnrollmentEligible && deferredState === 'PENDING_PII';
  }

  /**
   * Returns true if product features have deferred fulfillment eligible and deferred state is pending credit
   * @param {ProductFeatures} productFeatures - contains all the features of a plan
   * @returns {boolean} Returns true if product features have deferred fulfillment eligible and deferred state is pending credit
   */
  static isPendingCreditCollectionState(productFeatures: ProductFeatures): boolean {
    const deferredEnrollmentEligible = _get(productFeatures, 'deferredServicesSummary.deferredEligible', false);
    const deferredState = _get(productFeatures, 'deferredServicesSummary.deferredState', '');
    return deferredEnrollmentEligible && deferredState === 'PENDING_CREDIT';
  }

  /**
   * Helper method to add spaces around back slashes in a string for enhanced line breaking
   * @param {string} str - string to be modified
   * @returns {string} Returns modified string with spaces around back slashes
   */
  static addSpacesAroundSlash(str: string) {
    return str.replace(/\//g, ' / ');
  }

  /**
   * Helper method to get displayLang, countryCode, partnerId, puId to be used in site director support urls
   * @param {AuthState} auth
   */
  static getSupportUrlParams(auth: AuthState) {
    const displayLang = (getBrowserLanguage() || 'en').substring(0, 2);
    const countryCode = _get(auth, 'user.countryCode', 'US');
    const partnerId = (_get(auth, 'user.primaryMember.plan.partnerDetails.partnerId', '') || '').toString();
    const puId = (_get(auth, 'user.primaryMember.plan.partnerDetails.partnerUnitId', '') || '').toString();
    return {displayLang, countryCode, partnerId, puId};
  }

  /**
   * Returns a site director support url
   * @param {string} helpId - value for helpid param
   * @param {string} displayLang - value for displayLang param
   * @param {string} countryCode - value for countryCode param
   * @param {string} partnerId - value for partnerid param
   * @param {string} puid - value for puid param
   * @param {string} ssdCat - value for ssdCat param; default value is '243'
   * @returns {string} Returns a site director support url
   */
  static getSupportUrl(
    helpId: string,
    displayLang: string,
    countryCode: string,
    partnerId: string,
    puid: string,
    ssdCat = LL_SSDCAT
  ) {
    let url = interpolate(window.REACT_APP_SITEDIRECTOR_URL, {
      helpid: helpId,
      env: window.REACT_APP_LABEL === 'production' ? 'prod' : 'int',
      displayLang,
      localeParam: _isEmpty(countryCode) ? 'US' : countryCode,
      partnerid: partnerId || '',
      puid: puid || '',
    });
    if (ssdCat !== LL_SSDCAT) {
      url = url.replace(`ssdcat=${LL_SSDCAT}`, `ssdcat=${ssdCat}`);
    }
    if (partnerId && ![LL_SITE_DIRECORY_PARTNER_ID].includes(partnerId)) {
      url += '&layouttype=sos';
    }
    return url;
  }

  /**
   * Override specific values in config.js using site director url.
   * These keys are overridden for partners in src/helper/partnerUtils.js.
   * @param {string} displayLang - value for displayLang param
   * @param {string} countryCode - value for countryCode param
   * @param {string} partnerId - value for partnerid param
   * @param {string} puid - value for puid param
   * @param {string} ssdCat - value for ssdCat param; default value is '243'
   * @returns {string} Returns a site director support url
   */
  static overrideLocksAndFreezeSupportUrl(
    displayLang: string,
    countryCode: string,
    partnerId: string,
    puId: string,
    LL_SSDCAT: string
  ) {
    window.REACT_APP_CREDIT_FREEZE_IRS_LEARN_MORE = utils.getSupportUrl(
      'Freeze_IRS',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_BANK_LEARN_MORE_ARTICLE = utils.getSupportUrl(
      'TXM_Bank_Freeze',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_CHILD_CREDIT_LEARN_MORE_ARTICLE = utils.getSupportUrl(
      'Child_Credit',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_CREDIT_LEARN_MORE_ARTICLE = utils.getSupportUrl(
      'Credit_Freeze_learnmore',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_CREDIT_FREEZE_EXPERIAN_KB_LINK = utils.getSupportUrl(
      'Credit_Freeze_Experian',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_EQUIFAX_LEARN_MORE = utils.getSupportUrl(
      'Data_Freeze_Equifax',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_EVERIFY_LEARN_MORE = utils.getSupportUrl(
      'Everify_learnmore',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_IDENITY_LOCK_LEARN_MORE_ARTICLE = utils.getSupportUrl(
      'IDTheft_Learnmore',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_PAYDAY_LEARN_MORE_ARTICLE = utils.getSupportUrl(
      'Payday_Loan',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_UTILITIES_LEARN_MORE_ARTICLE = utils.getSupportUrl(
      'Freeze_Utilities',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
    window.REACT_APP_FREEZE_AND_LOCK_DIFFERENT_LINK = utils.getSupportUrl(
      'Lock_Freeze_Diff',
      displayLang,
      countryCode,
      partnerId,
      puId,
      LL_SSDCAT
    );
  }

  /**
   * Determines if a white label client should see the verification code component for agent desktop member verification purposes.
   *
   * @param whiteLabelClient the white label client string
   * @returns {boolean} whether the partner should see the verification code component
   */
  static partnerShouldSeeVerificationCode(whiteLabelClient: string) {
    return (
      whiteLabelClient === 'avast_intl' ||
      whiteLabelClient === 'avg_intl' ||
      whiteLabelClient === 'moneylion' ||
      whiteLabelClient === 'avast3' ||
      whiteLabelClient === 'avira_ngp'
    );
  }

  /**
   * Takes in a stringified number and formats it with dashes for readability ####-##-####
   *
   * @param inputNumber stringified number to format
   * @returns {string} formatted number
   */
  static formatNumber(inputNumber: string) {
    const numberString = inputNumber.toString();
    // Clean out all non-numeric characters
    const cleaned = numberString.replace(/\D/g, '');

    // format to ####-##-####
    let formatted = '';
    for (let i = 0; i < cleaned.length; i++) {
      if (i === 4 || i === 6) {
        formatted += '-';
      }
      formatted += cleaned[i];
    }

    return formatted;
  }

  /**
   * returns latest institution updated date from institutionList
   *
   * @param {Institution[]} institutionsList - stringified number to format
   * @returns {string} formatted latest institution updated date
   */
  static getLastUpdatedInstitutionDate(institutionsList: Institution[]) {
    let lastInstitutionUpdatedDate = '';

    if (Array.isArray(institutionsList) && institutionsList.length > 0) {
      // get first date from sorted institutionRefreshDate from institutionsList
      lastInstitutionUpdatedDate = institutionsList.sort(
        (a: Institution, b: Institution) =>
          new Date(b?.institutionRefreshDate).getTime() - new Date(a?.institutionRefreshDate).getTime()
      )[0].institutionRefreshDate;
    }

    return dateUtils.convertDateToSlashFormat(lastInstitutionUpdatedDate, getLangCode(), '');
  }

  /**
   * Retrieves member data based on the provided authentication state, accountId, and primary member status.
   *
   * @param {AuthState} auth Auth object containing all the member related data
   * @param {string} accountId - accountId of the target member
   * @param {boolean} isPrimary - A boolean indicating whether to retrieve the primary member or an associated member.
   * @return {Member} The member data object if found, or an empty object if no member data is available.
   */
  static getMemberData = (auth: AuthState, accountId: string, isPrimary: boolean) => {
    const user = _get(auth, 'user', {});
    if (_isEmpty(user)) {
      return {};
    }
    const member = isPrimary
      ? _get(user, 'primaryMember', {})
      : _get(user, 'associatedMembers', []).find((m: Member) => m.accountId === accountId);

    return member || {};
  };

  /**
   * Retrieves the social media accounts of a junior member from the given account collection.
   *
   * @param {SmmAccountsData} accountCollection - The collection of social media accounts, including junior members.
   * @param {string} memberAccountId - The accountId of the junior member whose social media accounts are to be retrieved.
   * @returns {SocialMediaAccountsType[]} An array of social media accounts associated with the specified junior member.
   */
  static getJuniorSocialMediaAccounts = (accountCollection: SmmAccountsData, memberAccountId: string) => {
    const juniorMembers = _get(accountCollection, 'juniorMembers', []);
    const accounts = juniorMembers.find(
      (smmJuniorAccount: SmmAccount) => _get(smmJuniorAccount, 'accountId', []) === memberAccountId
    );
    return _get(accounts, 'socialMediaAccounts', []);
  };

  /**
   * Determines if a social media manager (SMM) account requires attention based on the member's account
   * and the status of their associated social media accounts.
   *
   * @param {Member} member - The member object containing details about the user.
   * @param {SmmAccountsData} smmAccountList - The list of social media manager accounts, including primary and junior accounts.
   * @param  {boolean} isPrimary - A boolean indicating whether to check the primary member's social media accounts.
   * @returns {boolean} A boolean value indicating whether the SMM account requires attention. Returns `true` if
   *          there are no associated accounts or if any account is in a "DISCONNECTED" status; otherwise, `false`.
   */
  static isSmmNeedAttention(member: Member, smmAccountList: SmmAccountsData, isPrimary: boolean) {
    const memberAccountId = _get(member, 'accountId', '');
    const smmAccounts = isPrimary
      ? _get(smmAccountList, 'primaryMember.socialMediaAccounts', [])
      : this.getJuniorSocialMediaAccounts(smmAccountList, memberAccountId);

    const unconnectedAccounts = smmAccounts.filter((account: any) =>
      ['DISCONNECTED'].includes(_get(account, 'status', ''))
    );
    return _isEmpty(unconnectedAccounts) ? false : true;
  }

  /**
   * Checks if any social media monitoring (SMM) account is disconnected.
   *
   * This function inspects the `socialMediaMonitoring` object to determine if any
   * primary or junior member's social media accounts have a status of "DISCONNECTED".
   *
   * @param {SmmAccountsData} smmAccounts - The object containing social media monitoring data.
   * @returns {boolean} `true` if any SMM account is disconnected; otherwise, `false`.
   */
  static isAnySmmAccountDisconnected(smmAccounts: $TSFixMe) {
    const primarySmmAccounts = _get(smmAccounts, 'primaryMember.socialMediaAccounts', []);
    if (
      primarySmmAccounts.filter((account: any) => ['DISCONNECTED'].includes(_get(account, 'status', ''))).length > 0
    ) {
      return true;
    }

    const juniorMembers = _get(smmAccounts, 'juniorMembers', []);
    for (const junior of juniorMembers) {
      const unconnectedAccounts = _get(junior, 'socialMediaAccounts', []).filter((account: any) =>
        ['DISCONNECTED'].includes(_get(account, 'status', ''))
      );

      if (unconnectedAccounts.length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Determines if a monitored member's information requires attention.
   *
   * @param {Member} member - The member object to evaluate.
   * @param {boolean} isPrimary - A boolean indicating if the member is the primary account holder.
   * @param {SmmAccountsData} smmAccounts - The SMM accounts associated with the member.
   * @param {Institution[]} institutionsList - A list of institutions to check for inactivity.
   * @returns {boolean} A boolean indicating whether the member's information needs attention.
   */
  static isMonitoredInfoNeedAttention(
    member: Member,
    isPrimary: boolean,
    smmAccounts: SmmAccountsData,
    institutionsList: Institution[],
    isUnified = false,
    showWarningIconUnified = false
  ) {
    if (!isPrimary && _get(member, 'relationship', '') === 'Administrator') return false;

    if (isUnified && _get(member, 'relationship', '') === 'Administrator') {
      return showWarningIconUnified;
    }
    return isPrimary
      ? utils.isSmmNeedAttention(member, smmAccounts, true) || checkInactiveInstitutions(institutionsList).length > 0
      : utils.isSmmNeedAttention(member, smmAccounts, false);
  }

  /*
   * Create a GA event string by combining partner ID, product family ID, and plan name.
   * Only non-empty and non-null values will be included in the string.
   * Spaces in the plan name will be replaced with underscores.
   *
   * @param {object} analytics - The analytics object containing partnerId, productFamilyId, and planName.
   * @returns {string} Formatted string (e.g., "partnerId_productFamilyId_planName") or 'missing_partner_family_plan_id' if no valid properties are available.
   */
  static createPartnerFamilyPlanIdEventStr(analytics: any): string {
    const parts = [analytics?.partnerId, analytics?.productFamilyId, analytics?.planName?.replace(/\s/g, '_')]
      .filter((part) => part !== undefined && part !== null && part !== '')
      .map((part) => String(part)); // Ensure all parts are strings

    return parts.length > 0 ? parts.join('_') : 'missing_partner_family_plan_id';
  }

  /**
   * Given an internal navigation path, returns urlRedirect string for navigating window.open to DSP Web app target path
   *
   * @param {string} path - The target path/page in the DSP SPA (e.g. "/locks")
   * @return {string} A redirectUrl query param string to append to DSP Web App host URL, for window.open
   */
  static buildRedirectUrlParam(path: string) {
    if (!path) return '';

    const encodedPath = encodeURIComponent(path);
    return `?redirectUrl=${encodedPath}#${path}`;
  }

  /**
   * Validates that a path is a safe internal route path.
   * Prevents open redirect vulnerabilities by ensuring only clean internal paths are used.
   *
   * @param {string} path - The path to validate (may optionally start with #)
   * @returns {boolean} True if the path is a valid internal route
   */
  static isValidInternalPath(path: string): boolean {
    if (!path || typeof path !== 'string') return false;
    // Remove leading # if present
    const cleanPath = path.startsWith('#') ? path.substring(1) : path;
    // Allow only alphanumeric characters, hyphens, underscores, and forward slashes
    // Leading slash is optional
    return /^\/?[a-zA-Z0-9\-_\/]*$/.test(cleanPath);
  }

  /**
   * Given an internal navigation path, navigates to DSP Web app target path.
   * For white label clients, navigates within the same browser tab to respect parent auth.
   * For non-white label clients, opens in a new browser tab.
   *
   * @param {string} path - The target path/page in the DSP SPA (e.g. "/locks")
   * @param {string} whiteLabelClient - Optional white label client identifier
   * @return {undefined}
   */
  static navigateInNewBrowserTab(path: string, whiteLabelClient?: string) {
    if (whiteLabelClient) {
      // Validate path before setting hash to prevent open redirect
      if (!this.isValidInternalPath(path)) {
        log.error('Invalid internal path for whitelabel navigation:', path);
        return;
      }
      // For white label clients, navigate within the same window to respect parent auth
      window.location.hash = `#${path}`;
    } else {
      // For non-white label clients, open in a new browser tab
      const redirectUrlParam = this.buildRedirectUrlParam(path);
      window.open(`${utils.getDspUrl()}${redirectUrlParam}`, '_blank', 'noopener noreferrer');
    }
  }

  /**
   * LLS_SMMBUSINESS feature is available for primary member
   * @param {AuthState} auth - Auth object containing all the member related data
   * @returns {boolean} Returns true if LLS_SMMBUSINESS feature is available for primary member, false otherwise
   */
  static smmBusinessFeatureAvailable(auth: AuthState): boolean {
    const smmMonitoring = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures.socialMediaMonitoring', {});
    return smmMonitoring?.serviceCode === 'LLS_SMMBUSINESS' && smmMonitoring?.featureStatus === 'AVAILABLE';
  }

  /**
   * Safely gets a value from an object, returning a fallback if the value is null or undefined.
   * @param {Object} object - The object to query.
   * @param {string|Array} path - The path of the property to get.
   * @param {*} fallback - The fallback value if the result is null or undefined.
   * @returns {*} - The resolved value or the fallback.
   */

  static safeGet<T, R = unknown>(object: T, path: string, fallback?: R): R | any {
    const value = _get(object, path);
    return value === undefined || value === null ? fallback : value;
  }

  static breakUpSummaryTitle(summaryTitle: string) {
    let dashIndex = summaryTitle.indexOf('-');
    if (dashIndex === -1) {
      // look for em-dash, as used by some translators (Polish)
      dashIndex = summaryTitle.indexOf('—');
    }
    if (dashIndex > 0) {
      // if a dash is found, then title is the part before the dash, subtitle is the part after the dash
      return {
        title: summaryTitle.substring(0, dashIndex).trim(),
        subtitle: summaryTitle.substring(dashIndex + 1).trim(),
      };
    }

    // if no dash found, then title is simply the entire summaryTitle, and subtitle is blank
    return {title: summaryTitle, subtitle: ''};
  }

  static isPrivacyMonitorUser(privacyMonitor: any, fetchingSummaryFailed = false) {
    const validateLicense = _get(privacyMonitor, 'license.validateLicense', false);
    const validLicense = _get(privacyMonitor, 'license.validLicense', false);
    const isUserEntitled = validateLicense && validLicense;

    const isNewUser = _get(privacyMonitor, 'isNewUser', false);
    const isPrivacyMonitorUser = isUserEntitled && (!fetchingSummaryFailed || isNewUser);

    return isPrivacyMonitorUser;
  }
  /**
   * modified business account object after processing business accounts data
   * @param {SocialMediaAccountsType[]} businessAccounts - Array of social media business account objects
   * @returns {SocialMediaBusinessAccountsType} Modified business account with consolidated data
   */
  static getSmmBusinessAccountsData(businessAccounts: SocialMediaAccountsType[]): SocialMediaBusinessAccountsType {
    //get businessUserNameList
    const businessUserNameList = businessAccounts.reduce((usernames, businessAccount) => {
      if (businessAccount.username !== '') {
        usernames.push(businessAccount.username);
      }
      return usernames;
    }, [] as string[]);

    // Determine status based on the status of all accounts/pages
    let status: string = businessAccounts[0]?.status; // default status
    if (businessAccounts.some((account) => account.status === 'DISCONNECTED')) {
      status = 'DISCONNECTED';
    } else if (businessAccounts.every((account) => account.status === 'CONNECTED')) {
      status = 'CONNECTED';
    }

    return {
      id: '',
      network: businessAccounts[0].network,
      partner: businessAccounts[0].partner,
      providerDisplayName: businessAccounts[0].providerDisplayName,
      userNameList: businessUserNameList,
      status,
      facebookWarningShown: businessAccounts[0].facebookWarningShown,
      type: businessAccounts[0].type,
    };
  }

  /**
   * Returns true when transaction monitoring is AVAILABLE and ENROLLED.
   *
   * @param {ProductFeatures} productFeatures - The user's product features containing transactionMonitoring status
   * @returns {boolean} Returns true if the user is eligible for FM activation, false otherwise
   *
   */
  static isFMAccessible(productFeatures: ProductFeatures): boolean {
    if (!productFeatures?.transactionMonitoring) {
      return false;
    }

    return (
      productFeatures.transactionMonitoring.featureStatus === 'AVAILABLE' &&
      productFeatures.transactionMonitoring.fulfillmentStatus === 'ENROLLED'
    );
  }

  /**
   * gives modifiedSmmAccounts data with personal and business accounts sorted by network
   *
   * @param {SocialMediaAccountsType[]} accounts - Array of social media account object
   * @returns {Array<SocialMediaAccountsType | SocialMediaBusinessAccountsType>} Returns object with network names as keys and arrays of accounts as values
   */
  static smmAccountsForBusiness(
    accounts: SocialMediaAccountsType[]
  ): Array<SocialMediaAccountsType | SocialMediaBusinessAccountsType> {
    if (accounts.length === 0) {
      return accounts;
    }

    const modifiedSmmAccountsData: Array<SocialMediaAccountsType | SocialMediaBusinessAccountsType> = [];

    // Group accounts by network
    const accountsByNetwork = accounts.reduce(
      (recordsArrByNetwork: Record<string, SocialMediaAccountsType[]>, account) => {
        const network = account.network;
        if (!recordsArrByNetwork[network]) {
          recordsArrByNetwork[network] = [];
        }
        recordsArrByNetwork[network].push(account);
        return recordsArrByNetwork;
      },
      {}
    );

    for (const groupedAccounts of Object.values(accountsByNetwork)) {
      const personalAccounts = groupedAccounts.filter((acc) => acc.type === 'personal');
      const businessAccounts = groupedAccounts.filter((acc) => acc.type === 'business');
      // Add personal account first
      modifiedSmmAccountsData.push(...personalAccounts);
      //Add Modified Business Account after personal
      if (!_isEmpty(businessAccounts)) modifiedSmmAccountsData.push(utils.getSmmBusinessAccountsData(businessAccounts));
    }

    return modifiedSmmAccountsData;
  }

  /**
   * smm accounts to display for DashboardSmm component
   * @param {SocialMediaAccountsType[]} accounts - SmmAccountsData object containing primary and junior members social media accounts
   * @returns {DashboardSmmItem[] | []} Returns array of social media accounts with consolidated status and network
   */
  static dashboardSmmAccountsForBusiness(accounts: DashboardSmmItem[]) {
    if (accounts.length === 0) return [];

    // Group accounts by network
    const accountsByNetwork = accounts.reduce(
      (recordsArrByNetwork: Record<string, DashboardSmmItem[]>, account: DashboardSmmItem) => {
        const network = account.network;
        if (!recordsArrByNetwork[network]) {
          recordsArrByNetwork[network] = [];
        }
        recordsArrByNetwork[network].push(account);
        return recordsArrByNetwork;
      },
      {}
    );

    const smmDashboardAccounts: DashboardSmmItem[] = [];

    for (const [network, accounts] of Object.entries(accountsByNetwork)) {
      // Determine status based on the status of both personal and business accounts
      let status: string;
      if (accounts.some((account: DashboardSmmItem) => account.status === 'DISCONNECTED')) {
        status = 'DISCONNECTED';
      } else if (accounts.some((account: DashboardSmmItem) => account.status === 'CONNECTED')) {
        status = 'CONNECTED';
      } else {
        status = 'NOT_ADDED';
      }

      smmDashboardAccounts.push({status, network});
    }
    return smmDashboardAccounts;
  }

  /**
   * Get the maximum institutions allowed from productFeatures.fwMultiAggregator
   * @param productFeatures - Product features object from user state
   * @returns The max institutions allowed number, or null if not available
   */
  static getMaxInstitutionsAllowedBDL(productFeatures: ProductFeatures): number | null {
    return utils.safeGet(productFeatures, 'fwMultiAggregator.maxInstitutionsAllowed', null);
  }

  /**
   * Check if user has reached their account limit for financial monitoring
   * @param maxInstitutionsAllowed - Max institutions allowed from fwMultiAggregator
   * @param currentConnectionsCount - Current number of connections
   * @returns true if limit is reached, false otherwise
   */
  static hasReachedAccountLimit(maxInstitutionsAllowed: number | null, currentConnectionsCount: number): boolean {
    if (!maxInstitutionsAllowed) return false;
    return currentConnectionsCount >= maxInstitutionsAllowed;
  }

  /**
   * Check if user can add more accounts during onboarding
   * @param maxInstitutionsAllowed - Max institutions allowed from fwMultiAggregator
   * @param currentConnectionsCount - Current number of connections
   * @returns true if can add more accounts, false otherwise
   */
  static canAddMoreAccountsOnboarding(maxInstitutionsAllowed: number | null, currentConnectionsCount: number): boolean {
    if (!maxInstitutionsAllowed) return true;

    // During onboarding, allow buffer for async connection updates
    // 2-account plans get buffer of 1, 5-account plans get buffer of 3
    const buffer = maxInstitutionsAllowed === 2 ? 1 : 3;
    return currentConnectionsCount < maxInstitutionsAllowed - buffer;
  }

  /**
   * Checks if Adobe Analytics (window.s) is available
   */
  static isAdobeAnalyticsAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.s === 'object' && window.s !== null;
  }

  /**
   * Tracks an Adobe Analytics event using s.tl().
   * @param event - Event data with name, category, label, value, and customProps
   */
  static trackAdobeEvent(event: AdobeTrackEventData): void {
    if (event.name && window.REACT_APP_ENABLE_AMPLITUDE_PAGE_VIEWS !== false) {
      window.genDL = window.genDL || [];
      // Not all Adobe link events correspond to a literal user click; the `event`
      // value can be re-evaluated once all events have been catalogued for Amplitude.
      window.genDL.push({
        event: 'click',
        click_name: event.name,
      });
    }

    if (!this.isAdobeAnalyticsAvailable()) {
      return;
    }

    try {
      const s = window.s;

      // Clear previous values
      if (typeof s.clearVars === 'function') {
        s.clearVars();
      }

      // Set event properties
      s.linkTrackVars = 'events,eVar1,eVar2,eVar3';
      if (event.name) {
        s.linkTrackEvents = event.name;
        s.events = event.name;
      }

      // Set event variables
      if (event.category) {
        s.eVar1 = event.category;
      }
      if (event.label) {
        s.eVar2 = event.label;
      }
      if (event.value !== undefined) {
        s.eVar3 = String(event.value);
      }

      // Apply custom properties
      if (event.customProps) {
        Object.entries(event.customProps).forEach(([key, value]) => {
          s[key] = value;
        });
      }

      // Track the event (only if name is provided)
      if (typeof s.tl === 'function' && event.name) {
        s.tl(true, 'o', event.name);
      }
    } catch (error) {
      log.error('Error tracking Adobe Analytics event:', error);
    }
  }

  static getFulfilledBureau(productFeatures: ProductFeatures) {
    const fulfilledBureau = utils.safeGet(productFeatures, 'creditFeaturesSummary.fulfilledBureau', 'EQUIFAX');

    const bureauAbbreviations: Record<string, string> = {
      EXPERIAN: 'EXP',
      EQUIFAX: 'EFX',
      TRANSUNION: 'TU',
    };

    return bureauAbbreviations[fulfilledBureau] || 'EFX';
  }

  /**
   * Parses comma-separated config lists (e.g. REACT_APP_HIDE_GENIE_PRODUCT_IDS and REACT_APP_HIDE_GENIE_PRODUCT_FAMILY_IDS).
   * Empty or whitespace-only CSV segments are dropped (not coerced to 0). Non-numeric tokens are omitted after Number().
   */
  static toNumberArray = (v: any): number[] => {
    const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [v];
    return arr
      .map((x) => (typeof x === 'string' ? x.trim() : x))
      .filter((x) => x !== '')
      .map((x) => Number(x))
      .filter((n) => !isNaN(n) && isFinite(n));
  };

  static shouldHideGenie(auth: AuthState): boolean {
    const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', {});
    const rawFamilyId = utils.safeGet(auth, 'user.primaryMember.plan.productFamilyId', 0);
    const rawProductId = utils.safeGet(auth, 'user.primaryMember.plan.productId', 0);
    const productFamilyId = Number(rawFamilyId);
    const productId = Number(rawProductId);
    const isValidId = (n: number) => Number.isFinite(n) && n > 0;
    const creditScore1bFeatureStatus = utils.safeGet(productFeatures, 'creditScore_1B.featureStatus', '');
    const creditReport1bFeatureStatus = utils.safeGet(productFeatures, 'creditReports_1B.featureStatus', '');

    const hideIds = utils.toNumberArray(window.REACT_APP_HIDE_GENIE_PRODUCT_IDS);
    const hideFamilies = utils.toNumberArray(window.REACT_APP_HIDE_GENIE_PRODUCT_FAMILY_IDS);

    const isInHideList =
      isValidId(productFamilyId) &&
      isValidId(productId) &&
      hideFamilies.includes(productFamilyId) &&
      hideIds.includes(productId);
    const featuresUnavailable =
      creditScore1bFeatureStatus !== 'AVAILABLE' && creditReport1bFeatureStatus !== 'AVAILABLE';
    return isInHideList && featuresUnavailable;
  }
}

utils.resizeIframe = _debounce(utils.resizeIframe, 150);
